import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

import { Box, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography, TextField, Button, Grid, Paper, Tooltip, Menu, MenuItem, CircularProgress, Modal, Stack, Alert, Divider, Switch, FormControlLabel } from '@mui/material';

import { Close, ArrowLeft, Send, Memory, ArrowUpward, Save, DesktopWindows, Smartphone, Undo, Redo, Edit, Upload, AttachFile, SmartToy as SmartToyIcon, CheckCircle, FormatBold, FormatItalic, FormatUnderlined, FormatListBulleted, FormatListNumbered, FormatAlignLeft, FormatAlignCenter, FormatAlignRight, FormatQuote, Link as LinkIcon, Image as ImageIcon, Code as CodeIcon, FormatColorText, FormatColorFill, BorderAll } from '@mui/icons-material';

import grapesjs from 'grapesjs';

import 'grapesjs/dist/css/grapes.min.css';
import './grapesjs-mautic.css';

import newsletter from 'grapesjs-preset-newsletter';
import postcss from 'grapesjs-parser-postcss';
import mjml from 'grapesjs-mjml';
import mjml2html from 'mjml-browser';
import { api } from '../services/auth';

import { useNotification } from '../contexts/NotificationContext';

import { useBrand } from '../contexts/BrandContext';

import { fetchLLMConfig } from '../services/config';

// Fonction utilitaire debounce
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const MAUTIC_DYNAMIC_FIELDS = [
  { id: 'city', label: 'Ville', object: 'lead', type: 'text' },
  { id: 'email', label: 'E-mail', object: 'lead', type: 'email' },
  { id: 'firstname', label: 'Prénom', object: 'lead', type: 'text' },
  { id: 'lastname', label: 'Nom', object: 'lead', type: 'text' },
  { id: 'gender', label: 'Genre', object: 'lead', type: 'text' },
  { id: 'age', label: 'Âge', object: 'lead', type: 'number' },
  { id: 'score', label: 'Score', object: 'lead', type: 'number' },
  { id: 'phone', label: 'Téléphone', object: 'lead', type: 'tel' },
  // On traite "Pays" comme un champ texte pour le filtrage dynamique, afin
  // d'envoyer une valeur libre dans le JSON (et éviter la validation stricte
  // sur la liste de pays côté Mautic).
  { id: 'country', label: 'Pays', object: 'lead', type: 'text' },
];

const MAUTIC_OPERATORS = [

  // Opérateurs génériques (texte, nombres, dates, booléens)

  { id: 'eq', label: 'égal à', types: ['text', 'number', 'date', 'datetime', 'boolean', 'email', 'tel'], requiresValue: true },

  { id: 'neq', label: 'différent de', types: ['text', 'number', 'date', 'datetime', 'boolean', 'email', 'tel'], requiresValue: true },

  { id: 'empty', label: 'vide', types: ['text', 'number', 'date', 'datetime', 'boolean', 'email', 'tel'], requiresValue: false },

  { id: 'notEmpty', label: 'non vide', types: ['text', 'number', 'date', 'datetime', 'boolean', 'email', 'tel'], requiresValue: false },

  // Opérateurs textuels (y compris email / téléphone)

  { id: 'like', label: 'contient', types: ['text', 'email', 'tel'], requiresValue: true },

  { id: 'notLike', label: 'ne contient pas', types: ['text', 'email', 'tel'], requiresValue: true },

  { id: 'regexp', label: 'expression régulière', types: ['text', 'email', 'tel'], requiresValue: true },

  { id: 'notRegexp', label: 'ne correspond pas (regexp)', types: ['text', 'email', 'tel'], requiresValue: true },

  { id: 'startsWith', label: 'commence par', types: ['text', 'date', 'datetime', 'email', 'tel'], requiresValue: true },

  { id: 'endsWith', label: 'se termine par', types: ['text', 'date', 'datetime', 'email', 'tel'], requiresValue: true },

  { id: 'contains', label: 'contient (partout)', types: ['text', 'email', 'tel'], requiresValue: true },

  // Comparateurs numériques / dates
  // D'après le code de Mautic (OperatorOptions + MatchFilterForLeadTrait),
  // ces opérateurs existent bien, mais ton instance renvoie une erreur 400
  // lorsque nous les utilisons pour le contenu dynamique d'email.
  //
  // Pour éviter ces 400, on les garde définis (pour compatibilité de lecture)
  // mais sans les associer à un type : ils ne seront plus proposés dans l'UI.

  { id: 'gt', label: 'supérieur à', types: [], requiresValue: true },

  { id: 'gte', label: 'supérieur ou égal à', types: [], requiresValue: true },

  { id: 'lt', label: 'inférieur à', types: [], requiresValue: true },

  { id: 'lte', label: 'inférieur ou égal à', types: [], requiresValue: true },

];

const getMauticFieldById = (id) => MAUTIC_DYNAMIC_FIELDS.find((field) => field.id === id) || null;

const getMauticOperatorsForType = (type) =>
  MAUTIC_OPERATORS.filter((op) => !type || op.types.includes(type));

const getMauticOperatorById = (id) =>
  MAUTIC_OPERATORS.find((op) => op.id === id) || null;

// Conversion helpers entre les IDs d'opérateurs côté UI (eq, neq, gt...)
// et les valeurs attendues par Mautic dans le JSON ("=", "!=", "empty", "!empty"...).
const mapUiOperatorToMautic = (op) => {
  const value = (op || '').trim();
  switch (value) {
    // Égalité / inégalité
    case 'eq':
      return '=';
    case 'neq':
      return '!=';

    // Opérateurs numériques (Mautic utilise les codes gt/gte/lt/lte)
    case 'gt':
      return 'gt';
    case 'gte':
      return 'gte';
    case 'lt':
      return 'lt';
    case 'lte':
      return 'lte';

    // Opérateurs sur la présence de valeur
    case 'empty':
      return 'empty';
    case 'notEmpty':
      return '!empty';

    // Opérateurs textuels
    case 'like':
      return 'like';
    case 'notLike':
      return '!like';
    case 'regexp':
      return 'regexp';
    case 'notRegexp':
      return '!regexp';
    case 'startsWith':
      return 'startsWith';
    case 'endsWith':
      return 'endsWith';
    case 'contains':
      return 'contains';

    default:
      // Par défaut, renvoyer la valeur brute (permet de supporter
      // d'éventuelles valeurs déjà conformes côté Mautic).
      return value;
  }
};

const mapMauticOperatorToUi = (op) => {
  const value = (op || '').trim();
  switch (value) {
    // Égalité / inégalité
    case '=':
      return 'eq';
    case '!=':
      return 'neq';

    // Opérateurs numériques
    case '>':
      return 'gt';
    case '>=':
      return 'gte';
    case '<':
      return 'lt';
    case '<=':
      return 'lte';

    // Opérateurs sur la présence de valeur
    case 'empty':
      return 'empty';
    case '!empty':
      return 'notEmpty';

    // Opérateurs textuels
    case 'like':
      return 'like';
    case '!like':
      return 'notLike';
    case 'regexp':
      return 'regexp';
    case '!regexp':
      return 'notRegexp';
    case 'startsWith':
      return 'startsWith';
    case 'endsWith':
      return 'endsWith';
    case 'contains':
      return 'contains';

    default:
      // Pour les valeurs déjà au format UI, renvoyer tel quel
      return value;
  }
};

const isMauticConditionValid = (cond) => {
  if (!cond) return false;
  const fieldId = (cond.field || '').trim();
  if (!fieldId) return false;

  const fieldDef = getMauticFieldById(fieldId);
  const fieldType = cond.type || (fieldDef && fieldDef.type) || 'text';

  const operatorId = (cond.operator || '').trim();
  if (!operatorId) return false;

  const opDef = getMauticOperatorById(operatorId);
  const requiresValue = !opDef || opDef.requiresValue !== false;

  if (!requiresValue) return true;

  const value = cond.filter;
  if (value === null || value === undefined) return false;
  if (String(value).trim() === '') return false;

  return true;
};

const hasInvalidDynamicContentConditions = (slot) => {
  if (!slot || !Array.isArray(slot.variations)) return false;

  return slot.variations.some((variation) =>
    Array.isArray(variation.conditions) &&
    variation.conditions.some((cond) => !isMauticConditionValid(cond))
  );
};

const SimpleRichTextEditor = ({ value, onChange, minHeight = 160, menuContainer = null, onPickImageFromLibrary, onUploadImageFromDisk }) => {
  const editorRef = useRef(null);
  const containerRef = useRef(null);
  const savedSelectionRef = useRef(null);
  const selectedImageRef = useRef(null);
  const imageResizeStateRef = useRef({ startX: 0, startWidth: 0 });

  const [blockFormat, setBlockFormat] = useState('p');
  const [fontSize, setFontSize] = useState('normal');
  const [textColorAnchorEl, setTextColorAnchorEl] = useState(null);
  const [bgColorAnchorEl, setBgColorAnchorEl] = useState(null);
  const [selectedTextColor, setSelectedTextColor] = useState('#000000');
  const [selectedBgColor, setSelectedBgColor] = useState('#FFFFFF');
  const [customTextColor, setCustomTextColor] = useState('#000000');
  const [customBgColor, setCustomBgColor] = useState('#FFFFFF');
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkNewTab, setLinkNewTab] = useState(false);
  const [showImageForm, setShowImageForm] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [isSourceMode, setIsSourceMode] = useState(false);
  const [sourceValue, setSourceValue] = useState('');
  const [selectedImageRect, setSelectedImageRect] = useState(null);
  const [selectedImageWidth, setSelectedImageWidth] = useState('');
  const [hasTableSelection, setHasTableSelection] = useState(false);

  const saveSelection = () => {
    try {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0 && editorRef.current) {
        const range = sel.getRangeAt(0);
        if (editorRef.current.contains(range.startContainer)) {
          savedSelectionRef.current = range;
        }
      }
    } catch (_) {}
  };

  const restoreSelection = () => {
    try {
      const range = savedSelectionRef.current;
      if (!range) return;
      const sel = window.getSelection();
      if (!sel) return;
      sel.removeAllRanges();
      sel.addRange(range);
    } catch (_) {}
  };

  useEffect(() => {
    if (!editorRef.current || isSourceMode) return;
    const currentHtml = editorRef.current.innerHTML;
    const nextHtml = value || '';
    if (currentHtml !== nextHtml) {
      editorRef.current.innerHTML = nextHtml;
    }
  }, [value, isSourceMode]);

  const getImageWidthPx = (img) => {
    if (!img) return null;
    let width = 0;

    const styleWidth = img.style && img.style.width;
    if (styleWidth && styleWidth.endsWith('px'))
    {
      const parsed = parseFloat(styleWidth);
      if (!Number.isNaN(parsed)) {
        width = parsed;
      }
    }

    if (!width) {
      const attrWidth = img.getAttribute && img.getAttribute('width');
      if (attrWidth) {
        const parsedAttr = parseFloat(attrWidth);
        if (!Number.isNaN(parsedAttr)) {
          width = parsedAttr;
        }
      }
    }

    if (!width) {
      const rect = img.getBoundingClientRect();
      if (rect && rect.width) {
        width = rect.width;
      }
    }

    if (!width) return null;
    return Math.round(width);
  };

  const measureSelectedImage = useCallback(() => {
    try {
      if (!editorRef.current || !containerRef.current) return;
      const img = selectedImageRef.current;
      if (!img) return;

      const imgRect = img.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();

      setSelectedImageRect({
        top: imgRect.top - containerRect.top,
        left: imgRect.left - containerRect.left,
        width: imgRect.width,
        height: imgRect.height,
      });
    } catch (_) {}
  }, []);

  const normalizeColorToHex = (color) => {
    if (!color) return '';
    const c = color.toString().trim();
    if (!c) return '';

    if (c[0] === '#') {
      if (c.length === 4) {
        const r = c[1];
        const g = c[2];
        const b = c[3];
        return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
      }
      if (c.length === 7) {
        return c.toUpperCase();
      }
      return c;
    }

    const rgbMatch = c.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
    if (rgbMatch) {
      const toHex = (n) => {
        const v = Math.max(0, Math.min(255, parseInt(n, 10) || 0));
        return v.toString(16).padStart(2, '0');
      };
      const r = toHex(rgbMatch[1]);
      const g = toHex(rgbMatch[2]);
      const b = toHex(rgbMatch[3]);
      return `#${r}${g}${b}`.toUpperCase();
    }

    return c;
  };

  const updateColorIndicators = () => {
    try {
      if (!editorRef.current) return;
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      if (!editorRef.current.contains(range.startContainer)) return;

      let textColor;
      try {
        textColor = document.queryCommandValue('foreColor');
      } catch (_) {}
      if (textColor && typeof textColor === 'string') {
        const hex = normalizeColorToHex(textColor);
        if (hex && hex !== selectedTextColor) {
          setSelectedTextColor(hex);
        }
        if (hex && hex !== customTextColor) {
          setCustomTextColor(hex);
        }
      }

      let bgColorRaw;
      try {
        bgColorRaw = document.queryCommandValue('hiliteColor');
      } catch (_) {}
      if (!bgColorRaw) {
        try {
          bgColorRaw = document.queryCommandValue('backColor');
        } catch (_) {}
      }
      if (bgColorRaw && typeof bgColorRaw === 'string') {
        const hexBg = normalizeColorToHex(bgColorRaw);
        if (hexBg && hexBg !== selectedBgColor) {
          setSelectedBgColor(hexBg);
        }
        if (hexBg && hexBg !== customBgColor) {
          setCustomBgColor(hexBg);
        }
      }

      let node = range.startContainer;
      if (!node) {
        selectedImageRef.current = null;
        setSelectedImageRect(null);
        setSelectedImageWidth('');
        setHasTableSelection(false);
        return;
      }

      let element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
      let imgEl = null;

      if (element) {
        if (element.nodeName === 'IMG') {
          imgEl = element;
        } else if (element.closest) {
          imgEl = element.closest('img');
        }
      }

      if (!imgEl && element && element.childNodes && range.startOffset < element.childNodes.length) {
        const child = element.childNodes[range.startOffset];
        if (child && child.nodeType === Node.ELEMENT_NODE && child.nodeName === 'IMG') {
          imgEl = child;
        }
      }

      if (imgEl && editorRef.current.contains(imgEl)) {
        selectedImageRef.current = imgEl;
        const widthPx = getImageWidthPx(imgEl);
        if (widthPx) {
          setSelectedImageWidth(String(widthPx));
        } else {
          setSelectedImageWidth('');
        }
        measureSelectedImage();
        setHasTableSelection(false);
        return;
      }

      selectedImageRef.current = null;
      setSelectedImageRect(null);
      setSelectedImageWidth('');

      let cellEl = element && element.closest ? element.closest('td, th') : null;
      if (cellEl && editorRef.current.contains(cellEl)) {
        setHasTableSelection(true);
      } else {
        setHasTableSelection(false);
      }
    } catch (_) {}
  };

  useEffect(() => {
    const handleSelectionChange = () => {
      updateColorIndicators();
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [selectedTextColor, selectedBgColor]);

  useEffect(() => {
    if (!selectedImageRect) return;

    const handleWindowChange = () => {
      measureSelectedImage();
    };

    window.addEventListener('scroll', handleWindowChange, true);
    window.addEventListener('resize', handleWindowChange);

    return () => {
      window.removeEventListener('scroll', handleWindowChange, true);
      window.removeEventListener('resize', handleWindowChange);
    };
  }, [selectedImageRect, measureSelectedImage]);

  const emitChange = () => {
    if (!editorRef.current || !onChange) return;
    const html = editorRef.current.innerHTML;
    onChange(html);
    updateColorIndicators();
  };

  const handleEditorClick = (event) => {
    if (!editorRef.current) return;
    const target = event.target;
    if (!target) return;

    const imgEl = target.closest && target.closest('img');

    if (imgEl && editorRef.current.contains(imgEl)) {
      try {
        const range = document.createRange();
        range.selectNode(imgEl);
        const sel = window.getSelection();
        if (sel) {
          sel.removeAllRanges();
          sel.addRange(range);
        }
      } catch (_) {}

      selectedImageRef.current = imgEl;
      const widthPx = getImageWidthPx(imgEl);
      if (widthPx) {
        setSelectedImageWidth(String(widthPx));
      } else {
        setSelectedImageWidth('');
      }
      measureSelectedImage();
      setHasTableSelection(false);
    } else {
      selectedImageRef.current = null;
      setSelectedImageRect(null);
      setSelectedImageWidth('');
      updateColorIndicators();
    }

    saveSelection();
  };

  const execCommand = (command, ui = false, valueArg = null) => {
    if (!editorRef.current) return;
    try {
      editorRef.current.focus();
      document.execCommand(command, ui, valueArg);
      emitChange();
    } catch (_) {}
  };

  const execCommandWithSavedSelection = (command, ui = false, valueArg = null) => {
    if (!editorRef.current) return;
    try {
      editorRef.current.focus();
      const range = savedSelectionRef.current;
      if (range && editorRef.current.contains(range.startContainer)) {
        restoreSelection();
      }
      document.execCommand(command, ui, valueArg);
      emitChange();
    } catch (_) {}
  };

  const COLORS = ['#000000', '#FFFFFF', '#F97316', '#EF4444', '#22C55E', '#3B82F6', '#6366F1', '#6B7280'];

  const handleBlockFormatChange = (event) => {
    const val = event.target.value || 'p';
    setBlockFormat(val);
    const tag = val === 'p' ? 'p' : val;
    execCommand('formatBlock', false, tag);
  };

  const handleFontSizeChange = (event) => {
    const val = event.target.value || 'normal';
    setFontSize(val);
    let size = 3;
    if (val === 'small') size = 2;
    if (val === 'large') size = 4;
    execCommand('fontSize', false, size);
  };

  const handleOpenTextColorMenu = (event) => {
    saveSelection();
    setTextColorAnchorEl(event.currentTarget);
  };

  const handleCloseTextColorMenu = () => {
    setTextColorAnchorEl(null);
  };

  const handleSelectTextColor = (color) => {
    execCommandWithSavedSelection('foreColor', false, color);
    setSelectedTextColor(color);
    setCustomTextColor(color);
    setTextColorAnchorEl(null);
  };

  const handleOpenBgColorMenu = (event) => {
    saveSelection();
    setBgColorAnchorEl(event.currentTarget);
  };

  const handleCloseBgColorMenu = () => {
    setBgColorAnchorEl(null);
  };

  const handleSelectBgColor = (color) => {
    execCommandWithSavedSelection('hiliteColor', false, color);
    setSelectedBgColor(color);
    setCustomBgColor(color);
    setBgColorAnchorEl(null);
  };

  const handleApplyCustomTextColor = () => {
    let color = (customTextColor || '').trim();
    if (!color) return;
    if (!color.startsWith('#')) {
      color = `#${color}`;
    }
    setCustomTextColor(color);
    execCommandWithSavedSelection('foreColor', false, color);
    setSelectedTextColor(color);
    setTextColorAnchorEl(null);
  };

  const handleApplyCustomBgColor = () => {
    let color = (customBgColor || '').trim();
    if (!color) return;
    if (!color.startsWith('#')) {
      color = `#${color}`;
    }
    setCustomBgColor(color);
    execCommandWithSavedSelection('hiliteColor', false, color);
    setSelectedBgColor(color);
    setBgColorAnchorEl(null);
  };

  const handleOpenLinkForm = () => {
    try {
      // Save the current selection to apply the link to it
      saveSelection();
      const selection = window.getSelection();
      let existingUrl = '';
      let existingNewTab = false;
      if (selection && selection.anchorNode) {
        const anchor = selection.anchorNode.parentElement?.closest('a');
        if (anchor) {
          existingUrl = anchor.getAttribute('href') || '';
          existingNewTab = anchor.getAttribute('target') === '_blank';
        }
      }
      setLinkUrl(existingUrl || 'https://');
      setLinkNewTab(existingNewTab);
    } catch (_) {
      setLinkUrl('https://');
      setLinkNewTab(false);
    }
    setShowLinkForm(true);
  };

  const handleApplyLink = () => {
    if (!linkUrl.trim()) {
      setShowLinkForm(false);
      return;
    }
    // Use the saved selection (like for colors)
    execCommandWithSavedSelection('createLink', false, linkUrl.trim());
    try {
      if (editorRef.current) {
        const anchors = editorRef.current.querySelectorAll('a[href]');
        anchors.forEach((a) => {
          if (a.getAttribute('href') === linkUrl.trim()) {
            if (linkNewTab) {
              a.setAttribute('target', '_blank');
              a.setAttribute('rel', 'noopener noreferrer');
            } else {
              a.removeAttribute('target');
              a.removeAttribute('rel');
            }
          }
        });
        emitChange();
      }
    } catch (_) {}
    setShowLinkForm(false);
  };

  const handleCancelLink = () => {
    setShowLinkForm(false);
  };

  const handleOpenImageForm = () => {
    setImageUrl('https://');
    setImageAlt('');
    setShowImageForm(true);
  };

  const handleApplyImage = () => {
    if (!imageUrl.trim()) {
      setShowImageForm(false);
      return;
    }
    execCommand('insertImage', false, imageUrl.trim());
    try {
      if (editorRef.current) {
        const imgs = editorRef.current.querySelectorAll('img[src]');
        let targetImg = null;
        imgs.forEach((img) => {
          if (img.getAttribute('src') === imageUrl.trim()) {
            targetImg = img;
          }
        });
        if (targetImg) {
          if (imageAlt.trim()) {
            targetImg.setAttribute('alt', imageAlt.trim());
          } else {
            targetImg.removeAttribute('alt');
          }
          try {
            targetImg.style.maxWidth = '100%';
            targetImg.style.height = 'auto';
          } catch (_) {}
        }
        emitChange();
      }
    } catch (_) {}
    setShowImageForm(false);
  };

  const handleCancelImage = () => {
    setShowImageForm(false);
  };

  const handleToggleSourceMode = () => {
    if (!isSourceMode) {
      const current = typeof value === 'string' && value.length
        ? value
        : (editorRef.current ? editorRef.current.innerHTML || '' : '');
      setSourceValue(current);
      setIsSourceMode(true);
    } else {
      if (editorRef.current) {
        editorRef.current.innerHTML = sourceValue || '';
        emitChange();
      }
      setIsSourceMode(false);
    }
  };

  const TABLE_MAX_ROWS = 8;
  const TABLE_MAX_COLS = 10;

  const insertTable = (rows, cols) => {
    const r = Math.max(1, Math.min(rows, TABLE_MAX_ROWS));
    const c = Math.max(1, Math.min(cols, TABLE_MAX_COLS));
    let html = '<table style="width:100%; border-collapse:collapse; border:1px solid #9CA3AF; background-color:#FFFFFF;"><tbody>';
    for (let i = 0; i < r; i += 1) {
      html += '<tr>';
      for (let j = 0; j < c; j += 1) {
        html += '<td style="border:1px solid #9CA3AF; padding:6px; background-color:#FFFFFF;">&nbsp;</td>';
      }
      html += '</tr>';
    }
    html += '</tbody></table>';
    execCommand('insertHTML', false, html);
  };

  const handleExternalImageSelected = (url) => {
    if (!url) return;
    setImageUrl(url);
    if (!showImageForm) {
      setShowImageForm(true);
    }
  };

  const handlePickImageFromLibrary = () => {
    if (typeof onPickImageFromLibrary === 'function') {
      onPickImageFromLibrary(handleExternalImageSelected);
    }
  };

  const handleUploadImageFromDisk = () => {
    if (typeof onUploadImageFromDisk === 'function') {
      onUploadImageFromDisk(handleExternalImageSelected);
    }
  };

  const handleImageWidthInputChange = (event) => {
    const value = event.target.value;
    setSelectedImageWidth(value);

    const img = selectedImageRef.current;
    if (!img) return;

    const parsed = parseInt(value, 10);
    if (Number.isNaN(parsed) || parsed <= 0) return;

    img.style.width = `${parsed}px`;
    img.style.height = 'auto';
    try {
      img.style.maxWidth = '100%';
    } catch (_) {}
    img.setAttribute('width', String(parsed));
    measureSelectedImage();
    emitChange();
  };

  const handleImageAlign = (alignment) => {
    const img = selectedImageRef.current;
    if (!img) return;

    img.style.display = 'block';
    img.removeAttribute('align');

    if (alignment === 'left') {
      img.style.marginLeft = '0';
      img.style.marginRight = 'auto';
      img.setAttribute('align', 'left');
    } else if (alignment === 'center') {
      img.style.marginLeft = 'auto';
      img.style.marginRight = 'auto';
      img.setAttribute('align', 'center');
    } else if (alignment === 'right') {
      img.style.marginLeft = 'auto';
      img.style.marginRight = '0';
      img.setAttribute('align', 'right');
    }

    try {
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
    } catch (_) {}

    measureSelectedImage();
    emitChange();
  };

  const handleDeleteSelectedImage = () => {
    const img = selectedImageRef.current;
    if (!img || !editorRef.current) return;

    if (img.parentNode) {
      img.parentNode.removeChild(img);
    }

    selectedImageRef.current = null;
    setSelectedImageRect(null);
    setSelectedImageWidth('');
    emitChange();
  };

  const handleImageResizeMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const img = selectedImageRef.current;
    if (!img) return;

    const widthPx = getImageWidthPx(img) || img.getBoundingClientRect().width;
    imageResizeStateRef.current = {
      startX: event.clientX,
      startWidth: widthPx || 0,
    };

    const handleMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - imageResizeStateRef.current.startX;
      let newWidth = Math.round(imageResizeStateRef.current.startWidth + deltaX);
      if (newWidth < 40) newWidth = 40;

      img.style.width = `${newWidth}px`;
      img.style.height = 'auto';
      try {
        img.style.maxWidth = '100%';
      } catch (_) {}
      img.setAttribute('width', String(newWidth));
      setSelectedImageWidth(String(newWidth));
      measureSelectedImage();
    };

    const handleUp = () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      emitChange();
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  };

  const [tableAnchorEl, setTableAnchorEl] = useState(null);
  const [tableRowsPreview, setTableRowsPreview] = useState(0);
  const [tableColsPreview, setTableColsPreview] = useState(0);

  const handleOpenTableMenu = (event) => {
    setTableAnchorEl(event.currentTarget);
    setTableRowsPreview(0);
    setTableColsPreview(0);
  };

  const handleCloseTableMenu = () => {
    setTableAnchorEl(null);
  };

  const handleHoverTableCell = (row, col) => {
    setTableRowsPreview(row);
    setTableColsPreview(col);
  };

  const handleClickTableCell = (row, col) => {
    insertTable(row, col);
    handleCloseTableMenu();
  };

  const getTableContextFromSelection = () => {
    if (!editorRef.current) return null;
    try {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return null;
      const range = sel.getRangeAt(0);
      if (!editorRef.current.contains(range.startContainer)) return null;

      let node = range.startContainer;
      let element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
      if (!element) return null;

      const cell = element.closest && element.closest('td, th');
      if (!cell) return null;

      const rowEl = cell.parentElement;
      if (!rowEl) return null;

      const tableEl = rowEl.closest('table');
      if (!tableEl || !editorRef.current.contains(tableEl)) return null;

      const rowIndex = Array.from(rowEl.parentElement.children).indexOf(rowEl);
      const colIndex = Array.from(rowEl.children).indexOf(cell);
      if (rowIndex < 0 || colIndex < 0) return null;

      return { table: tableEl, row: rowEl, cell, rowIndex, colIndex };
    } catch (_) {
      return null;
    }
  };

  const handleAddTableRowAbove = () => {
    const ctx = getTableContextFromSelection();
    if (!ctx) return;

    const { row } = ctx;
    const tbody = row.parentElement;
    if (!tbody) return;

    const newRow = row.cloneNode(true);
    Array.from(newRow.cells).forEach((cell) => {
      cell.innerHTML = '&nbsp;';
    });

    tbody.insertBefore(newRow, row);
    emitChange();
  };

  const handleAddTableRowBelow = () => {
    const ctx = getTableContextFromSelection();
    if (!ctx) return;

    const { row } = ctx;
    const tbody = row.parentElement;
    if (!tbody) return;

    const newRow = row.cloneNode(true);
    Array.from(newRow.cells).forEach((cell) => {
      cell.innerHTML = '&nbsp;';
    });

    if (row.nextSibling) {
      tbody.insertBefore(newRow, row.nextSibling);
    } else {
      tbody.appendChild(newRow);
    }
    emitChange();
  };

  const handleDeleteTableRow = () => {
    const ctx = getTableContextFromSelection();
    if (!ctx) return;

    const { table, row } = ctx;
    const tbody = row.parentElement;
    if (!tbody) return;

    tbody.removeChild(row);

    if (tbody.rows.length === 0 && table.parentElement) {
      table.parentElement.removeChild(table);
    }

    setHasTableSelection(false);
    emitChange();
  };

  const handleAddTableColumnLeft = () => {
    const ctx = getTableContextFromSelection();
    if (!ctx) return;

    const { table, colIndex, cell } = ctx;
    const styleAttr = cell.getAttribute && cell.getAttribute('style');

    Array.from(table.rows).forEach((rowEl) => {
      const insertIndex = Math.min(colIndex, rowEl.cells.length);
      const newCell = rowEl.insertCell(insertIndex);
      if (styleAttr) {
        newCell.setAttribute('style', styleAttr);
      }
      newCell.innerHTML = '&nbsp;';
    });

    emitChange();
  };

  const handleAddTableColumnRight = () => {
    const ctx = getTableContextFromSelection();
    if (!ctx) return;

    const { table, colIndex, cell } = ctx;
    const styleAttr = cell.getAttribute && cell.getAttribute('style');

    Array.from(table.rows).forEach((rowEl) => {
      const insertIndex = Math.min(colIndex + 1, rowEl.cells.length);
      const newCell = rowEl.insertCell(insertIndex);
      if (styleAttr) {
        newCell.setAttribute('style', styleAttr);
      }
      newCell.innerHTML = '&nbsp;';
    });

    emitChange();
  };

  const handleDeleteTableColumn = () => {
    const ctx = getTableContextFromSelection();
    if (!ctx) return;

    const { table, colIndex } = ctx;

    const rows = Array.from(table.rows);
    rows.forEach((rowEl) => {
      if (rowEl.cells.length > colIndex) {
        rowEl.deleteCell(colIndex);
      }
    });

    const hasAnyCell = rows.some((rowEl) => rowEl.cells.length > 0);
    if (!hasAnyCell && table.parentElement) {
      table.parentElement.removeChild(table);
    }

    setHasTableSelection(false);
    emitChange();
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        border: '1px solid #4B5563',
        borderRadius: 1,
        bgcolor: '#020617',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          borderBottom: '1px solid #1F2933',
          px: 0.5,
          py: 0.25,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          alignItems: 'center',
          color: '#E5E7EB',
          '& .MuiIconButton-root': {
            color: '#E5E7EB',
          },
          '& .MuiSvgIcon-root': {
            color: '#E5E7EB',
          },
        }}
      >
        <Tooltip title="Annuler" disablePortal>
          <span>
            <IconButton size="small" onClick={() => execCommand('undo')}>
              <Undo fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Rétablir" disablePortal>
          <span>
            <IconButton size="small" onClick={() => execCommand('redo')}>
              <Redo fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: '#1F2933' }} />

        <Tooltip title="Gras" disablePortal>
          <span>
            <IconButton size="small" onClick={() => execCommand('bold')}>
              <FormatBold fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Italique" disablePortal>
          <span>
            <IconButton size="small" onClick={() => execCommand('italic')}>
              <FormatItalic fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Souligné" disablePortal>
          <span>
            <IconButton size="small" onClick={() => execCommand('underline')}>
              <FormatUnderlined fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>

        <Box sx={{ mx: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TextField
            select
            size="small"
            value={blockFormat}
            onChange={handleBlockFormatChange}
            sx={{
              minWidth: 130,
              '& .MuiInputBase-root': {
                height: 32,
                backgroundColor: 'transparent',
                color: '#E5E7EB',
                fontSize: 13,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#374151',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#6B7280',
              },
              '& .MuiSvgIcon-root': {
                color: '#E5E7EB',
              },
            }}
            SelectProps={{
              MenuProps: {
                disablePortal: !!menuContainer,
                container: menuContainer || undefined,
              },
            }}
          >
            <MenuItem value="p">Paragraphe</MenuItem>
            <MenuItem value="h1">Titre 1</MenuItem>
            <MenuItem value="h2">Titre 2</MenuItem>
            <MenuItem value="h3">Titre 3</MenuItem>
          </TextField>

          <TextField
            select
            size="small"
            value={fontSize}
            onChange={handleFontSizeChange}
            sx={{
              minWidth: 110,
              '& .MuiInputBase-root': {
                height: 32,
                backgroundColor: 'transparent',
                color: '#E5E7EB',
                fontSize: 13,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#374151',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#6B7280',
              },
              '& .MuiSvgIcon-root': {
                color: '#E5E7EB',
              },
            }}
            SelectProps={{
              MenuProps: {
                disablePortal: !!menuContainer,
                container: menuContainer || undefined,
              },
            }}
          >
            <MenuItem value="small">Petite</MenuItem>
            <MenuItem value="normal">Normale</MenuItem>
            <MenuItem value="large">Grande</MenuItem>
          </TextField>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: '#1F2933' }} />

        <Tooltip title="Liste à puces" disablePortal>
          <span>
            <IconButton size="small" onClick={() => execCommand('insertUnorderedList')}>
              <FormatListBulleted fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Liste numérotée" disablePortal>
          <span>
            <IconButton size="small" onClick={() => execCommand('insertOrderedList')}>
              <FormatListNumbered fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Insérer un tableau" disablePortal>
          <span>
            <IconButton size="small" onClick={handleOpenTableMenu}>
              <BorderAll fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: '#1F2933' }} />

        <Tooltip title="Aligner à gauche" disablePortal>
          <span>
            <IconButton size="small" onClick={() => execCommand('justifyLeft')}>
              <FormatAlignLeft fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Centrer" disablePortal>
          <span>
            <IconButton size="small" onClick={() => execCommand('justifyCenter')}>
              <FormatAlignCenter fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Aligner à droite" disablePortal>
          <span>
            <IconButton size="small" onClick={() => execCommand('justifyRight')}>
              <FormatAlignRight fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Citation" disablePortal>
          <span>
            <IconButton size="small" onClick={() => execCommand('formatBlock', false, 'blockquote')}>
              <FormatQuote fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: '#1F2933' }} />

        <Tooltip title="Couleur du texte" disablePortal>
          <span>
            <IconButton size="small" onClick={handleOpenTextColorMenu}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <FormatColorText fontSize="inherit" />
                <Box sx={{ width: 18, height: 2, borderRadius: 1, mt: 0.3, bgcolor: selectedTextColor || '#FFFFFF' }} />
              </Box>
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Couleur de fond du texte" disablePortal>
          <span>
            <IconButton size="small" onClick={handleOpenBgColorMenu}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <FormatColorFill fontSize="inherit" />
                <Box sx={{ width: 18, height: 2, borderRadius: 1, mt: 0.3, bgcolor: selectedBgColor || '#FFFFFF' }} />
              </Box>
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Lien" disablePortal>
          <span>
            <IconButton size="small" onClick={handleOpenLinkForm}>
              <LinkIcon fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Image" disablePortal>
          <span>
            <IconButton size="small" onClick={handleOpenImageForm}>
              <ImageIcon fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={isSourceMode ? 'Masquer le code source HTML' : 'Afficher le code source HTML'} disablePortal>
          <span>
            <IconButton size="small" onClick={handleToggleSourceMode}>
              <CodeIcon fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {selectedImageRect && !isSourceMode && (
        <Box
          sx={{
            borderBottom: '1px solid #1F2933',
            px: 1,
            py: 0.5,
            bgcolor: '#020617',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="caption" sx={{ color: '#E5E7EB' }}>
            Image :
          </Typography>
          <TextField
            size="small"
            type="number"
            label="Largeur (px)"
            value={selectedImageWidth}
            onChange={handleImageWidthInputChange}
            sx={{ width: 140 }}
          />
          <Tooltip title="Aligner à gauche" disablePortal>
            <span>
              <IconButton size="small" onClick={() => handleImageAlign('left')}>
                <FormatAlignLeft fontSize="inherit" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Centrer" disablePortal>
            <span>
              <IconButton size="small" onClick={() => handleImageAlign('center')}>
                <FormatAlignCenter fontSize="inherit" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Aligner à droite" disablePortal>
            <span>
              <IconButton size="small" onClick={() => handleImageAlign('right')}>
                <FormatAlignRight fontSize="inherit" />
              </IconButton>
            </span>
          </Tooltip>
          <Box sx={{ flexGrow: 1 }} />
          <Button size="small" color="error" onClick={handleDeleteSelectedImage}>
            Supprimer l'image
          </Button>
        </Box>
      )}

      {!selectedImageRect && hasTableSelection && !isSourceMode && (
        <Box
          sx={{
            borderBottom: '1px solid #1F2933',
            px: 1,
            py: 0.5,
            bgcolor: '#020617',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="caption" sx={{ color: '#E5E7EB', mr: 1 }}>
            Tableau :
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <Button size="small" variant="outlined" onClick={handleAddTableRowAbove}>
              Ligne au-dessus
            </Button>
            <Button size="small" variant="outlined" onClick={handleAddTableRowBelow}>
              Ligne en dessous
            </Button>
            <Button size="small" variant="outlined" onClick={handleDeleteTableRow}>
              Supprimer la ligne
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <Button size="small" variant="outlined" onClick={handleAddTableColumnLeft}>
              Colonne à gauche
            </Button>
            <Button size="small" variant="outlined" onClick={handleAddTableColumnRight}>
              Colonne à droite
            </Button>
            <Button size="small" variant="outlined" onClick={handleDeleteTableColumn}>
              Supprimer la colonne
            </Button>
          </Stack>
        </Box>
      )}

      {showLinkForm && (
        <Box
          sx={{
            borderBottom: '1px solid #1F2933',
            px: 1,
            py: 1,
            bgcolor: '#020617',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              fullWidth
              label="URL du lien"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={linkNewTab}
                  onChange={(e) => setLinkNewTab(e.target.checked)}
                />
              }
              label={<Typography variant="caption">Nouvel onglet</Typography>}
              sx={{ color: '#E5E7EB', ml: 1 }}
            />
          </Stack>
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button size="small" onClick={handleCancelLink}>
              Annuler
            </Button>
            <Button size="small" variant="contained" onClick={handleApplyLink}>
              Appliquer
            </Button>
          </Box>
        </Box>
      )}

      {showImageForm && (
        <Box
          sx={{
            borderBottom: '1px solid #1F2933',
            px: 1,
            py: 1,
            bgcolor: '#020617',
          }}
        >
          <Stack spacing={1}>
            <TextField
              size="small"
              fullWidth
              label="URL de l'image"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
            />
            <TextField
              size="small"
              fullWidth
              label="Texte alternatif (alt)"
              value={imageAlt}
              onChange={(e) => setImageAlt(e.target.value)}
            />
          </Stack>
          <Box
            sx={{
              mt: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Stack direction="row" spacing={1}>
              {onPickImageFromLibrary && (
                <Button size="small" variant="outlined" onClick={handlePickImageFromLibrary}>
                  Bibliothèque d'images
                </Button>
              )}
              {onUploadImageFromDisk && (
                <Button size="small" variant="outlined" onClick={handleUploadImageFromDisk}>
                  Depuis mon ordinateur
                </Button>
              )}
            </Stack>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" onClick={handleCancelImage}>
                Annuler
              </Button>
              <Button size="small" variant="contained" onClick={handleApplyImage}>
                Insérer
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {isSourceMode ? (
        <Box sx={{ p: 1 }}>
          <TextField
            value={sourceValue}
            onChange={(e) => setSourceValue(e.target.value)}
            multiline
            minRows={6}
            maxRows={16}
            fullWidth
            variant="outlined"
            InputProps={{
              sx: {
                fontFamily: 'monospace',
                fontSize: 12,
                bgcolor: '#020617',
                color: '#E5E7EB',
              },
            }}
          />
        </Box>
      ) : (
        <Box
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={emitChange}
          onBlur={emitChange}
          onMouseUp={() => {
            saveSelection();
            updateColorIndicators();
          }}
          onKeyUp={() => {
            saveSelection();
            updateColorIndicators();
          }}
          onClick={handleEditorClick}
          sx={{
            minHeight,
            p: 1.5,
            fontSize: 14,
            lineHeight: 1.5,
            outline: 'none',
            cursor: 'text',
            bgcolor: '#FFFFFF',
            color: '#000000',
            '& img': {
              maxWidth: '100%',
              height: 'auto',
              display: 'block',
            },
            '& a': {
              color: '#2563EB',
            },
            '&:focus': {
              outline: 'none',
              boxShadow: '0 0 0 1px #6366F1',
            },
          }}
        />
      )}

      {selectedImageRect && !isSourceMode && (
        <Box
          sx={{
            position: 'absolute',
            top: selectedImageRect.top - 2,
            left: selectedImageRect.left - 2,
            width: selectedImageRect.width + 4,
            height: selectedImageRect.height + 4,
            border: '2px solid #6366F1',
            borderRadius: 0.5,
            pointerEvents: 'none',
            boxSizing: 'border-box',
          }}
        >
          <Box
            onMouseDown={handleImageResizeMouseDown}
            sx={{
              position: 'absolute',
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: '#6366F1',
              top: -5,
              left: -5,
              cursor: 'nwse-resize',
              pointerEvents: 'auto',
            }}
          />
          <Box
            onMouseDown={handleImageResizeMouseDown}
            sx={{
              position: 'absolute',
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: '#6366F1',
              top: -5,
              right: -5,
              cursor: 'nesw-resize',
              pointerEvents: 'auto',
            }}
          />
          <Box
            onMouseDown={handleImageResizeMouseDown}
            sx={{
              position: 'absolute',
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: '#6366F1',
              bottom: -5,
              left: -5,
              cursor: 'nesw-resize',
              pointerEvents: 'auto',
            }}
          />
          <Box
            onMouseDown={handleImageResizeMouseDown}
            sx={{
              position: 'absolute',
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: '#6366F1',
              bottom: -5,
              right: -5,
              cursor: 'nwse-resize',
              pointerEvents: 'auto',
            }}
          />
        </Box>
      )}

      <Menu
        anchorEl={textColorAnchorEl}
        open={Boolean(textColorAnchorEl)}
        onClose={handleCloseTextColorMenu}
        disablePortal={!!menuContainer}
        container={menuContainer || undefined}
      >
        {COLORS.map((color) => (
          <MenuItem key={color} onClick={() => handleSelectTextColor(color)}>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                bgcolor: color,
                border: '1px solid #111827',
                mr: 1,
              }}
            />
            {color}
          </MenuItem>
        ))}
        <Divider sx={{ my: 0.5 }} />
        <Box sx={{ px: 2, py: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            component="input"
            type="color"
            value={customTextColor}
            onChange={(e) => setCustomTextColor(e.target.value)}
            sx={{
              width: 32,
              height: 24,
              p: 0,
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
            }}
          />
          <TextField
            size="small"
            label="#RRGGBB"
            value={customTextColor}
            onChange={(e) => setCustomTextColor(e.target.value)}
            sx={{
              width: 120,
              '& .MuiInputBase-root': {
                bgcolor: '#FFFFFF',
                color: '#000000',
              },
              '& .MuiInputBase-input': {
                color: '#000000',
              },
              '& .MuiInputLabel-root': {
                color: '#111827',
              },
            }}
          />
          <Button
            size="small"
            onClick={handleApplyCustomTextColor}
            variant="contained"
            sx={{
              minWidth: 40,
              px: 1,
              py: 0.5,
              bgcolor: '#6366F1',
              color: '#FFFFFF',
              fontSize: 12,
              '&:hover': {
                bgcolor: '#4F46E5',
              },
            }}
          >
            OK
          </Button>
        </Box>
      </Menu>

      <Menu
        anchorEl={bgColorAnchorEl}
        open={Boolean(bgColorAnchorEl)}
        onClose={handleCloseBgColorMenu}
        disablePortal={!!menuContainer}
        container={menuContainer || undefined}
      >
        {COLORS.map((color) => (
          <MenuItem key={color} onClick={() => handleSelectBgColor(color)}>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                bgcolor: color,
                border: '1px solid #111827',
                mr: 1,
              }}
            />
            {color}
          </MenuItem>
        ))}
        <Divider sx={{ my: 0.5 }} />
        <Box sx={{ px: 2, py: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            component="input"
            type="color"
            value={customBgColor}
            onChange={(e) => setCustomBgColor(e.target.value)}
            sx={{
              width: 32,
              height: 24,
              p: 0,
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
            }}
          />
          <TextField
            size="small"
            label="#RRGGBB"
            value={customBgColor}
            onChange={(e) => setCustomBgColor(e.target.value)}
            sx={{
              width: 120,
              '& .MuiInputBase-root': {
                bgcolor: '#FFFFFF',
                color: '#000000',
              },
              '& .MuiInputBase-input': {
                color: '#000000',
              },
              '& .MuiInputLabel-root': {
                color: '#111827',
              },
            }}
          />
          <Button
            size="small"
            onClick={handleApplyCustomBgColor}
            variant="contained"
            sx={{
              minWidth: 40,
              px: 1,
              py: 0.5,
              bgcolor: '#6366F1',
              color: '#FFFFFF',
              fontSize: 12,
              '&:hover': {
                bgcolor: '#4F46E5',
              },
            }}
          >
            OK
          </Button>
        </Box>
      </Menu>

      <Menu
        anchorEl={tableAnchorEl}
        open={Boolean(tableAnchorEl)}
        onClose={handleCloseTableMenu}
        disablePortal={!!menuContainer}
        container={menuContainer || undefined}
        MenuListProps={{ disablePadding: true }}
      >
        <Box sx={{ p: 1 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${TABLE_MAX_COLS}, 16px)`,
              gridAutoRows: '16px',
              gap: 2,
            }}
          >
            {Array.from({ length: TABLE_MAX_ROWS }).map((_, rowIndex) => (
              Array.from({ length: TABLE_MAX_COLS }).map((__, colIndex) => {
                const r = rowIndex + 1;
                const c = colIndex + 1;
                const active =
                  tableRowsPreview >= r && tableColsPreview >= c &&
                  tableRowsPreview > 0 && tableColsPreview > 0;
                return (
                  <Box
                    key={`${r}-${c}`}
                    onMouseEnter={() => handleHoverTableCell(r, c)}
                    onClick={() => handleClickTableCell(r, c)}
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: 0.5,
                      border: '1px solid #4B5563',
                      bgcolor: active ? '#3B82F6' : '#111827',
                      cursor: 'pointer',
                    }}
                  />
                );
              })
            ))}
          </Box>
          <Box sx={{ mt: 1, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: '#E5E7EB' }}>
              {tableRowsPreview > 0 && tableColsPreview > 0
                ? `${tableRowsPreview} × ${tableColsPreview}`
                : 'Sélectionnez le nombre de lignes et de colonnes'}
            </Typography>
          </Box>
        </Box>
      </Menu>
    </Box>
  );
};

const EmailAssistant = ({ open, onClose, mode = 'new', email = null, onContentChange = null, onCreated = null, initialMjml = null, initialBuilderJson = null, initialLastMode = null, initialHtmlEdited = false, initialHtmlSnapshot = null, editorStateLoading = false, editorStateError = null, onRetryEditorState = null, initialDynamicContent = null }) => {

  // Debug helper
  const DEBUG = false;
  const logDebug = (...args) => { try { if (DEBUG) console.log(...args); } catch (_) {} };

  const { showNotification } = useNotification();

  const { currentBrand } = useBrand();

  const [currentStep, setCurrentStep] = useState(mode === 'edit' ? 2 : 0);

  // Indique quand la sélection automatique du mode (HTML/MJML) est terminée
  const [autoModeReady, setAutoModeReady] = useState(false);

  // En mode édition, ne pas initialiser selectedMethod pour éviter de déclencher le useEffect qui change editorType
  const [selectedMethod, setSelectedMethod] = useState(mode === 'new' ? 'ai' : null);

  const [isEditMode, setIsEditMode] = useState(mode === 'edit');

  const [isNaturalMode, setIsNaturalMode] = useState(false); // false = Mode Manuel (éditeur GrapesJS)

  const [isPreviewMobile, setIsPreviewMobile] = useState(false);

  // Type d'éditeur à utiliser : 'html' (newsletter preset) ou 'mjml' (MJML components)
  // En mode édition, toujours HTML. En mode création, MJML par défaut (méthode 'ai')
  const [editorType, setEditorType] = useState(mode === 'edit' ? 'html' : 'mjml');

  // État pour empêcher le rendu avant l'initialisation complète (évite le flash visuel)
  const [isInitialized, setIsInitialized] = useState(false);

  // Alerte changement non sauvegardé au switch de mode

  const [confirmSwitchOpen, setConfirmSwitchOpen] = useState(false);

  const [pendingTargetNaturalMode, setPendingTargetNaturalMode] = useState(null);

  // Alerte changement non sauvegardé à la fermeture de la modale

  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const lastSavedHtmlRef = useRef('');

  // Flag pour suivre si l'email a été sauvegardé (création ou édition)
  const hasBeenSavedRef = useRef(mode === 'edit'); // En mode edit, considéré comme "déjà sauvegardé"

  const [chatMessages, setChatMessages] = useState([

    {

      text: mode === 'edit' && email ? `Bonjour ! Quel aspect de l'email "${email.name}" souhaitez-vous modifier ?` : "Bonjour ! Je suis là pour vous aider à créer votre email. Décrivez-moi ce que vous souhaitez.",

      sender: 'assistant'

    }

  ]);

  const [chatInput, setChatInput] = useState('');

  const [editorContent, setEditorContent] = useState(
    mode === 'edit' && email
      ? (
          // Priorité à l’instantané HTML persistant si disponible, sinon fallback sur le HTML de l’email
          initialHtmlSnapshot || email.customHtml || email.content || '<p>Commencez à rédiger votre email ici...</p>'
        )
      : '<p>Commencez à rédiger votre email ici...</p>'
  );

  const editorContentRef = useRef(editorContent);

  // États source pour Mautic-like: MJML et JSON du builder
  const [mjmlSource, setMjmlSource] = useState(
    mode === 'edit'
      ? ((email && email.mjml) || initialMjml || null)
      : (initialMjml || null)
  );
  const [builderJson, setBuilderJson] = useState(
    mode === 'edit'
      ? ((email && email.builderJson) || initialBuilderJson || null)
      : (initialBuilderJson || null)
  );
 
  

  // États pour les fonctionnalités d'édition

  const [loadingAI, setLoadingAI] = useState(false);

  const [emailHistory, setEmailHistory] = useState([]);

  const [saving, setSaving] = useState(false);

  const [imageUploadModalOpen, setImageUploadModalOpen] = useState(false);

  const [uploadedImageUrl, setUploadedImageUrl] = useState('');

  const [imageInsertPrompt, setImageInsertPrompt] = useState('');

  const [imageUploading, setImageUploading] = useState(false);

  const [imageUploadError, setImageUploadError] = useState('');

  const [importPromptAnchorEl, setImportPromptAnchorEl] = useState(null);

  const [importHtmlError, setImportHtmlError] = useState('');

  // États pour la configuration du bloc "Contenu dynamique" Mautic

  const [dynamicContentDialogOpen, setDynamicContentDialogOpen] = useState(false);

  const [dynamicContents, setDynamicContents] = useState([]);

  const [dynamicContentError, setDynamicContentError] = useState('');

  const [selectedDynamicContentId, setSelectedDynamicContentId] = useState(null);

  const [dynamicContentAlias, setDynamicContentAlias] = useState('');

  const [emailDynamicContent, setEmailDynamicContent] = useState([]);

  const emailDynamicContentRef = useRef([]);

  const [currentDcSlot, setCurrentDcSlot] = useState(null);

  const [currentDcTab, setCurrentDcTab] = useState('default');

  const [currentDcVariationIndex, setCurrentDcVariationIndex] = useState(null);

  const [dcFromAiAnchorEl, setDcFromAiAnchorEl] = useState(null);

  // États pour la modal de confirmation d'import HTML
  const [htmlImportConfirmOpen, setHtmlImportConfirmOpen] = useState(false);
  const [pendingHtmlContent, setPendingHtmlContent] = useState('');
  const [pendingHtmlFileName, setPendingHtmlFileName] = useState('');
  const [htmlInsertPrompt, setHtmlInsertPrompt] = useState('');
  const [isInsertingHtml, setIsInsertingHtml] = useState(false);

  // États pour la galerie d'images en mode IA
  const [imageGalleryOpen, setImageGalleryOpen] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loadingGallery, setLoadingGallery] = useState(false);
  const [galleryPage, setGalleryPage] = useState(1);
  const [galleryHasMore, setGalleryHasMore] = useState(true);

  // État pour forcer la mise à jour du preview
  const [previewKey, setPreviewKey] = useState(0);

  // États pour la validation du nom interne
  const [isValidatingName, setIsValidatingName] = useState(false);
  const [nameValidationError, setNameValidationError] = useState('');
  const [nameValidationSuccess, setNameValidationSuccess] = useState(false);
  const [nameValidationWarning, setNameValidationWarning] = useState('');

  const [canUndo, setCanUndo] = useState(false);

  const [canRedo, setCanRedo] = useState(false);

  // États pour undo/redo (index courant dans l'historique)

  const [historyIndex, setHistoryIndex] = useState(0);

  // ============================================================
  // ÉTATS POUR LE SYSTÈME DE TEMPLATES PERSONNALISABLES
  // ============================================================
  const [userTemplates, setUserTemplates] = useState([]); // Liste des templates utilisateur
  const [selectedTemplate, setSelectedTemplate] = useState(null); // Template sélectionné
  const [loadingTemplates, setLoadingTemplates] = useState(false); // Chargement des templates
  const [templateManagerOpen, setTemplateManagerOpen] = useState(false); // Modal gestion templates (liste)
  const [templateEditorOpen, setTemplateEditorOpen] = useState(false); // Modal éditeur de template
  const [editingTemplate, setEditingTemplate] = useState(null); // Template en cours d'édition (null = nouveau)
  const [savingTemplate, setSavingTemplate] = useState(false); // Sauvegarde du template
  
  // États pour les prévisualisations
  const [templatePreviewOpen, setTemplatePreviewOpen] = useState(false); // Modal prévisualisation template étape 0
  const [templatePreviewHtml, setTemplatePreviewHtml] = useState(''); // HTML compilé pour prévisualisation
  const [customPreviewOpen, setCustomPreviewOpen] = useState(false); // Modal prévisualisation personnalisation
  const [customPreviewHtml, setCustomPreviewHtml] = useState(''); // HTML compilé de la personnalisation
  const [inlinePreviewHtml, setInlinePreviewHtml] = useState('');
  
  const [templateFormData, setTemplateFormData] = useState({
    name: '',
    description: '',
    isDefault: false,
    // Nouveau système: liste d'éléments configurables pour header et footer
    headerElements: [
      { id: 1, type: 'text', props: { content: '{webview_text}', fontSize: '11px', align: 'center', color: '#000000' } },
      { id: 2, type: 'spacer', props: { height: '10px' } },
      { id: 3, type: 'image', props: { src: '/app/assets/images/placeholder-logo.png', width: '70px', align: 'center' } }
    ],
    footerElements: [
      { id: 1, type: 'spacer', props: { height: '10px' } },
      { id: 2, type: 'social', props: { 
        networks: [
          { name: 'facebook', url: '', enabled: false },
          { name: 'instagram', url: '', enabled: false },
          /*{ name: 'twitter', url: '', enabled: false },*/
          { name: 'x', url: '', enabled: false },
          { name: 'linkedin', url: '', enabled: false },
          { name: 'youtube', url: '', enabled: false },
          { name: 'pinterest', url: '', enabled: false },
          { name: 'tumblr', url: '', enabled: false },
          { name: 'xing', url: '', enabled: false },
          { name: 'github', url: '', enabled: false },
          { name: 'snapchat', url: '', enabled: false },
          { name: 'vimeo', url: '', enabled: false },
          { name: 'medium', url: '', enabled: false },
          { name: 'soundcloud', url: '', enabled: false },
          { name: 'dribbble', url: '', enabled: false },
          { name: 'web', url: '', enabled: false },
          { name: 'google', url: '', enabled: false },
          { name: 'whatsapp', url: '', enabled: false }
        ]
      }},
      { id: 3, type: 'spacer', props: { height: '10px' } },
      { id: 4, type: 'text', props: { content: `${currentBrand?.name || '{brand=name}'}<br/>11111 Beautiful City, 1212 Nice Street<br/>Brazil`, fontSize: '12px', align: 'center', color: '#ffffff' } },
      { id: 5, type: 'spacer', props: { height: '10px' } },
      { id: 6, type: 'text', props: { content: 'Fancy seeing you down here. You\'re getting this email because you gave us your email address.<br/>Want to change how you receive these emails?', fontSize: '11px', align: 'center', color: '#b3b3b3' } },
      { id: 7, type: 'text', props: { content: '{unsubscribe_text}', fontSize: '11px', align: 'center', color: '#b3b3b3' } },
      { id: 8, type: 'spacer', props: { height: '10px' } }
    ],
    headerBgColor: '#ffffff',
    footerBgColor: '#000000'
  });

  

  // Refs

  const promptFileInputRef = useRef(null);
  const dcImageFileInputRef = useRef(null);
  const grapesFileInputRef = useRef(null);

  const chatEndRef = useRef(null);

  const requestControllerRef = useRef(null);

  const ignoreInitialManualChangeRef = useRef(false);

  const editorInstanceRef = useRef(null);

  const grapesContainerRef = useRef(null);

  const autoModeAppliedRef = useRef(false);

  const dynamicContentInitializedRef = useRef(false);

  // Debounce pour la capture du state du builder
  const builderSaveTimeoutRef = useRef(null);

  const updatingFromGrapesRef = useRef(false);

  const suppressNextEditorChangeRef = useRef(false);

  // Désactive la propagation live du contenu vers le parent pour éviter la désynchronisation de la liste

  const allowLivePreviewRef = useRef(false);

  const hasUserChangesRef = useRef(false);
  const editorInitializedRef = useRef(false);
  const lastSelectedComponentRef = useRef({ id: null, time: 0 });
  const gjsDynamicContentModalElRef = useRef(null);
  const dcImageInsertCallbackRef = useRef(null);

  const showGrapesLoader =
    open &&
    !isNaturalMode &&
    (
      (mode === 'edit' && (!editorInstanceRef.current || !autoModeReady || editorStateLoading)) ||
      (mode === 'new' && currentStep === 2 && !editorInstanceRef.current)
    );

  const isManualInitInProgress = showGrapesLoader;



  const notifyParentIfAllowed = (html) => {

    if (!allowLivePreviewRef.current) return;

    try { onContentChange && onContentChange(html); } catch (_) {}

  };

  // ✅ Champs unifiés pour les deux méthodes (IA et Manuelle)
  const [formData, setFormData] = useState({

    internalName: '',      // Nom interne (obligatoire)

    subject: '',           // Sujet de l'email (obligatoire)

    preheaderText: '',     // Texte de prévisualisation (non obligatoire)

    objective: '',         // Objectif de l'email (pour méthode IA uniquement)

    htmlFile: null

  });



  // Utilitaire: extraire meta (titre, h1, preheader) depuis HTML importé

  const extractEmailMetaFromHtml = (htmlString) => {

    try {

      const parser = new DOMParser();

      const doc = parser.parseFromString(htmlString, 'text/html');

      const title = (doc.querySelector('title')?.textContent || '').trim();

      const h1 = (doc.querySelector('h1')?.textContent || '').trim();

      const metaPreheader = doc.querySelector('meta[name="preheader" i]')?.getAttribute('content') || '';

      const metaDescription = doc.querySelector('meta[name="description" i]')?.getAttribute('content') || '';

      const subject = title || h1 || '';

      const preheader = metaPreheader || metaDescription || '';

      return { subject, preheader };

    } catch (_) {

      return { subject: '', preheader: '' };

    }

  };



  // Mapping helper: convertir le JSON dynamicContent de Mautic
  // en structure EmailDynamicContent pour le frontend
  const mapMauticDynamicContentToSlots = (mauticSlots) => {

    if (!Array.isArray(mauticSlots)) return [];

    return mauticSlots

      .map((slot, slotIndex) => {

        if (!slot || typeof slot !== 'object') return null;

        const tokenName = (slot.tokenName || slot.token || '').trim();

        if (!tokenName) return null;

        const defaultContent = slot.content ?? '';

        const rawFilters = Array.isArray(slot.filters) ? slot.filters : [];

        const variations = rawFilters

          .map((f, varIndex) => {

            if (!f || typeof f !== 'object') return null;

            const variationContent = f.content ?? '';

            const rawConditions = Array.isArray(f.filters) ? f.filters : [];

            const conditions = rawConditions

              .map((c) => {

                if (!c || typeof c !== 'object') return null;

                const mapped = {

                  glue: c.glue || 'and',

                  field: c.field || '',

                  object: c.object || c.entity || 'lead',

                  type: c.type || '',

                  operator: mapMauticOperatorToUi(c.operator || c.op || ''),

                  filter: c.filter ?? '',

                  display: c.display,

                };

                // Ignorer les conditions vides/placeholder renvoyées par Mautic
                // (ex: field/operator null, type = TextType Symfony, etc.).
                return isMauticConditionValid(mapped) ? mapped : null;

              })

              .filter(Boolean);

            // Si aucune condition valide n'est trouvée, ignorer complètement
            // cette variation (cas des placeholders auto-ajoutés par Mautic).
            if (!conditions.length) return null;

            return {

              id: f.id != null ? String(f.id) : `${slotIndex}-${varIndex}`,

              content: variationContent,

              conditions,

            };

          })

          .filter(Boolean);

        return {

          tokenName,

          defaultContent,

          variations,

        };

      })

      .filter(Boolean);

  };

  // Mapping inverse: convertir la structure interne EmailDynamicContent
  // en JSON dynamicContent compatible Mautic
  const mapSlotsToMauticDynamicContent = (slots) => {

    if (!Array.isArray(slots)) return [];

    return slots

      .map((slot) => {

        if (!slot || typeof slot !== 'object') return null;

        const tokenName = (slot.tokenName || slot.token || '').trim();

        if (!tokenName) return null;

        const defaultContent = slot.defaultContent ?? '';

        const variations = Array.isArray(slot.variations) ? slot.variations : [];

        const filters = variations

          .map((v, varIndex) => {

            if (!v || typeof v !== 'object') return null;

            const variationContent = v.content ?? '';

            const rawConditions = Array.isArray(v.conditions) ? v.conditions : [];

            const conditions = rawConditions

              .map((c) => {

                if (!c || typeof c !== 'object') return null;

                return {

                  glue: c.glue || 'and',

                  field: c.field || '',

                  object: c.object || c.entity || 'lead',

                  type: c.type || '',

                  operator: mapUiOperatorToMautic(c.operator || c.op || ''),

                  filter: c.filter ?? '',

                  display: c.display,

                };

              })

              .filter(Boolean);

            // Ne pas envoyer de variation sans conditions vers Mautic
            // pour éviter des filtres vides inutiles.
            if (!conditions.length) return null;

            return {

              content: variationContent,

              filters: conditions,

            };

          })

          .filter(Boolean);

        return {

          tokenName,

          content: defaultContent,

          filters,

        };

      })

      .filter(Boolean);

  };



  // États pour la création

  const [selectedSuggestion, setSelectedSuggestion] = useState(null);

  const [generatedContent, setGeneratedContent] = useState('');

  const [isGenerating, setIsGenerating] = useState(false);

  const [emailSuggestions, setEmailSuggestions] = useState([]);

  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);



  // Credentials Mautic pour la création côté backend

  const [userCredentials, setUserCredentials] = useState({

    mautic_url: '',

    mautic_username: '',

    mautic_password: ''

  });

  const [mauticAvailable, setMauticAvailable] = useState(false);

  // Ref pour le timeout de validation du nom (permet d'annuler la validation en attente)
  const validationTimeoutRef = useRef(null);
  
  // Ref pour suivre la méthode précédente et détecter les vrais changements
  const previousMethodRef = useRef(null);


  // Préparer un AbortController pour annuler les requêtes quand la modale ferme

  useEffect(() => {

    if (open) {

      requestControllerRef.current?.abort?.();

      requestControllerRef.current = new AbortController();

    }

    return () => {

      // Annuler toute requête en cours à démontage

      requestControllerRef.current?.abort?.();

    };

  }, [open]);

  useEffect(() => {
    if (!open) {
      autoModeAppliedRef.current = false;
      setAutoModeReady(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || mode !== 'edit') return;
    try {
      if (initialBuilderJson) {
        let parsed = initialBuilderJson;
        try { if (typeof parsed === 'string') parsed = JSON.parse(parsed); } catch (_) {}
        setBuilderJson(parsed);
      }
    } catch (_) {}
    try { if (initialMjml) setMjmlSource(initialMjml); } catch (_) {}
  }, [initialBuilderJson, initialMjml, open, mode]);

  // En mode édition, initialiser emailDynamicContent à partir du JSON
  // dynamicContent renvoyé par le backend (Mautic), si disponible.
  // Cela n'affecte PAS le choix du mode ou du type d'éditeur.
  useEffect(() => {

    if (!open || mode !== 'edit') {

      // Quand la modale se ferme ou qu'on quitte le mode édition,
      // on autorise un nouveau mapping pour le prochain email.
      dynamicContentInitializedRef.current = false;

      return;

    }

    // Attendre que le state éditeur ait fini de charger avant de mapper
    // le dynamicContent côté frontend (sinon on risque d'utiliser une
    // version obsolète provenant de email.dynamicContent).
    if (editorStateLoading) {

      return;

    }

    // Si le contenu dynamique a déjà été initialisé pour cet email
    // dans cette session d'édition, ne pas écraser l'état local
    // (sinon les dernières modifications seraient masquées tant
    // que l'éditeur reste ouvert).
    if (dynamicContentInitializedRef.current) {

      return;

    }

    let sourceDynamic = null;

    if (Array.isArray(initialDynamicContent)) {

      sourceDynamic = initialDynamicContent;

    } else if (email && Array.isArray(email.dynamicContent)) {

      sourceDynamic = email.dynamicContent;

    }

    if (!sourceDynamic) return;

    try {

      const mapped = mapMauticDynamicContentToSlots(sourceDynamic);

      setEmailDynamicContent(mapped || []);

      dynamicContentInitializedRef.current = true;

    } catch (e) {

      console.error('[DYNAMIC_CONTENT] Erreur lors du mapping dynamicContent Mautic vers emailDynamicContent:', e);

    }

  }, [open, mode, initialDynamicContent, email, editorStateLoading, editorStateError]);

  // Synchroniser la ref emailDynamicContentRef avec le state emailDynamicContent
  useEffect(() => {
    try {
      emailDynamicContentRef.current = Array.isArray(emailDynamicContent)
        ? emailDynamicContent
        : [];
    } catch (_) {
      emailDynamicContentRef.current = [];
    }
  }, [emailDynamicContent]);

  useEffect(() => {
    if (!open || mode !== 'edit') return;
    if (editorStateLoading) return;
    if (editorStateError) return;
    if (autoModeAppliedRef.current) return;

    // Tant que la sélection automatique n'est pas finie, empêcher GrapesJS de s'initialiser
    setAutoModeReady(false);

    const hasBuilder = !!initialBuilderJson;
    const hasMjml = !!(initialMjml && String(initialMjml).trim().startsWith('<mjml'));
    const lastMode = (initialLastMode || '').toLowerCase();
    const htmlEdited = Boolean(initialHtmlEdited);

    if (htmlEdited || lastMode === 'html') {
      setIsNaturalMode(false);
      setEditorType('html');
      setCurrentStep(2);
      autoModeAppliedRef.current = true;
      setAutoModeReady(true);
      return;
    }

    if (hasBuilder && !hasMjml) {
      setIsNaturalMode(false);
      setEditorType('html');
      setCurrentStep(2);
      autoModeAppliedRef.current = true;
      setAutoModeReady(true);
      return;
    }

    if (hasBuilder && hasMjml) {
      setIsNaturalMode(false);
      setEditorType('mjml');
      try { setEditorContent(initialMjml); } catch (_) {}
      setCurrentStep(2);
      autoModeAppliedRef.current = true;
      setAutoModeReady(true);
      return;
    }

    if (!hasBuilder && hasMjml) {
      setIsNaturalMode(true);
      setEditorType('mjml');
      setEditorContent(initialMjml);
      setCurrentStep(2);
      autoModeAppliedRef.current = true;
      setAutoModeReady(true);
      return;
    }

    setIsNaturalMode(false);
    setEditorType('html');
    setCurrentStep(2);
    autoModeAppliedRef.current = true;
    setAutoModeReady(true);
  }, [open, mode, initialBuilderJson, initialMjml, initialLastMode, initialHtmlEdited, email?.id, editorStateLoading, editorStateError]);



  const abortOngoingRequests = () => {

    try { requestControllerRef.current?.abort?.(); } catch (_) {}

    requestControllerRef.current = new AbortController();

  };


  // Handlers pour la configuration du contenu dynamique (emailDynamicContent)
  const handleDynamicContentAliasChange = (event) => {
    const value = event.target.value || '';
    setDynamicContentAlias(value);
    const trimmed = value.trim();
    setDynamicContentError('');

    if (!trimmed) {
      setCurrentDcSlot(null);
      setCurrentDcTab('default');
      return;
    }

    setCurrentDcTab((tab) => tab || 'default');

    setCurrentDcSlot((prev) => {
      if (prev && prev.tokenName === trimmed) {
        return prev;
      }

      try {
        const existing = Array.isArray(emailDynamicContentRef.current)
          ? emailDynamicContentRef.current.find((slot) => slot && slot.tokenName === trimmed)
          : null;

        if (existing) {
          return {
            tokenName: existing.tokenName,
            defaultContent: existing.defaultContent || '',
            variations: Array.isArray(existing.variations)
              ? [...existing.variations]
              : [],
          };
        }
      } catch (_) {}

      return {
        tokenName: trimmed,
        defaultContent: '',
        variations: [],
      };
    });
  };

  const handleDcDefaultContentChange = (event) => {
    const value = event.target.value || '';
    const alias = (dynamicContentAlias || '').trim();

    if (!alias) {
      setDynamicContentError("Veuillez d'abord saisir un Nom pour ce contenu dynamique.");
      return;
    }

    setCurrentDcSlot((prev) => {
      const base =
        prev && prev.tokenName === alias
          ? prev
          : {
              tokenName: alias,
              defaultContent: '',
              variations: [],
            };

      return {
        ...base,
        defaultContent: value,
      };
    });
  };

  const handleAddDcVariation = () => {
    const alias = (dynamicContentAlias || '').trim();

    if (!alias) {
      setDynamicContentError("Veuillez d'abord saisir un Nom pour ce contenu dynamique.");
      return;
    }

    setDynamicContentError('');
    setCurrentDcTab('variations');

    let newIndex = 0;

    setCurrentDcSlot((prev) => {
      const base =
        prev && prev.tokenName === alias
          ? prev
          : {
              tokenName: alias,
              defaultContent: prev?.defaultContent || '',
              variations: Array.isArray(prev?.variations) ? prev.variations : [],
            };

      const variations = Array.isArray(base.variations) ? [...base.variations] : [];
      variations.push({
        id: `${Date.now()}-${variations.length}`,
        content: '',
        conditions: [],
      });

      newIndex = variations.length - 1;

      return {
        ...base,
        variations,
      };
    });

    setCurrentDcVariationIndex(newIndex);
  };

  const handleRemoveDcVariation = (variationIndex) => {
    setCurrentDcSlot((prev) => {
      if (!prev || !Array.isArray(prev.variations)) return prev;

      const previousVariations = prev.variations;
      const variations = previousVariations.filter((_, idx) => idx !== variationIndex);
      if (variations.length === 0) {
        setCurrentDcTab('default');
      }

      setCurrentDcVariationIndex((prevIndex) => {
        if (!Array.isArray(previousVariations) || previousVariations.length === 0) {
          return null;
        }

        // Si aucune variation ne reste, on remet l'index à null
        if (variations.length === 0) {
          return null;
        }

        if (typeof prevIndex !== 'number') {
          return 0;
        }

        // Si on supprime une variation avant l'index courant, l'index courant recule d'une place
        if (prevIndex > variationIndex) {
          return prevIndex - 1;
        }

        // Si on supprime la variation actuellement sélectionnée,
        // on sélectionne la variation précédente (ou la première si index 0)
        if (prevIndex === variationIndex) {
          const newIndex = variationIndex - 1;
          return newIndex >= 0 ? newIndex : 0;
        }

        // Sinon, on garde l'index tel quel
        return prevIndex;
      });

      return {
        ...prev,
        variations,
      };
    });
  };

  const handleUpdateDcVariationContent = (variationIndex, value) => {
    setCurrentDcSlot((prev) => {
      if (!prev || !Array.isArray(prev.variations)) return prev;

      const variations = [...prev.variations];
      if (!variations[variationIndex]) return prev;

      variations[variationIndex] = {
        ...variations[variationIndex],
        content: value,
      };

      return {
        ...prev,
        variations,
      };
    });
  };

  const handleAddDcCondition = (variationIndex) => {
    setCurrentDcSlot((prev) => {
      if (!prev || !Array.isArray(prev.variations)) return prev;

      const variations = [...prev.variations];
      const variation = variations[variationIndex];
      if (!variation) return prev;

      const conditions = Array.isArray(variation.conditions)
        ? [...variation.conditions]
        : [];

      const defaultField = MAUTIC_DYNAMIC_FIELDS[0] || null;
      const defaultOps = defaultField ? getMauticOperatorsForType(defaultField.type) : [];
      const defaultOperator = defaultOps.length ? defaultOps[0] : null;

      conditions.push({
        glue: 'and',
        field: defaultField ? defaultField.id : '',
        object: defaultField ? defaultField.object : 'lead',
        type: defaultField ? defaultField.type : 'text',
        operator: defaultOperator ? defaultOperator.id : 'eq',
        filter: '',
        display: null,
      });

      variations[variationIndex] = {
        ...variation,
        conditions,
      };

      return {
        ...prev,
        variations,
      };
    });
  };

  const handleUpdateDcCondition = (variationIndex, conditionIndex, key, value) => {
    setCurrentDcSlot((prev) => {
      if (!prev || !Array.isArray(prev.variations)) return prev;

      const variations = [...prev.variations];
      const variation = variations[variationIndex];
      if (!variation || !Array.isArray(variation.conditions)) return prev;

      const conditions = [...variation.conditions];
      const current = conditions[conditionIndex];
      if (!current) return prev;

      let next = { ...current };

      if (key === 'field') {
        const fieldDef = getMauticFieldById(value);
        if (fieldDef) {
          next.field = fieldDef.id;
          next.object = fieldDef.object;
          next.type = fieldDef.type;
          const opsForType = getMauticOperatorsForType(fieldDef.type);
          if (opsForType.length) {
            next.operator = opsForType[0].id;
          }
        } else {
          next.field = value;
        }
      } else if (key === 'operator') {
        next.operator = value;
      } else {
        next[key] = value;
      }

      conditions[conditionIndex] = next;

      variations[variationIndex] = {
        ...variation,
        conditions,
      };

      return {
        ...prev,
        variations,
      };
    });
  };

  const handleRemoveDcCondition = (variationIndex, conditionIndex) => {
    setCurrentDcSlot((prev) => {
      if (!prev || !Array.isArray(prev.variations)) return prev;

      const variations = [...prev.variations];
      const variation = variations[variationIndex];
      if (!variation || !Array.isArray(variation.conditions)) return prev;

      const conditions = variation.conditions.filter((_, idx) => idx !== conditionIndex);

      variations[variationIndex] = {
        ...variation,
        conditions,
      };

      return {
        ...prev,
        variations,
      };
    });
  };

  const dcVariations =
    currentDcSlot && Array.isArray(currentDcSlot.variations)
      ? currentDcSlot.variations
      : [];

  const hasDcVariations = dcVariations.length > 0;

  const activeDcVariationIndex =
    typeof currentDcVariationIndex === 'number' &&
    currentDcVariationIndex >= 0 &&
    currentDcVariationIndex < dcVariations.length
      ? currentDcVariationIndex
      : null;

  const activeDcVariation =
    activeDcVariationIndex !== null ? dcVariations[activeDcVariationIndex] : null;

  const openDynamicContentDialog = () => {
    try {
      const editor = editorInstanceRef.current;
      if (editor && editor.Modal) {
        const modal = editor.Modal;
        if (gjsDynamicContentModalElRef.current) {
          modal.setTitle('Configurer le contenu dynamique Mautic');
          modal.setContent(gjsDynamicContentModalElRef.current);
        }
        modal.open();
      }
    } catch (_) {}
    setDynamicContentDialogOpen(true);
  };

  const handleCloseDynamicContentDialog = () => {
    try {
      const editor = editorInstanceRef.current;
      if (editor && editor.Modal) {
        editor.Modal.close();
      }
    } catch (_) {}
    dcImageInsertCallbackRef.current = null;
    setDynamicContentDialogOpen(false);
    setSelectedDynamicContentId(null);
    setDynamicContentAlias('');
    setDynamicContentError('');
    setCurrentDcSlot(null);
    setCurrentDcTab('default');
  };

  const handleConfirmDynamicContent = async () => {
    const placeholderToken = '{dynamiccontent="DYNAMIC_SLOT"}';

    // 1) Récupérer le contenu courant depuis GrapesJS (MJML ou HTML)
    let currentContent = '';

    try {
      if (!isNaturalMode && editorInstanceRef.current) {
        const editor = editorInstanceRef.current;

        if (editorType === 'mjml' && typeof editor.getMjml === 'function') {
          currentContent = editor.getMjml();
        } else if (typeof editor.getHtml === 'function') {
          currentContent = editor.getHtml();
        }
      } else {
        currentContent = editorContent || '';
      }
    } catch (e) {
      currentContent = editorContent || '';
    }

    // 2) Déterminer l'alias à utiliser
    let alias = (dynamicContentAlias || '').trim();

    if (!alias && selectedDynamicContentId && dynamicContents.length) {
      const selected = dynamicContents.find(
        (dc) => String(dc.id) === String(selectedDynamicContentId)
      );
      if (selected && selected.name) {
        alias = selected.name;
      }
    }

    if (!alias) {
      setDynamicContentError(
        "Veuillez sélectionner un contenu dynamique ou saisir un alias."
      );
      return;
    }

    const hasPlaceholder = currentContent && currentContent.indexOf(placeholderToken) !== -1;

    // 3) Remplacer le placeholder par le vrai token (uniquement lors de la première insertion)
    if (hasPlaceholder) {
      const newToken = `{dynamiccontent="${alias}"}`;
      const updatedContent = currentContent.replace(placeholderToken, newToken);

      if (!updatedContent || updatedContent === currentContent) {
        handleCloseDynamicContentDialog();
        return;
      }

      try {
        if (!isNaturalMode && editorInstanceRef.current) {
          const editor = editorInstanceRef.current;
          editor.setComponents(updatedContent);
        } else {
          setEditorContent(updatedContent);
        }
        hasUserChangesRef.current = true;
      } catch (e) {
        console.error('[DYNAMIC_CONTENT] Erreur lors de l’insertion du token:', e);
      }
    }

    // 4) Mettre à jour la configuration interne emailDynamicContent pour ce slot (toujours, même sans placeholder)
    try {
      const trimmedAlias = alias.trim();

      if (trimmedAlias) {
        let slotFromState =
          currentDcSlot && currentDcSlot.tokenName === trimmedAlias
            ? currentDcSlot
            : null;

        if (!slotFromState) {
          const existing = Array.isArray(emailDynamicContent)
            ? emailDynamicContent.find((s) => s && s.tokenName === trimmedAlias)
            : null;

          if (existing) {
            slotFromState = {
              tokenName: existing.tokenName,
              defaultContent: existing.defaultContent || '',
              variations: Array.isArray(existing.variations)
                ? existing.variations
                : [],
            };
          }
        }

        if (!slotFromState) {
          slotFromState = {
            tokenName: trimmedAlias,
            defaultContent: '',
            variations: [],
          };
        }

        // Si currentDcSlot correspond bien à cet alias, prioriser ses valeurs
        if (currentDcSlot && currentDcSlot.tokenName === trimmedAlias) {
          slotFromState = {
            tokenName: trimmedAlias,
            defaultContent: currentDcSlot.defaultContent || '',
            variations: Array.isArray(currentDcSlot.variations)
              ? currentDcSlot.variations
              : [],
          };
        }

        setEmailDynamicContent((prev) => {
          const previous = Array.isArray(prev) ? prev : [];
          const index = previous.findIndex(
            (s) => s && s.tokenName === trimmedAlias
          );

          if (index === -1) {
            return [...previous, slotFromState];
          }

          const next = [...previous];
          next[index] = slotFromState;
          return next;
        });
      }
    } catch (e) {
      console.error(
        '[DYNAMIC_CONTENT] Erreur lors de la mise à jour de emailDynamicContent:',
        e
      );
    }

    // 5) En mode IA, proposer un prompt d'insertion au chat si aucun texte n'est encore saisi
    try {
      const aliasForPrompt = (dynamicContentAlias || '').trim();
      if (isNaturalMode && aliasForPrompt && !hasPlaceholder && !chatInput.trim()) {
        setChatInput(
          `Insère le contenu dynamique {dynamiccontent="${aliasForPrompt}"} au meilleur endroit dans l'email.`
        );
      }
    } catch (_) {}

    handleCloseDynamicContentDialog();
  };



  const handleCloseModal = () => {
    // ✅ Bloquer toute tentative de fermeture pendant l'enregistrement
    if (saving) {
      return;
    }
    
    // Ne pas ouvrir la boîte de fermeture si la boîte de switch est déjà ouverte
    if (confirmSwitchOpen) {
      return;
    }
    
    // Vérifier les modifications UNIQUEMENT à l'étape 2 (édition)
    // Aux étapes 0 et 1, fermer directement sans confirmation
    if (currentStep !== 2) {
      abortOngoingRequests();
      onClose();
      return;
    }
    
    // En mode NEW : vérifier si du contenu a été généré mais jamais sauvegardé
    if (mode === 'new' && !hasBeenSavedRef.current) {
      // Contenu par défaut
      const defaultContent = '<p>Commencez à rédiger votre email ici...</p>';
      const normalize = (html) => (html || '').replace(/\s+/g, ' ').trim();
      
      let currentHtml = editorContent;
      try {
        if (!isNaturalMode && editorInstanceRef.current) {
          currentHtml = getHtmlFromGrapes();
        }
      } catch (_) {}
      
      // Si le contenu est différent du contenu par défaut, demander confirmation
      if (normalize(currentHtml) !== normalize(defaultContent)) {
        setConfirmCloseOpen(true);
        return;
      }
    }
    
    // En mode EDIT : vérifier s'il y a des modifications non sauvegardées
    if (mode === 'edit') {
      // Si aucun changement utilisateur n'a été détecté, fermer directement sans confirmation
      if (!hasUserChangesRef.current) {
        abortOngoingRequests();
        onClose();
        return;
      }
      try {
        const currentHtmlNorm = normalizeHtmlForCompare(getCurrentEditorHtml());
        const lastSavedNorm = normalizeHtmlForCompare(lastSavedHtmlRef.current || '');
        if ((currentHtmlNorm || '') !== (lastSavedNorm || '')) {
          setConfirmCloseOpen(true);
          return;
        }
      } catch (_) {
        // Fallback sécurisé si la normalisation échoue
        let currentHtml = editorContent;
        try {
          if (!isNaturalMode && editorInstanceRef.current) {
            currentHtml = getHtmlFromGrapes();
          }
        } catch (_) {}
        const lastSaved = lastSavedHtmlRef.current || '';
        const normalize = (html) => (html || '').replace(/\s+/g, ' ').trim();
        if (normalize(currentHtml) !== normalize(lastSaved)) {
          setConfirmCloseOpen(true);
          return;
        }
      }
    }
    
    // Pas de modifications, fermer directement
    abortOngoingRequests();
    onClose();
  };

  // Fonction pour valider le nom interne (avec debounce manuel et annulation)
  const validateInternalName = useCallback((name) => {
    // ✅ Annuler la validation précédente en attente
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
      validationTimeoutRef.current = null;
    }

    // ✅ Si champ vide, pas de validation ni d'erreur
    if (!name || name.trim().length === 0) {
      setNameValidationError('');
      setNameValidationSuccess(false);
      setNameValidationWarning('');
      setIsValidatingName(false);
      return;
    }

    // ✅ Ne pas valider en mode Edit (c'est l'email en cours d'édition)
    if (mode === 'edit') {
      setNameValidationError('');
      setNameValidationSuccess(false);
      setNameValidationWarning('');
      setIsValidatingName(false);
      return;
    }

    // ✅ Attendre 800ms avant de valider
    validationTimeoutRef.current = setTimeout(async () => {
      setIsValidatingName(true);
      setNameValidationError('');
      setNameValidationSuccess(false);
      setNameValidationWarning('');

      try {
        // ✅ Timeout 30s pour correspondre au backend (qui a 60s)
        const response = await api.post('/emails/validate-name', { name: name.trim() }, { timeout: 30000 });
        
        if (response.data.success && response.data.exists) {
          setNameValidationError('Un email avec ce nom existe déjà. Veuillez choisir un autre nom.');
          setNameValidationSuccess(false);
          setNameValidationWarning('');
        } else {
          // ✅ Nom disponible - feedback positif
          setNameValidationError('');
          setNameValidationSuccess(true);
          setNameValidationWarning('');
        }
      } catch (error) {
        console.error('[VALIDATION] Erreur lors de la validation du nom:', error);
        // ✅ En cas d'erreur réseau/timeout, ne pas bloquer l'utilisateur
        // Le backend fera la validation finale lors de la sauvegarde
        setNameValidationError('');
        setNameValidationSuccess(false);
        
        // ✅ Afficher un message d'avertissement visible pour l'utilisateur
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          console.warn('[VALIDATION] ⚠️ Timeout de validation - validation différée à la sauvegarde');
          setNameValidationWarning('⚠️ Vérification impossible (connexion lente). La disponibilité du nom sera vérifiée lors de l\'enregistrement.');
        } else {
          setNameValidationWarning('⚠️ Erreur de vérification. La disponibilité du nom sera vérifiée lors de l\'enregistrement.');
        }
      } finally {
        setIsValidatingName(false);
      }
    }, 800);
  }, [mode]);


  // Fonction pour générer les suggestions d'emails avec l'IA

  const generateEmailSuggestions = async () => {

    if (!formData.internalName.trim() || !formData.subject.trim() || !formData.objective.trim()) {

      showNotification('Veuillez remplir le nom interne, le sujet et l\'objectif avant de générer des suggestions', 'error');

      return;

    }



    setIsLoadingSuggestions(true);

    setEmailSuggestions([]);



    try {

      // Fetch LLM configuration from backend

    const llmConfig = await fetchLLMConfig();

    

    const formattedQuery = `Génère des suggestions d'emails pour une campagne marketing avec les paramètres suivants :

      - Nom interne: ${formData.internalName}

      - Sujet: ${formData.subject}

      - Preheader: ${formData.preheaderText || 'Non spécifié'}

      - Objectif: ${formData.objective}

      

      Je veux des suggestions d'emails spécifiques avec des sujets accrocheurs, des pré-headers et des descriptions détaillées qui correspondent à l'objectif décrit ci-dessus.`;

    

    console.log('[EMAIL_ASSISTANT] Génération des suggestions avec query:', formattedQuery);

    console.log('[EMAIL_ASSISTANT] Using LLM config:', llmConfig);

    

    // ✅ Récupérer le template sélectionné (version fraîche) ou utiliser le starter template
    let templateContent = null;
    let selected = selectedTemplate;
    try {
      if (selectedTemplate?.id) {
        const resFresh = await api.get('/emails/templates');
        const fresh = Array.isArray(resFresh.data) ? resFresh.data.find(t => t.id === selectedTemplate.id) : null;
        if (fresh) selected = fresh;
      }
    } catch (_) {}
    if (selected && selected.content) {
      templateContent = selected.content;
      console.log('[EMAIL_ASSISTANT] Utilisation du template personnalisé:', selected.name);
    } else {
      templateContent = generateBaseTemplate();
      console.log('[EMAIL_ASSISTANT] Utilisation du starter template');
    }

    const response = await api.post('/emails/suggest_mjml', {

      query: formattedQuery,

      provider: llmConfig.provider,

      model: llmConfig.model,

      template: templateContent  // ✅ Passer le template au backend

    });

    

    console.log('[EMAIL_ASSISTANT] Réponse suggestions:', response.data);

    

    if (response.data.success && response.data.suggestions && response.data.suggestions.length > 0) {

        setEmailSuggestions(response.data.suggestions);

        showNotification('Suggestions d\'emails générées avec succès !', 'success');

      } else {

        showNotification(response.data.message || 'Aucune suggestion d\'email trouvée. Essayez de décrire votre objectif de manière plus détaillée.', 'error');

      }

    } catch (err) {

      console.error('[EMAIL_ASSISTANT] Erreur génération suggestions:', err);

      showNotification('Erreur lors de la génération des suggestions d\'emails. Veuillez réessayer.', 'error');

    } finally {

      setIsLoadingSuggestions(false);

    }

  };



  // Utiliser useLayoutEffect pour garantir que les états sont appliqués AVANT le rendu
  // et AVANT les autres useEffect (notamment celui de GrapesJS)
  useLayoutEffect(() => {

    if (open) {

      hasUserChangesRef.current = false;
      editorInitializedRef.current = false;

      console.log('[INIT] Début initialisation (LAYOUT EFFECT), mode:', mode, 'editorType initial:', editorType, 'editorStateLoading:', editorStateLoading);

      // ✅ Reset validation du nom à l'ouverture
      setNameValidationError('');
      setNameValidationSuccess(false);
      setNameValidationWarning('');
      setIsValidatingName(false);
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
        validationTimeoutRef.current = null;
      }
      
      // ✅ Reset la méthode précédente pour permettre le tracking des changements
      previousMethodRef.current = null;

      // Réinitialiser l'état d'initialisation au début
      setIsInitialized(false);

      // Réinitialiser le flag de sauvegarde
      hasBeenSavedRef.current = (mode === 'edit'); // En mode edit, déjà "sauvegardé"

      // Initialisation uniquement à l'ouverture pour éviter les réinitialisations intempestives

      setCurrentStep(mode === 'edit' ? 2 : 0);

      setIsEditMode(mode === 'edit');

      // Toujours démarrer en mode Manuel pour que GrapesJS soit initialisé

      setIsNaturalMode(false);

      // Déterminer le type d'éditeur à utiliser
      // En mode édition: laisser l'auto-sélection (initialMjml / initialBuilderJson) décider
      // En mode création: définir seulement les valeurs par défaut
      if (mode === 'new') {
        // En mode création, définir selectedMethod à 'ai' par défaut
        setSelectedMethod('ai');
        // Par défaut 'ai' → MJML, sera mis à jour si l'utilisateur change de méthode
        setEditorType('mjml');
        console.log('[EDITOR_TYPE] Mode création (méthode ai par défaut) → Éditeur MJML');
      }

      // Désactiver la propagation live tant que l'utilisateur n'a pas sauvegardé

      allowLivePreviewRef.current = false;

      

      // Réinitialiser tous les états de création seulement en mode création

      if (mode === 'new') {

        resetCreationState();

      }

      

      // En mode édition, si le chargement de l'état éditeur a échoué,
      // ne pas initialiser le contenu ni GrapesJS : on affichera un écran
      // d'erreur avec un bouton "Réessayer".
      if (mode === 'edit' && email && editorStateError) {

        console.log('[INIT] Erreur état éditeur détectée, affichage écran d\'erreur');

        Promise.resolve().then(() => {
          setIsInitialized(true);
        });

        return;

      }

      

      if (mode === 'edit' && email) {

        // En mode édition, si l'état éditeur est encore en cours de chargement,
        // ne pas écraser le contenu : on attendra la fin du chargement pour
        // appliquer la version finale (snapshot ou HTML de l'email).
        if (editorStateLoading) {
          console.log('[INIT] État éditeur en cours de chargement, attente avant application du HTML initial');
        } else {
          // Prioriser l'instantané HTML persistant, sinon fallback sur le HTML
          // de l'email stocké dans la liste.
          const emailHtml =
            (initialHtmlSnapshot && String(initialHtmlSnapshot).trim() !== '' ? initialHtmlSnapshot : null) ||
            email.customHtml ||
            email.content ||
            '<p>Commencez à rédiger votre email ici...</p>';

          console.log('[INIT] Chargement email (avec snapshot si dispo):', {
            id: email.id,
            name: email.name,
            contentLength: emailHtml.length,
            source: initialHtmlSnapshot ? 'snapshot' : 'email',
          });

          setEditorContent(emailHtml);

          setChatMessages([

          { text: `Bonjour ! Quel aspect de l'email "${email.name}" souhaitez-vous modifier ?`, sender: 'assistant' }

        ]);

          // Initialiser l'historique pour le mode édition

          setEmailHistory([emailHtml]);

          setHistoryIndex(0);

          setCanUndo(false);

          setCanRedo(false);

          // Contenu considéré comme sauvegardé au démarrage

          lastSavedHtmlRef.current = emailHtml;
        }

      } else {

        // Mode création - contenu par défaut

        const defaultContent = '<p>Commencez à rédiger votre email ici...</p>';

        setEditorContent(defaultContent);

        setEmailHistory([defaultContent]);

        setHistoryIndex(0);

        setCanUndo(false);

        setCanRedo(false);

        // Contenu considéré comme sauvegardé au démarrage

        lastSavedHtmlRef.current = defaultContent;

      }

      // Marquer l'initialisation comme complète pour afficher l'UI
      // Utiliser un microtask pour s'assurer que tous les états ci-dessus sont appliqués
      // avant d'afficher l'UI (évite les doubles rendus et le flash MJML en mode édition)
      Promise.resolve().then(() => {
        console.log('[INIT] Tous les états initialisés, affichage de l\'UI');
        setIsInitialized(true);
      });

      // Charger les credentials Mautic pour la création

      (async () => {

        try {

          // Essayer d'abord /auth/credentials puis fallback sur /auth/me/credentials
          let res;
          try {
            res = await api.get('/auth/credentials');
          } catch (e) {
            console.warn('[CREDENTIALS] /auth/credentials a échoué, fallback sur /auth/me/credentials');
            res = await api.get('/auth/me/credentials');
          }

          const creds = res?.data?.credentials || {};
          console.log('[CREDENTIALS] Récupération credentials:', {
            hasUrl: !!creds.mautic_url,
            hasUsername: !!creds.mautic_username,
            hasPassword: !!creds.mautic_password,
            url: creds.mautic_url ? creds.mautic_url.substring(0, 20) + '...' : 'missing'
          });

          setUserCredentials({

            mautic_url: creds.mautic_url || '',

            mautic_username: creds.mautic_username || '',

            mautic_password: creds.mautic_password || ''

          });

          const isAvailable = !!(creds.mautic_url && creds.mautic_username && creds.mautic_password);
          console.log('[CREDENTIALS] Mautic disponible:', isAvailable);
          setMauticAvailable(isAvailable);

        } catch (err) {
          console.error('[CREDENTIALS] Erreur récupération:', err);

          setMauticAvailable(false);

        }

      })();

    }

    

    return () => {
      // ✅ Cleanup lors de la fermeture de la modal

      // S'assurer que la prochaine ouverture repart aussi en mode Manuel
      setIsNaturalMode(false);
      setSelectedMethod('ai');

      // Réinitialiser l'état d'initialisation
      setIsInitialized(false);

      // ✅ Reset validation du nom lors de la fermeture
      setNameValidationError('');
      setNameValidationSuccess(false);
      setNameValidationWarning('');
      setIsValidatingName(false);
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
        validationTimeoutRef.current = null;
      }

      // Détruire GrapesJS si présent
      try {
        if (editorInstanceRef.current && editorInstanceRef.current.destroy) {
          editorInstanceRef.current.destroy();
        }
      } catch (_) {}

      editorInstanceRef.current = null;
    };

  }, [open, mode, email?.id, editorStateLoading, initialHtmlSnapshot, editorStateError]);

  // ============================================================
  // GESTION DES TEMPLATES PERSONNALISABLES
  // ============================================================

  // Charger les templates de l'utilisateur
  const loadUserTemplates = async () => {
    try {
      setLoadingTemplates(true);
      console.log('[TEMPLATES] Chargement des templates utilisateur...');
      
      const res = await api.get('/emails/templates');
      
      if (res.data && Array.isArray(res.data)) {
        setUserTemplates(res.data);
        console.log(`[TEMPLATES] ${res.data.length} templates chargés`);
        
        // Auto-sélectionner le template par défaut MJML s'il existe (en mode NEW)
        if (mode === 'new') {
          const defaultTemplate = res.data.find(t => t.is_default && t.type === 'mjml');
          if (defaultTemplate && !selectedTemplate) {
            setSelectedTemplate(defaultTemplate);
            console.log('[TEMPLATES] Template par défaut auto-sélectionné:', defaultTemplate.name);
          }
          // Si un template est déjà sélectionné, le rafraîchir depuis la nouvelle liste
          if (selectedTemplate?.id) {
            const refreshed = res.data.find(t => t.id === selectedTemplate.id);
            if (refreshed) {
              setSelectedTemplate(refreshed);
              console.log('[TEMPLATES] Template sélectionné rafraîchi après reload:', refreshed.name);
            }
          }
        }
      }
      
      setLoadingTemplates(false);
    } catch (error) {
      console.error('[TEMPLATES] Erreur chargement templates:', error);
      showNotification('Erreur lors du chargement des templates', 'error');
      setLoadingTemplates(false);
    }
  };

  // ============================================================
  // FONCTIONS DE MANIPULATION DES ÉLÉMENTS (HEADER/FOOTER)
  // ============================================================
  
  // Ajouter un élément
  const addElement = (section, elementType) => {
    const newElement = {
      id: Date.now(),
      type: elementType,
      props: getDefaultPropsForType(elementType)
    };
    
    if (section === 'header') {
      setTemplateFormData({
        ...templateFormData,
        headerElements: [...templateFormData.headerElements, newElement]
      });
    } else {
      setTemplateFormData({
        ...templateFormData,
        footerElements: [...templateFormData.footerElements, newElement]
      });
    }
  };
  
  // Props par défaut selon le type d'élément
  const getDefaultPropsForType = (type) => {
    switch (type) {
      case 'text':
        return { content: 'Votre texte ici', fontSize: '14px', align: 'center', color: '#000000' };
      case 'image':
        return { src: '/app/assets/images/placeholder.png', width: '100px', align: 'center' };
      case 'button':
        return { text: 'Cliquez ici', href: '#', bgColor: '#007B8A', textColor: '#ffffff' };
      case 'spacer':
        return { height: '10px' };
      case 'divider':
        return { borderWidth: '1px', borderColor: '#cccccc' };
      case 'social':
        return {
          networks: [
            { name: 'facebook', url: '', enabled: false },
            { name: 'instagram', url: '', enabled: false },
            { name: 'twitter', url: '', enabled: false },
            { name: 'x', url: '', enabled: false },
            { name: 'linkedin', url: '', enabled: false },
            { name: 'youtube', url: '', enabled: false },
            { name: 'pinterest', url: '', enabled: false },
            { name: 'tumblr', url: '', enabled: false },
            { name: 'xing', url: '', enabled: false },
            { name: 'github', url: '', enabled: false },
            { name: 'snapchat', url: '', enabled: false },
            { name: 'vimeo', url: '', enabled: false },
            { name: 'medium', url: '', enabled: false },
            { name: 'soundcloud', url: '', enabled: false },
            { name: 'dribbble', url: '', enabled: false },
            { name: 'web', url: '', enabled: false },
            { name: 'google', url: '', enabled: false },
            { name: 'whatsapp', url: '', enabled: false }
          ]
        };
      default:
        return {};
    }
  };
  
  // Supprimer un élément
  const removeElement = (section, elementId) => {
    if (section === 'header') {
      setTemplateFormData({
        ...templateFormData,
        headerElements: templateFormData.headerElements.filter(el => el.id !== elementId)
      });
    } else {
      setTemplateFormData({
        ...templateFormData,
        footerElements: templateFormData.footerElements.filter(el => el.id !== elementId)
      });
    }
  };
  
  // Déplacer un élément vers le haut
  const moveElementUp = (section, elementId) => {
    const elements = section === 'header' ? [...templateFormData.headerElements] : [...templateFormData.footerElements];
    const index = elements.findIndex(el => el.id === elementId);
    
    if (index > 0) {
      [elements[index - 1], elements[index]] = [elements[index], elements[index - 1]];
      
      if (section === 'header') {
        setTemplateFormData({ ...templateFormData, headerElements: elements });
      } else {
        setTemplateFormData({ ...templateFormData, footerElements: elements });
      }
    }
  };
  
  // Déplacer un élément vers le bas
  const moveElementDown = (section, elementId) => {
    const elements = section === 'header' ? [...templateFormData.headerElements] : [...templateFormData.footerElements];
    const index = elements.findIndex(el => el.id === elementId);
    
    if (index < elements.length - 1) {
      [elements[index], elements[index + 1]] = [elements[index + 1], elements[index]];
      
      if (section === 'header') {
        setTemplateFormData({ ...templateFormData, headerElements: elements });
      } else {
        setTemplateFormData({ ...templateFormData, footerElements: elements });
      }
    }
  };
  
  // Mettre à jour les props d'un élément
  const updateElementProps = (section, elementId, newProps) => {
    const elements = section === 'header' ? [...templateFormData.headerElements] : [...templateFormData.footerElements];
    const element = elements.find(el => el.id === elementId);
    
    if (element) {
      element.props = { ...element.props, ...newProps };
      
      if (section === 'header') {
        setTemplateFormData({ ...templateFormData, headerElements: elements });
      } else {
        setTemplateFormData({ ...templateFormData, footerElements: elements });
      }
    }
  };

  // ============================================================
  // FONCTIONS DE PRÉVISUALISATION DES TEMPLATES
  // ============================================================
  
  // Helper pour générer le MJML d'un élément (réutilisé pour sauvegarde et prévisualisation)
  const generateElementMJML = (element) => {
    switch (element.type) {
      case 'text':
        return `        <mj-text font-size="${element.props.fontSize}" align="${element.props.align}" color="${element.props.color}">
          <p>${element.props.content}</p>
        </mj-text>`;
      
      case 'image':
        return `        <mj-image src="${element.props.src}" width="${element.props.width}" align="${element.props.align}"></mj-image>`;
      
      case 'button':
        return `        <mj-button href="${element.props.href}" background-color="${element.props.bgColor}" color="${element.props.textColor}">
          ${element.props.text}
        </mj-button>`;
      
      case 'spacer':
        return `        <mj-spacer height="${element.props.height}"></mj-spacer>`;
      
      case 'divider':
        return `        <mj-divider border-width="${element.props.borderWidth}" border-color="${element.props.borderColor}"></mj-divider>`;
      
      case 'social':
        const enabledNetworks = element.props.networks.filter(n => n.enabled && n.url);
        if (enabledNetworks.length === 0) return '';
        const supportedNames = new Set(['facebook','twitter','google','pinterest','linkedin','tumblr','xing','github','instagram','web','snapchat','youtube','vimeo','medium','soundcloud','dribbble']);
        const whatsappIcon = (element.props && element.props.whatsappIcon) || 'https://img.icons8.com/color/48/25D366/whatsapp--v1.png';
        const xIcon = (element.props && element.props.xIcon) || 'https://upload.wikimedia.org/wikipedia/commons/0/0a/X_logo_2023_%28white%29.svg';
        const socialElements = enabledNetworks.map(network => {
          const rawName = (network.name || '').toLowerCase();
          if (rawName === 'whatsapp') {
            const bg = network.bgColor || '#25D366';
            const iconSrc = network.iconSrc || whatsappIcon;
            return `          <mj-social-element name="custom" href="${network.url}" background-color="${bg}" src="${iconSrc}"></mj-social-element>`;
          }
          if (rawName === 'x') {
            const bg = network.bgColor || '#000000';
            const iconSrc = network.iconSrc || xIcon;
            return `          <mj-social-element name="custom" href="${network.url}" background-color="${bg}" src="${iconSrc}"></mj-social-element>`;
          }
          if (supportedNames.has(rawName)) {
            return `          <mj-social-element name="${rawName}" href="${network.url}"></mj-social-element>`;
          }
          if (network.iconSrc) {
            const bg = network.bgColor || '#000000';
            return `          <mj-social-element name="custom" href="${network.url}" background-color="${bg}" src="${network.iconSrc}"></mj-social-element>`;
          }
          return `          <mj-social-element name="web" href="${network.url}"></mj-social-element>`;
        }).join('\n');
        return `        <mj-social font-size="12px" icon-size="30px" mode="horizontal">\n${socialElements}\n        </mj-social>`;
      
      default:
        return '';
    }
  };
  
  // Générer le MJML complet à partir des éléments header/footer
  const generateFullMJMLFromElements = (headerElements, footerElements, headerBgColor, footerBgColor) => {
    // Générer le MJML pour le header
    const headerMJML = headerElements
      .map(el => generateElementMJML(el))
      .filter(mjml => mjml !== '')
      .join('\n');
    
    // Générer le MJML pour le footer
    const footerMJML = footerElements
      .map(el => generateElementMJML(el))
      .filter(mjml => mjml !== '')
      .join('\n');
    
    // Template MJML complet
    return `<mjml>
  <mj-head>
    <mj-style inline="inline">
      p, li { margin:0 !important; padding:0; line-height:1.4em; }
    </mj-style>
  </mj-head>
  
  <mj-body background-color="#d0d0d0">
    
    <!-- HEADER PERSONNALISÉ -->
    <mj-section background-color="${headerBgColor}">
      <mj-column>
${headerMJML}
      </mj-column>
    </mj-section>
    
    <!-- BODY CONTENT (zone éditable) -->
    <mj-section background-color="#ffffff">
      <mj-column width="550px">
        <mj-text font-size="30px" align="center" font-weight="700">
          <p>Your main headline goes here</p>
        </mj-text>
        <mj-text font-size="16px" align="center">
          <p>Customize these sections with your brand voice.</p>
        </mj-text>
        <mj-spacer></mj-spacer>
      </mj-column>
    </mj-section>
    
    <!-- FOOTER PERSONNALISÉ -->
    <mj-section padding-top="0" background-color="${footerBgColor}">
      <mj-column>
${footerMJML}
      </mj-column>
    </mj-section>
    
  </mj-body>
</mjml>`;
  };
  
  // Prévisualiser le template en cours de personnalisation
  const handlePreviewCustomTemplate = () => {
    try {
      console.log('[PREVIEW] Génération de la prévisualisation du template personnalisé...');
      
      const mjmlContent = generateFullMJMLFromElements(
        templateFormData.headerElements,
        templateFormData.footerElements,
        templateFormData.headerBgColor,
        templateFormData.footerBgColor
      );
      
      // Compiler en HTML
      const result = mjml2html(mjmlContent, {
        validationLevel: 'soft',
        minify: false,
      });
      
      if (result.errors && result.errors.length > 0) {
        console.warn('[PREVIEW] Avertissements MJML:', result.errors);
      }
      
      setCustomPreviewHtml(result.html);
      setCustomPreviewOpen(true);
      console.log('[PREVIEW] Prévisualisation générée avec succès');
    } catch (error) {
      console.error('[PREVIEW] Erreur lors de la génération:', error);
      showNotification('Erreur lors de la génération de la prévisualisation', 'error');
    }
  };
  
  // Prévisualiser un template sélectionné (étape 0)
  const handlePreviewTemplate = (template) => {
    try {
      console.log('[PREVIEW] Prévisualisation du template:', template.name);
      
      if (!template.content) {
        showNotification('Ce template n\'a pas de contenu à prévisualiser', 'warning');
        return;
      }
      
      // Si c'est du MJML, compiler
      if (template.content.trim().startsWith('<mjml')) {
        let mjmlStr = template.content;
        try {
          // Nettoyer anciens attributs illégaux et normaliser "x"
          mjmlStr = (mjmlStr || '')
            .replace(/\s+text-mode="[^"]*"/gi, '')
            .replace(/\s+display="[^"]*"/gi, '')
            .replace(/<mj-social-element([^>]*?)name="x"/gi, '<mj-social-element$1name="twitter"');
        } catch (_) {}
        const result = mjml2html(mjmlStr, {
          validationLevel: 'soft',
          minify: false,
        });
        
        if (result.errors && result.errors.length > 0) {
          console.warn('[PREVIEW] Avertissements MJML:', result.errors);
        }
        
        setTemplatePreviewHtml(result.html);
      } else {
        // Sinon c'est déjà du HTML
        setTemplatePreviewHtml(template.content);
      }
      
      setTemplatePreviewOpen(true);
      console.log('[PREVIEW] Prévisualisation du template ouverte');
    } catch (error) {
      console.error('[PREVIEW] Erreur lors de la prévisualisation:', error);
      showNotification('Erreur lors de la prévisualisation du template', 'error');
    }
  };

  // Sauvegarder un template personnalisé (header + footer) - GÉNÈRE LE MJML AUTOMATIQUEMENT
  const saveCustomTemplate = async () => {
    if (!templateFormData.name.trim()) {
      showNotification('Veuillez entrer un nom pour le template', 'error');
      return;
    }

    try {
      setSavingTemplate(true);
      console.log('[TEMPLATES] Génération du MJML à partir des éléments...');
      
      // Fonction helper pour générer le MJML d'un élément
      const generateElementMJML = (element) => {
        switch (element.type) {
          case 'text':
            return `        <mj-text font-size="${element.props.fontSize}" align="${element.props.align}" color="${element.props.color}">
          <p>${element.props.content}</p>
        </mj-text>`;
          
          case 'image':
            return `        <mj-image src="${element.props.src}" width="${element.props.width}" align="${element.props.align}"></mj-image>`;
          
          case 'button':
            return `        <mj-button href="${element.props.href}" background-color="${element.props.bgColor}" color="${element.props.textColor}">
          ${element.props.text}
        </mj-button>`;
          
          case 'spacer':
            return `        <mj-spacer height="${element.props.height}"></mj-spacer>`;
          
          case 'divider':
            return `        <mj-divider border-width="${element.props.borderWidth}" border-color="${element.props.borderColor}"></mj-divider>`;
          
          case 'social':
            const enabledNetworks = element.props.networks.filter(n => n.enabled && n.url);
            if (enabledNetworks.length === 0) return '';
            const supportedNamesLocal = new Set(['facebook','twitter','google','pinterest','linkedin','tumblr','xing','github','instagram','web','snapchat','youtube','vimeo','medium','soundcloud','dribbble']);
            const whatsappIconLocal = (element.props && element.props.whatsappIcon) || 'https://img.icons8.com/color/48/25D366/whatsapp--v1.png';
            const xIconLocal = (element.props && element.props.xIcon) || 'https://upload.wikimedia.org/wikipedia/commons/0/0a/X_logo_2023_%28white%29.svg';
            const socialElements = enabledNetworks.map(network => {
              const rawName = (network.name || '').toLowerCase();
              if (rawName === 'whatsapp') {
                const bg = network.bgColor || '#25D366';
                const iconSrc = network.iconSrc || whatsappIconLocal;
                return `          <mj-social-element name="custom" href="${network.url}" background-color="${bg}" src="${iconSrc}"></mj-social-element>`;
              }
              if (rawName === 'x') {
                const bg = network.bgColor || '#000000';
                const iconSrc = network.iconSrc || xIconLocal;
                return `          <mj-social-element name="custom" href="${network.url}" background-color="${bg}" src="${iconSrc}"></mj-social-element>`;
              }
              if (supportedNamesLocal.has(rawName)) {
                return `          <mj-social-element name="${rawName}" href="${network.url}"></mj-social-element>`;
              }
              if (network.iconSrc) {
                const bg = network.bgColor || '#000000';
                return `          <mj-social-element name="custom" href="${network.url}" background-color="${bg}" src="${network.iconSrc}"></mj-social-element>`;
              }
              return `          <mj-social-element name="web" href="${network.url}"></mj-social-element>`;
            }).join('\n');
            return `        <mj-social font-size="12px" icon-size="30px" mode="horizontal">\n${socialElements}\n        </mj-social>`;
          
          default:
            return '';
        }
      };
      
      // Générer le MJML pour le header
      const headerMJML = templateFormData.headerElements
        .map(el => generateElementMJML(el))
        .filter(mjml => mjml !== '')
        .join('\n');
      
      // Générer le MJML pour le footer
      const footerMJML = templateFormData.footerElements
        .map(el => generateElementMJML(el))
        .filter(mjml => mjml !== '')
        .join('\n');
      
      // Template MJML complet
      const mjmlContent = `<mjml>
  <mj-head>
    <mj-style inline="inline">
      p, li { margin:0 !important; padding:0; line-height:1.4em; }
    </mj-style>
  </mj-head>
  
  <mj-body background-color="#d0d0d0">
    
    <!-- HEADER PERSONNALISÉ -->
    <mj-section background-color="${templateFormData.headerBgColor}">
      <mj-column>
${headerMJML}
      </mj-column>
    </mj-section>
    
    <!-- BODY CONTENT (zone éditable) -->
    <mj-section background-color="#ffffff">
      <mj-column width="550px">
        <mj-text font-size="30px" align="center" font-weight="700">
          <p>Your main headline goes here</p>
        </mj-text>
        <mj-text font-size="16px" align="center">
          <p>Customize these sections with your brand voice.</p>
        </mj-text>
        <mj-spacer></mj-spacer>
      </mj-column>
    </mj-section>
    
    <!-- FOOTER PERSONNALISÉ -->
    <mj-section padding-top="0" background-color="${templateFormData.footerBgColor}">
      <mj-column>
${footerMJML}
      </mj-column>
    </mj-section>
    
  </mj-body>
</mjml>`.trim();
      
      let res;
      
      if (editingTemplate) {
        // MODE MODIFICATION : Utiliser PUT
        console.log('[TEMPLATES] Mise à jour du template:', editingTemplate.id);
        res = await api.put(`/emails/templates/${editingTemplate.id}`, {
          name: templateFormData.name,
          description: templateFormData.description || '',
          content: mjmlContent,
          is_default: templateFormData.isDefault
        });
      } else {
        // MODE CRÉATION : Utiliser POST
        console.log('[TEMPLATES] Création nouveau template');
        res = await api.post('/emails/templates', {
          name: templateFormData.name,
          description: templateFormData.description || '',
          type: 'mjml',
          content: mjmlContent,
          is_default: templateFormData.isDefault
        });
      }
      
      if (res.data && (res.data.id || editingTemplate)) {
        showNotification(editingTemplate ? 'Template modifié avec succès !' : 'Template créé avec succès !', 'success');
        
        // Mettre à jour la sélection immédiatement avec la version générée (garantie d'être à jour)
        const saved = res.data || {};
        const updatedSelected = { ...saved, content: mjmlContent, name: saved.name || templateFormData.name, type: saved.type || 'mjml', description: saved.description ?? templateFormData.description, is_default: saved.is_default ?? templateFormData.isDefault };
        try { setSelectedTemplate(updatedSelected); } catch (_) {}
        
        // Recharger la liste des templates puis rafraîchir la sélection depuis cette liste
        await loadUserTemplates();
        try {
          const latest = (userTemplates || []).find(t => t.id === (res.data?.id || editingTemplate?.id));
          if (latest) setSelectedTemplate({ ...latest, content: latest.content || mjmlContent });
        } catch (_) {}
        
        // Réinitialiser le formulaire
        setTemplateFormData({
          name: '',
          description: '',
          isDefault: false,
          headerElements: [
            { id: Date.now(), type: 'text', props: { content: '{webview_text}', fontSize: '11px', align: 'center', color: '#000000' } },
            { id: Date.now() + 1, type: 'spacer', props: { height: '10px' } },
            { id: Date.now() + 2, type: 'image', props: { src: '/app/assets/images/placeholder-logo.png', width: '70px', align: 'center' } }
          ],
          footerElements: [
            { id: Date.now(), type: 'spacer', props: { height: '10px' } },
            { id: Date.now() + 1, type: 'social', props: { 
              networks: [
                { name: 'facebook', url: '', enabled: false },
                { name: 'instagram', url: '', enabled: false },
                /*{ name: 'twitter', url: '', enabled: false },*/
                { name: 'x', url: '', enabled: false },
                { name: 'linkedin', url: '', enabled: false },
                { name: 'youtube', url: '', enabled: false },
                { name: 'pinterest', url: '', enabled: false },
                { name: 'tumblr', url: '', enabled: false },
                { name: 'xing', url: '', enabled: false },
                { name: 'github', url: '', enabled: false },
                { name: 'snapchat', url: '', enabled: false },
                { name: 'vimeo', url: '', enabled: false },
                { name: 'medium', url: '', enabled: false },
                { name: 'soundcloud', url: '', enabled: false },
                { name: 'dribbble', url: '', enabled: false },
                { name: 'web', url: '', enabled: false },
                { name: 'google', url: '', enabled: false },
                { name: 'whatsapp', url: '', enabled: false }
              ]
            }},
            { id: Date.now() + 2, type: 'spacer', props: { height: '10px' } },
            { id: Date.now() + 3, type: 'text', props: { content: `${currentBrand?.name || '{brand=name}'}<br/>11111 Beautiful City, 1212 Nice Street<br/>Brazil`, fontSize: '12px', align: 'center', color: '#ffffff' } },
            { id: Date.now() + 4, type: 'spacer', props: { height: '10px' } },
            { id: Date.now() + 5, type: 'text', props: { content: 'Fancy seeing you down here. You\'re getting this email because you gave us your email address.<br/>Want to change how you receive these emails?', fontSize: '11px', align: 'center', color: '#b3b3b3' } },
            { id: Date.now() + 6, type: 'text', props: { content: '{unsubscribe_text}', fontSize: '11px', align: 'center', color: '#b3b3b3' } },
            { id: Date.now() + 7, type: 'spacer', props: { height: '10px' } }
          ],
          headerBgColor: '#ffffff',
          footerBgColor: '#000000'
        });
        
        setSavingTemplate(false);
        setTemplateEditorOpen(false);
        setEditingTemplate(null); // Réinitialiser le template en édition
        
        // Revenir à la liste des templates
        setTemplateManagerOpen(true);
      }
    } catch (error) {
      console.error('[TEMPLATES] Erreur sauvegarde template:', error);
      showNotification('Erreur lors de la sauvegarde du template', 'error');
      setSavingTemplate(false);
    }
  };

  // Réinitialiser le formulaire de template aux valeurs par défaut
  const resetTemplateFormData = () => {
    console.log('[TEMPLATES] Réinitialisation du formulaire template');
    setTemplateFormData({
      name: '',
      description: '',
      isDefault: false,
      headerElements: [
        { id: Date.now(), type: 'text', props: { content: '{webview_text}', fontSize: '11px', align: 'center', color: '#000000' } },
        { id: Date.now() + 1, type: 'spacer', props: { height: '10px' } },
        { id: Date.now() + 2, type: 'image', props: { src: '/app/assets/images/placeholder-logo.png', width: '70px', align: 'center' } }
      ],
      footerElements: [
        { id: Date.now(), type: 'spacer', props: { height: '10px' } },
        { id: Date.now() + 1, type: 'social', props: { 
          networks: [
            { name: 'facebook', url: '', enabled: false },
            { name: 'instagram', url: '', enabled: false },
            /*{ name: 'twitter', url: '', enabled: false },*/
            { name: 'x', url: '', enabled: false },
            { name: 'linkedin', url: '', enabled: false },
            { name: 'youtube', url: '', enabled: false },
            { name: 'pinterest', url: '', enabled: false },
            { name: 'tumblr', url: '', enabled: false },
            { name: 'xing', url: '', enabled: false },
            { name: 'github', url: '', enabled: false },
            { name: 'snapchat', url: '', enabled: false },
            { name: 'vimeo', url: '', enabled: false },
            { name: 'medium', url: '', enabled: false },
            { name: 'soundcloud', url: '', enabled: false },
            { name: 'dribbble', url: '', enabled: false },
            { name: 'web', url: '', enabled: false },
            { name: 'google', url: '', enabled: false },
            { name: 'whatsapp', url: '', enabled: false }
          ]
        }},
        { id: Date.now() + 2, type: 'spacer', props: { height: '10px' } },
        { id: Date.now() + 3, type: 'text', props: { content: `${currentBrand?.name || '{brand=name}'}<br/>11111 Beautiful City, 1212 Nice Street<br/>Brazil`, fontSize: '12px', align: 'center', color: '#ffffff' } },
        { id: Date.now() + 4, type: 'spacer', props: { height: '10px' } },
        { id: Date.now() + 5, type: 'text', props: { content: 'Fancy seeing you down here. You\'re getting this email because you gave us your email address.<br/>Want to change how you receive these emails?', fontSize: '11px', align: 'center', color: '#b3b3b3' } },
        { id: Date.now() + 6, type: 'text', props: { content: '{unsubscribe_text}', fontSize: '11px', align: 'center', color: '#b3b3b3' } },
        { id: Date.now() + 7, type: 'spacer', props: { height: '10px' } }
      ],
      headerBgColor: '#ffffff',
      footerBgColor: '#000000'
    });
  };

  // Charger un template pour modification
  const editTemplate = (template) => {
    console.log('[TEMPLATES] Chargement template pour modification:', template.name);
    
    // Définir le template en cours d'édition
    setEditingTemplate(template);
    
    // Parser le MJML pour extraire les éléments
    // Note: Parsing MJML complexe, on va extraire les sections header/footer
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(template.content, 'text/xml');

      const idBase = Date.now();
      let idInc = 0;
      const nextId = () => idBase + (idInc++);

      const canonicalNetworks = ['facebook','instagram','twitter','x','linkedin','youtube','pinterest','tumblr','xing','github','snapchat','vimeo','medium','soundcloud','dribbble','web','google','whatsapp'];

      const parseSocial = (socialEl) => {
        const nets = canonicalNetworks.map(n => ({ name: n, url: '', enabled: false }));
        const items = Array.from(socialEl ? socialEl.getElementsByTagName('mj-social-element') : []);
        items.forEach(se => {
          const href = se.getAttribute('href') || '';
          const nameAttr = (se.getAttribute('name') || '').toLowerCase();
          const srcAttr = (se.getAttribute('src') || '').toLowerCase();
          const bg = (se.getAttribute('background-color') || '').toLowerCase();
          let targetName = '';
          if (nameAttr && canonicalNetworks.includes(nameAttr)) {
            targetName = nameAttr;
          } else if (href.includes('wa.me') || href.includes('whatsapp') || srcAttr.includes('whatsapp') || bg.includes('#25d366')) {
            targetName = 'whatsapp';
          } else if (canonicalNetworks.includes('web')) {
            targetName = 'web';
          }
          if (targetName) {
            const found = nets.find(n => n.name === targetName);
            if (found) {
              found.url = href;
              found.enabled = !!href;
            }
          }
        });
        return { id: nextId(), type: 'social', props: { networks: nets } };
      };

      const parseSectionNode = (sectionEl) => {
        if (!sectionEl) return { elements: [], bgColor: undefined };
        const bgColor = sectionEl.getAttribute('background-color') || undefined;
        const column = sectionEl.getElementsByTagName('mj-column')[0] || sectionEl;
        const out = [];
        Array.from(column.childNodes).forEach(node => {
          if (node.nodeType !== 1) return;
          const tag = node.nodeName.toLowerCase();
          if (tag === 'mj-text') {
            let content = '';
            try {
              const serializer = new XMLSerializer();
              const pEl = node.querySelector ? node.querySelector('p') : null;
              const source = pEl || node;
              const parts = Array.from(source.childNodes).map(ch => {
                if (ch.nodeType === 3) return ch.nodeValue; // texte brut
                try { return serializer.serializeToString(ch); } catch (_) { return ch.textContent || ''; }
              });
              content = parts.join('').trim();
              // Normaliser les br autoclosants si le sérialiseur les a transformés
              content = content.replace(/<br\s*><\/br>/gi, '<br/>');
            } catch (e) {
              const contentP = node.querySelector ? node.querySelector('p') : null;
              content = (contentP ? contentP.textContent : node.textContent || '').trim();
            }
            const fontSize = node.getAttribute('font-size') || '16px';
            const align = node.getAttribute('align') || 'left';
            const color = node.getAttribute('color') || '#000000';
            out.push({ id: nextId(), type: 'text', props: { content, fontSize, align, color } });
          } else if (tag === 'mj-image') {
            const src = node.getAttribute('src') || '';
            const width = node.getAttribute('width') || '70px';
            const align = node.getAttribute('align') || 'center';
            out.push({ id: nextId(), type: 'image', props: { src, width, align } });
          } else if (tag === 'mj-button') {
            const href = node.getAttribute('href') || '';
            const bgColorBtn = node.getAttribute('background-color') || '#007B8A';
            const textColor = node.getAttribute('color') || '#ffffff';
            const text = (node.textContent || '').trim();
            out.push({ id: nextId(), type: 'button', props: { href, bgColor: bgColorBtn, textColor, text } });
          } else if (tag === 'mj-spacer') {
            const height = node.getAttribute('height') || '10px';
            out.push({ id: nextId(), type: 'spacer', props: { height } });
          } else if (tag === 'mj-divider') {
            const borderWidth = node.getAttribute('border-width') || '1px';
            const borderColor = node.getAttribute('border-color') || '#cccccc';
            out.push({ id: nextId(), type: 'divider', props: { borderWidth, borderColor } });
          } else if (tag === 'mj-social') {
            out.push(parseSocial(node));
          }
        });
        return { elements: out, bgColor };
      };

      const contentStr = template.content || '';
      const headerMatch = contentStr.match(/<!--\s*HEADER PERSONNALISÉ\s*-->[\s\S]*?<mj-section[\s\S]*?>[\s\S]*?<\/mj-section>/i);
      const footerMatch = contentStr.match(/<!--\s*FOOTER PERSONNALISÉ\s*-->[\s\S]*?<mj-section[\s\S]*?>[\s\S]*?<\/mj-section>/i);

      let headerParsed = { elements: [], bgColor: undefined };
      let footerParsed = { elements: [], bgColor: undefined };

      if (headerMatch) {
        const docH = parser.parseFromString(`<root>${headerMatch[0]}</root>`, 'text/xml');
        const secH = docH.getElementsByTagName('mj-section')[0];
        headerParsed = parseSectionNode(secH);
      }
      if (footerMatch) {
        const docF = parser.parseFromString(`<root>${footerMatch[0]}</root>`, 'text/xml');
        const secF = docF.getElementsByTagName('mj-section')[0];
        footerParsed = parseSectionNode(secF);
      }

      if (!headerMatch || headerParsed.elements.length === 0 || !footerMatch || footerParsed.elements.length === 0) {
        const allSections = doc.getElementsByTagName('mj-section');
        if (allSections && allSections.length > 0) {
          const headerSec = allSections[0];
          const footerSec = allSections[allSections.length - 1];
          if (headerParsed.elements.length === 0) headerParsed = parseSectionNode(headerSec);
          if (footerParsed.elements.length === 0) footerParsed = parseSectionNode(footerSec);
        }
      }

      const headerBgColor = headerParsed.bgColor || '#ffffff';
      const footerBgColor = footerParsed.bgColor || '#000000';

      const defaultHeader = [
        { id: nextId(), type: 'text', props: { content: '{webview_text}', fontSize: '11px', align: 'center', color: '#000000' } },
        { id: nextId(), type: 'spacer', props: { height: '10px' } },
        { id: nextId(), type: 'image', props: { src: '/app/assets/images/placeholder-logo.png', width: '70px', align: 'center' } }
      ];
      const defaultFooter = [
        { id: nextId(), type: 'spacer', props: { height: '10px' } },
        { id: nextId(), type: 'social', props: { networks: canonicalNetworks.map(n => ({ name: n, url: '', enabled: false })) } },
        { id: nextId(), type: 'spacer', props: { height: '10px' } },
        { id: nextId(), type: 'text', props: { content: `${currentBrand?.name || '{brand=name}'}<br/>11111 Beautiful City, 1212 Nice Street<br/>Brazil`, fontSize: '12px', align: 'center', color: '#ffffff' } },
        { id: nextId(), type: 'spacer', props: { height: '10px' } },
        { id: nextId(), type: 'text', props: { content: 'Fancy seeing you down here. You\'re getting this email because you gave us your email address.<br/>Want to change how you receive these emails?', fontSize: '11px', align: 'center', color: '#b3b3b3' } },
        { id: nextId(), type: 'text', props: { content: '{unsubscribe_text}', fontSize: '11px', align: 'center', color: '#b3b3b3' } },
        { id: nextId(), type: 'spacer', props: { height: '10px' } }
      ];

      const headerElements = headerParsed.elements.length ? headerParsed.elements : defaultHeader;
      const footerElements = footerParsed.elements.length ? footerParsed.elements : defaultFooter;

      setTemplateFormData({
        name: template.name,
        description: template.description || '',
        isDefault: template.is_default || false,
        headerElements,
        footerElements,
        headerBgColor,
        footerBgColor
      });

      setTemplateManagerOpen(false);
      setTemplateEditorOpen(true);
      showNotification('Template chargé. Personnalisez le header et footer puis sauvegardez.', 'info');
    } catch (error) {
      console.error('[TEMPLATES] Erreur parsing template:', error);
      showNotification('Erreur lors du chargement du template', 'error');
    }
  };

  // Supprimer un template
  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce template ?')) {
      return;
    }

    try {
      console.log('[TEMPLATES] Suppression template:', templateId);
      
      await api.delete(`/emails/templates/${templateId}`);
      
      showNotification('Template supprimé avec succès', 'success');
      
      // Recharger la liste
      await loadUserTemplates();
      
      // Désélectionner si c'était le template sélectionné
      if (selectedTemplate?.id === templateId) {
        setSelectedTemplate(null);
      }
    } catch (error) {
      console.error('[TEMPLATES] Erreur suppression template:', error);
      showNotification('Erreur lors de la suppression du template', 'error');
    }
  };

  // Appliquer un template sélectionné
  const applyTemplate = (template) => {
    if (!template) return;
    
    console.log('[TEMPLATES] Application du template:', template.name);
    
    // Charger le contenu du template (toujours prendre la version la plus fraîche depuis la liste si possible)
    const fresh = (userTemplates || []).find(t => t.id === template.id) || template;
    setEditorContent(fresh.content);
    lastSavedHtmlRef.current = fresh.content;
    // Marquer le template comme sélectionné pour refléter visuellement à l'étape 0
    try { setSelectedTemplate(fresh); } catch (_) {}
    
    // Mettre à jour l'historique
    setEmailHistory([fresh.content]);
    setHistoryIndex(0);
    setCanUndo(false);
    setCanRedo(false);
    
    showNotification(`Template "${template.name}" appliqué`, 'success');
  };

  // Charger les templates au montage du composant en mode NEW
  useEffect(() => {
    if (open && mode === 'new' && currentStep === 0) {
      loadUserTemplates();
    }
  }, [open, mode, currentStep]);

  // ✅ Recharger les templates quand la brand change
  useEffect(() => {
    if (currentBrand?.id && open && mode === 'new') {
      console.log('[TEMPLATES] Brand changée, rechargement des templates pour:', currentBrand.name);
      loadUserTemplates();
      // Réinitialiser le template sélectionné car il appartient peut-être à l'ancienne brand
      setSelectedTemplate(null);
    }
  }, [currentBrand?.id, open, mode]);

  useEffect(() => {
    console.log('[EDITOR_TYPE_EFFECT] Déclenchement useEffect selectedMethod:', { selectedMethod, mode, editorType });
    // Ne s'exécuter QUE en mode création pour éviter de changer editorType en mode édition
    if (mode === 'new') {
      if (selectedMethod === 'ai' || selectedMethod === 'manual') {
        setEditorType('mjml');
        console.log(`[EDITOR_TYPE] Méthode ${selectedMethod} → Éditeur MJML`);
      } else if (selectedMethod === 'import') {
        setEditorType('html');
        console.log('[EDITOR_TYPE] Méthode import → Éditeur HTML');
      }
    } else {
      console.log('[EDITOR_TYPE_EFFECT] Mode édition - pas de changement de editorType');
    }
    // En mode édition, ne rien faire - editorType reste 'html' comme défini dans le useEffect d'initialisation
  }, [selectedMethod, mode]);

  // ✅ Reset validation du nom ET champs formData quand l'utilisateur change de méthode
  useEffect(() => {
    // Si c'est un vrai changement de méthode (pas l'initialisation)
    if (selectedMethod && previousMethodRef.current !== null && previousMethodRef.current !== selectedMethod) {
      console.log(`[FORM_RESET] Changement de méthode: ${previousMethodRef.current} → ${selectedMethod}`);
      
      // Reset validation
      setNameValidationError('');
      setNameValidationSuccess(false);
      setNameValidationWarning('');
      setIsValidatingName(false);
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
        validationTimeoutRef.current = null;
      }
      
      // ✅ Reset les champs du formulaire
      setFormData({
        internalName: '',
        subject: '',
        preheaderText: '',
        objective: '',
        htmlFile: null
      });
    }
    
    // Mettre à jour la méthode précédente
    if (selectedMethod) {
      previousMethodRef.current = selectedMethod;
    }
  }, [selectedMethod]);

  useEffect(() => {
    if (!templateEditorOpen) return;
    const handle = setTimeout(() => {
      try {
        const gen = (el) => {
          switch (el.type) {
            case 'text':
              return `        <mj-text font-size="${el.props.fontSize}" align="${el.props.align}" color="${el.props.color}">
          <p>${el.props.content}</p>
        </mj-text>`;
            case 'image':
              return `        <mj-image src="${el.props.src}" width="${el.props.width}" align="${el.props.align}"></mj-image>`;
            case 'button':
              return `        <mj-button href="${el.props.href}" background-color="${el.props.bgColor}" color="${el.props.textColor}">
          ${el.props.text}
        </mj-button>`;
            case 'spacer':
              return `        <mj-spacer height="${el.props.height}"></mj-spacer>`;
            case 'divider':
              return `        <mj-divider border-width="${el.props.borderWidth}" border-color="${el.props.borderColor}"></mj-divider>`;
            case 'social': {
              const enabledNetworks = (el.props.networks || []).filter(n => n.enabled && n.url);
              if (enabledNetworks.length === 0) return '';
              const whatsappIconLocal = (el.props && el.props.whatsappIcon) || 'https://img.icons8.com/color/48/25D366/whatsapp--v1.png';
              const xIconLocal = (el.props && el.props.xIcon) || 'https://upload.wikimedia.org/wikipedia/commons/0/0a/X_logo_2023_%28white%29.svg';
              const supported = new Set(['facebook','twitter','google','pinterest','linkedin','tumblr','xing','github','instagram','web','snapchat','youtube','vimeo','medium','soundcloud','dribbble']);
              const socialElements = enabledNetworks.map(network => {
                const rawName = (network.name || '').toLowerCase();
                if (rawName === 'whatsapp') {
                  const bg = network.bgColor || '#25D366';
                  const iconSrc = network.iconSrc || whatsappIconLocal;
                  return `          <mj-social-element name="custom" href="${network.url}" background-color="${bg}" src="${iconSrc}"></mj-social-element>`;
                }
                if (rawName === 'x') {
                  const bg = network.bgColor || '#000000';
                  const iconSrc = network.iconSrc || xIconLocal;
                  return `          <mj-social-element name="custom" href="${network.url}" background-color="${bg}" src="${iconSrc}"></mj-social-element>`;
                }
                if (supported.has(rawName)) {
                  return `          <mj-social-element name="${rawName}" href="${network.url}"></mj-social-element>`;
                }
                if (network.iconSrc) {
                  const bg = network.bgColor || '#000000';
                  return `          <mj-social-element name="custom" href="${network.url}" background-color="${bg}" src="${network.iconSrc}"></mj-social-element>`;
                }
                return `          <mj-social-element name="web" href="${network.url}"></mj-social-element>`;
              }).join('\n');
              return `        <mj-social font-size="12px" icon-size="30px" mode="horizontal">\n${socialElements}\n        </mj-social>`;
            }
            default:
              return '';
          }
        };
        const headerMJML = (templateFormData.headerElements || []).map(gen).filter(Boolean).join('\n');
        const footerMJML = (templateFormData.footerElements || []).map(gen).filter(Boolean).join('\n');
        const mjmlContent = `<mjml>
  <mj-head>
    <mj-style inline="inline">
      p, li { margin:0 !important; padding:0; line-height:1.4em; }
    </mj-style>
  </mj-head>
  <mj-body background-color="#d0d0d0">
    <mj-section background-color="${templateFormData.headerBgColor}">
      <mj-column>
${headerMJML}
      </mj-column>
    </mj-section>
    <mj-section background-color="#ffffff">
      <mj-column width="550px">
        <mj-text font-size="30px" align="center" font-weight="700">
          <p>Your main headline goes here</p>
        </mj-text>
        <mj-text font-size="16px" align="center">
          <p>Customize these sections with your brand voice.</p>
        </mj-text>
        <mj-spacer></mj-spacer>
      </mj-column>
    </mj-section>
    <mj-section padding-top="0" background-color="${templateFormData.footerBgColor}">
      <mj-column>
${footerMJML}
      </mj-column>
    </mj-section>
  </mj-body>
</mjml>`.trim();
        try {
          const result = mjml2html(mjmlContent, { validationLevel: 'soft', minify: false });
          setInlinePreviewHtml(result && result.html ? result.html : '');
        } catch (e) {
          setInlinePreviewHtml('');
        }
      } catch (_) {
        setInlinePreviewHtml('');
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [templateEditorOpen, templateFormData]);


  const validateCurrentStep = () => {

    if (currentStep === 0) {

      const internalName = (formData && typeof formData.internalName === 'string') ? formData.internalName : '';
      const subject = (formData && typeof formData.subject === 'string') ? formData.subject : '';
      const objective = (formData && typeof formData.objective === 'string') ? formData.objective : '';
      
      // ✅ Bloquer si validation en cours
      if ((selectedMethod === 'ai' || selectedMethod === 'manual' || selectedMethod === 'import') && isValidatingName) {
        showNotification('Vérification du nom en cours... Veuillez patienter.', 'info');
        return false;
      }
      
      // ✅ Vérifier si le nom existe déjà
      if ((selectedMethod === 'ai' || selectedMethod === 'manual' || selectedMethod === 'import') && nameValidationError) {
        showNotification('Le nom interne est déjà utilisé. Veuillez en choisir un autre.', 'error');
        return false;
      }
      
      // ✅ Validation pour IA : internalName, subject ET objective obligatoires
      if (selectedMethod === 'ai' && (!internalName.trim() || !subject.trim() || !objective.trim())) {
        showNotification('Veuillez remplir le nom interne, le sujet et l\'objectif de l\'email.', 'error');

        return false;

      } else if (selectedMethod === 'manual' && (!internalName.trim() || !subject.trim())) {
        showNotification('Veuillez remplir le nom interne et le sujet.', 'error');

        return false;

      } else if (selectedMethod === 'import') {
        
        // Vérifier que le nom interne et le sujet sont remplis
        if (!internalName.trim() || !subject.trim()) {
          showNotification('Veuillez remplir le nom interne et le sujet.', 'error');
          return false;
        }
        
        // Vérifier qu'un fichier HTML est sélectionné
        if (!formData.htmlFile) {
          showNotification('Veuillez sélectionner un fichier HTML.', 'error');
          return false;
        }

      }

    } else if (currentStep === 1 && selectedMethod === 'ai') {

      // Pour l'étape 1 (génération IA), vérifier qu'un contenu a été généré

      if (!generatedContent.trim()) {

        showNotification('Veuillez d\'abord générer un contenu en sélectionnant une suggestion.', 'error');

        return false;

      }

    }

    return true;

  };



  const handleNext = () => {
    // ✅ Bloquer pendant l'enregistrement
    if (saving) return;

    if (!validateCurrentStep()) return;



    if (currentStep === 0) {

      if (selectedMethod === 'ai') {

        setCurrentStep(1);

      } else if (selectedMethod === 'manual') {

        // Si un template est sélectionné, l'appliquer
        if (selectedTemplate) {
          console.log('[TEMPLATES] Application du template sélectionné:', selectedTemplate.name);
          applyTemplate(selectedTemplate);
        } else {
          // Sinon créer le template manuel par défaut
          console.log('[TEMPLATES] Aucun template sélectionné, génération du template par défaut');
          handleManualCreation();
        }

        // Réinitialiser le mode d'édition à Manuel
        setIsNaturalMode(false);

        setCurrentStep(2);

      } else if (selectedMethod === 'import') {

        // Importer le fichier HTML
        // ✅ Le passage à l'étape 2 est géré dans handleImportCreation après lecture complète du fichier

        // Réinitialiser le mode d'édition à Manuel
        setIsNaturalMode(false);

        handleImportCreation();

        // Note : setCurrentStep(2) est maintenant dans handleImportCreation (ligne 1416)
        // après que le fichier soit complètement lu, pour éviter que GrapesJS s'initialise
        // avec un contenu vide puis doive recharger le contenu importé

      }

    } else if (currentStep === 1 && selectedMethod === 'ai') {

      // S'assurer que le contenu généré est chargé dans l'éditeur

      if (generatedContent.trim()) {

        setEditorContent(generatedContent);

        // Note: addToHistory n'est pas appelé ici car l'historique est déjà initialisé dans handleSuggestionSelect

        // Mettre à jour les messages de chat pour l'étape 2

        setChatMessages([

          {

            text: `Email "${selectedSuggestion?.name || selectedSuggestion?.subject}" généré avec succès ! Vous pouvez maintenant le personnaliser ou le sauvegarder.`,

            sender: 'assistant'

          }

        ]);

      }

      // Réinitialiser le mode d'édition à Manuel
      setIsNaturalMode(false);

      setCurrentStep(2);

    }

  };



  const handlePrevious = () => {
    // ✅ Bloquer pendant l'enregistrement
    if (saving) return;

    if (currentStep === 2 && selectedMethod !== 'ai') {

      // Pour manual/import, retour direct à l'étape 0

      setCurrentStep(0);

      // Réinitialiser le mode d'édition à Manuel pour la prochaine fois
      setIsNaturalMode(false);

      // NE PAS réinitialiser les états - l'utilisateur veut juste revenir en arrière
      // pour modifier quelque chose, pas perdre toutes ses données

    } else if (currentStep > 0) {

      // Si on quitte l'étape 2, réinitialiser le mode d'édition à Manuel
      if (currentStep === 2) {
        setIsNaturalMode(false);
      }

      setCurrentStep(currentStep - 1);

      // NE PAS réinitialiser les états lors de la navigation Précédent
      // Les données saisies à l'étape 0 doivent être conservées

    }

  };



  const handleSendMessage = async () => {

    if (!chatInput.trim()) return;

    logDebug('[EMAIL_ASSISTANT] === DÉBUT handleSendMessage ===');
    logDebug('[EMAIL_ASSISTANT] Chat input:', chatInput);
    logDebug('[EMAIL_ASSISTANT] Editor content actuel (premiers 200 chars):', editorContent.substring(0, 200));

    // On ajoute le message utilisateur dans l'historique local
    setChatMessages([...chatMessages, { text: chatInput, sender: 'user' }]);
    const currentInput = chatInput;
    setChatInput('');
    setLoadingAI(true);

    try {
      // Inclure TOUTES les instructions utilisateur + le prompt actuel
      const instructions = [
        ...chatMessages.filter(msg => msg.sender === 'user').map(msg => msg.text),
        currentInput
      ];
  
      logDebug('[EMAIL_ASSISTANT] Instructions complètes:', instructions);
      logDebug('[EMAIL_ASSISTANT] Envoi de la requête IA...');

      const res = await api.post('/emails/edit_with_ai', {
        prompt: currentInput,
        current_html: editorContent,
        instructions,
        // Transmettre la configuration actuelle du contenu dynamique pour que
        // l'IA comprenne et préserve les tokens {dynamiccontent="..."}
        dynamic_content_slots: Array.isArray(emailDynamicContent) ? emailDynamicContent : [],
      }, { signal: requestControllerRef.current?.signal });

      logDebug('[EMAIL_ASSISTANT] Réponse IA reçue:', res.data);
      const data = res.data;

      if (data.success && data.suggestion) {
        logDebug('[EMAIL_ASSISTANT] Succès - HTML suggéré (premiers 200 chars):', data.suggestion.substring(0, 200));
        logDebug('[EMAIL_ASSISTANT] HTML a-t-il changé?', data.suggestion !== editorContent);

        // Vérifier si le HTML a réellement changé
        if (data.suggestion !== editorContent) {
          logDebug('[EMAIL_ASSISTANT] HTML a changé - sauvegarde de l\'ancienne version');
          logDebug('[EMAIL_ASSISTANT] Ancien contenu (premiers 100 chars):', editorContent.substring(0, 100));
          logDebug('[EMAIL_ASSISTANT] Nouveau contenu (premiers 100 chars):', data.suggestion.substring(0, 100));

        setChatMessages(prev => [...prev, { text: 'Voici la version modifiée ! (prévisualisation à jour)', sender: 'assistant' }]);
          // Sauvegarder l'ancienne version AVANT de mettre à jour le contenu
          addToHistory(editorContent);
          logDebug('[EMAIL_ASSISTANT] Mise à jour du contenu éditeur...');
        setEditorContent(data.suggestion);
          hasUserChangesRef.current = true;
          notifyParentIfAllowed(data.suggestion);

        // GrapesJS sera synchronisé via l'effet lié à editorContent

          logDebug('[EMAIL_ASSISTANT] Mise à jour terminée');
      } else {
          setChatMessages(prev => [...prev, { text: 'Limite de quota API atteinte. Votre quota d\'utilisation de l\'API Google Gemini a été dépassé. Veuillez vérifier votre plan et vos détails de facturation, ou réessayer plus tard.', sender: 'assistant' }]);
          logDebug('[EMAIL_ASSISTANT] HTML identique - probablement quota dépassé');
        }
      } else {
        logDebug('[EMAIL_ASSISTANT] Échec - Message:', data.message);
        setChatMessages(prev => [...prev, { text: "Erreur lors de la génération IA : " + (data.message || 'inconnue'), sender: 'assistant' }]);
      }
    } catch (e) {
      console.error('[EMAIL_ASSISTANT] Erreur lors de l\'appel API:', e);
      setChatMessages(prev => [...prev, { text: "Erreur réseau ou serveur IA.", sender: 'assistant' }]);
    }

    setLoadingAI(false);
    logDebug('[EMAIL_ASSISTANT] === FIN handleSendMessage ===');
  };


  const handleOpenDynamicContentFromAi = (event) => {
    // Si aucun slot n'existe encore, on ouvre directement la configuration d'un nouveau slot
    const slots = Array.isArray(emailDynamicContent) ? emailDynamicContent : [];
    if (!slots.length) {
      setCurrentDcSlot({ tokenName: '', defaultContent: '', variations: [] });
      setDynamicContentAlias('');
      setDynamicContentError('');
      setSelectedDynamicContentId(null);
      setCurrentDcTab('default');
      openDynamicContentDialog();
      return;
    }

    // Sinon on affiche un menu pour créer un nouveau slot ou éditer un slot existant
    setDcFromAiAnchorEl(event.currentTarget || null);
  };

  const handleCloseDynamicContentFromAiMenu = () => {
    setDcFromAiAnchorEl(null);
  };

  const handleCreateNewDynamicSlotFromAi = () => {
    setCurrentDcSlot({ tokenName: '', defaultContent: '', variations: [] });
    setDynamicContentAlias('');
    setDynamicContentError('');
    setSelectedDynamicContentId(null);
    setCurrentDcTab('default');
    setDcFromAiAnchorEl(null);
    openDynamicContentDialog();
  };

  const handleEditDynamicSlotFromAi = (slot) => {
    if (!slot) return;
    setCurrentDcSlot({
      tokenName: slot.tokenName || '',
      defaultContent: slot.defaultContent || '',
      variations: Array.isArray(slot.variations) ? slot.variations : [],
    });
    setDynamicContentAlias(slot.tokenName || '');
    setDynamicContentError('');
    setSelectedDynamicContentId(null);
    setCurrentDcTab('default');
    setDcFromAiAnchorEl(null);
    openDynamicContentDialog();
  };

  

  

  

  // Fonction pour ajouter à l'historique

  const addToHistory = (content) => {

    logDebug('[EMAIL_ASSISTANT] === DÉBUT addToHistory ===');
    logDebug('[EMAIL_ASSISTANT] Contenu à ajouter:', content.substring(0, 100));
    logDebug('[EMAIL_ASSISTANT] Historique actuel avant ajout:', emailHistory.length, 'éléments');
    logDebug('[EMAIL_ASSISTANT] Index actuel:', historyIndex);
    logDebug('[EMAIL_ASSISTANT] canUndo actuel:', canUndo);
    logDebug('[EMAIL_ASSISTANT] canRedo actuel:', canRedo);

    // Vérifier si le contenu est identique à l'élément actuel (à historyIndex)
    const currentEntry = emailHistory[historyIndex];
    logDebug('[EMAIL_ASSISTANT] Élément actuel (index ' + historyIndex + '):', currentEntry ? currentEntry.substring(0, 100) : 'undefined');

    if (currentEntry === content) {
      logDebug('[EMAIL_ASSISTANT] Contenu identique à l\'élément actuel, pas d\'ajout à l\'historique');
      logDebug('[EMAIL_ASSISTANT] === FIN addToHistory ===');
      return;
    }

    // Si on n'est pas à la fin de l'historique (après des Undo), couper l'historique
    // et ajouter le nouveau contenu (cela efface les versions "futures")
    const newHistory = [...emailHistory.slice(0, historyIndex + 1), content];
    logDebug('[EMAIL_ASSISTANT] Nouvel historique créé, longueur:', newHistory.length);
    if (historyIndex < emailHistory.length - 1) {
      logDebug('[EMAIL_ASSISTANT] ⚠️ Historique coupé - ' + (emailHistory.length - historyIndex - 1) + ' élément(s) supprimé(s)');
    }

    // Calculer le nouvel index
    const newIndex = newHistory.length - 1;
    logDebug('[EMAIL_ASSISTANT] Nouvel index:', newIndex);
    logDebug('[EMAIL_ASSISTANT] Nouveau canUndo:', newIndex > 0);
    logDebug('[EMAIL_ASSISTANT] Nouveau canRedo: false');

    // Mettre à jour tous les états
    setEmailHistory(newHistory);
    setHistoryIndex(newIndex);
    setCanUndo(newIndex > 0);
    setCanRedo(false);

    logDebug('[EMAIL_ASSISTANT] === FIN addToHistory ===');
  };



  // Fonction undo

  const handleUndo = () => {

    console.log('[EMAIL_ASSISTANT] === DÉBUT handleUndo ===');

    console.log('[EMAIL_ASSISTANT] Mode naturel:', isNaturalMode);

    console.log('[EMAIL_ASSISTANT] canUndo:', canUndo);

    console.log('[EMAIL_ASSISTANT] historyIndex:', historyIndex);

    console.log('[EMAIL_ASSISTANT] emailHistory.length:', emailHistory.length);

    

    // Si GrapesJS est actif en mode manuel, utiliser son undo

    if (!isNaturalMode && editorInstanceRef.current) {

      console.log('[EMAIL_ASSISTANT] Mode manuel - utilisation de GrapesJS undo');

      try { editorInstanceRef.current.runCommand('core:undo'); } catch (_) {}

      return;

    }

    // Mode IA : utiliser notre historique
    if (canUndo && historyIndex > 0) {
      const newIndex = historyIndex - 1;
      console.log('[EMAIL_ASSISTANT] Undo vers index:', newIndex);
      const previousContent = emailHistory[newIndex];
      console.log('[EMAIL_ASSISTANT] Contenu précédent trouvé:', previousContent ? previousContent.substring(0, 100) : 'undefined');
      
      if (previousContent) {
        // Mettre à jour le contenu
        setEditorContent(previousContent);
        // Mettre à jour l'index et les états
        setHistoryIndex(newIndex);
        setCanUndo(newIndex > 0);
        setCanRedo(true);
        // Notifier le parent si autorisé
        notifyParentIfAllowed(previousContent);
        console.log('[EMAIL_ASSISTANT] Undo terminé - nouvel index:', newIndex);
        console.log('[EMAIL_ASSISTANT] Nouveau canUndo:', newIndex > 0);
        console.log('[EMAIL_ASSISTANT] Nouveau canRedo: true');
      } else {
        console.warn('[EMAIL_ASSISTANT] Contenu précédent introuvable à l\'index:', newIndex);
      }
    } else {
      console.log('[EMAIL_ASSISTANT] Undo impossible - canUndo:', canUndo, 'historyIndex:', historyIndex);
    }

    

    console.log('[EMAIL_ASSISTANT] === FIN handleUndo ===');

  };



  // Fonction redo

  const handleRedo = () => {

    console.log('[EMAIL_ASSISTANT] === DÉBUT handleRedo ===');

    console.log('[EMAIL_ASSISTANT] Mode naturel:', isNaturalMode);

    console.log('[EMAIL_ASSISTANT] canRedo:', canRedo);

    console.log('[EMAIL_ASSISTANT] historyIndex:', historyIndex);

    console.log('[EMAIL_ASSISTANT] emailHistory.length:', emailHistory.length);

    

    // Si GrapesJS est actif en mode manuel, utiliser son redo

    if (!isNaturalMode && editorInstanceRef.current) {

      console.log('[EMAIL_ASSISTANT] Mode manuel - utilisation de GrapesJS redo');

      try { editorInstanceRef.current.runCommand('core:redo'); } catch (_) {}

      return;

    }

    

    if (canRedo && historyIndex < emailHistory.length - 1) {
      const newIndex = historyIndex + 1;
      console.log('[EMAIL_ASSISTANT] Redo vers index:', newIndex);
      const nextContent = emailHistory[newIndex];
      console.log('[EMAIL_ASSISTANT] Contenu suivant trouvé:', nextContent ? nextContent.substring(0, 100) : 'undefined');
      
      if (nextContent) {
        // Mettre à jour le contenu
        setEditorContent(nextContent);
        // Mettre à jour l'index et les états
        setHistoryIndex(newIndex);
        setCanRedo(newIndex < emailHistory.length - 1);
        setCanUndo(true);
        // Notifier le parent si autorisé
        notifyParentIfAllowed(nextContent);
        console.log('[EMAIL_ASSISTANT] Redo terminé - nouvel index:', newIndex);
        console.log('[EMAIL_ASSISTANT] Nouveau canUndo: true');
        console.log('[EMAIL_ASSISTANT] Nouveau canRedo:', newIndex < emailHistory.length - 1);
      } else {
        console.warn('[EMAIL_ASSISTANT] Contenu suivant introuvable à l\'index:', newIndex);
      }
    } else {
      console.log('[EMAIL_ASSISTANT] Redo impossible - canRedo:', canRedo, 'historyIndex:', historyIndex, 'maxIndex:', emailHistory.length - 1);
    }

    

    console.log('[EMAIL_ASSISTANT] === FIN handleRedo ===');

  };



  // Fonction pour réinitialiser les états de création

  const resetCreationState = () => {

    console.log('[EMAIL_ASSISTANT] Réinitialisation des états de création');

    setEmailSuggestions([]);

    setSelectedSuggestion(null);

    setGeneratedContent('');

    setIsGenerating(false);

    setFormData({

      internalName: '',

      subject: '',

      preheaderText: '',

      objective: '',

      htmlFile: null

    });

    setEmailDynamicContent([]);
    setCurrentDcSlot(null);
    setCurrentDcTab('default');

    // Réinitialiser aussi l'éditeur et l'historique

    const defaultContent = '<p>Commencez à rédiger votre email ici...</p>';

    setEditorContent(defaultContent);

    setEmailHistory([defaultContent]);

    setHistoryIndex(0);

    setCanUndo(false);

    setCanRedo(false);

    setChatMessages([]);

  };



  // Fonction pour gérer la sélection d'une suggestion IA

  const handleSuggestionSelect = async (suggestion) => {

    console.log('[EMAIL_ASSISTANT] === DÉBUT handleSuggestionSelect ===');

    console.log('[EMAIL_ASSISTANT] Suggestion sélectionnée:', suggestion);

    

    setSelectedSuggestion(suggestion);

    setIsGenerating(true);

    

    try {

      // ✅ Récupérer directement le MJML depuis la suggestion (ne PAS créer d'email ici)
      const suggestionMjml = suggestion.config?.customMjml || suggestion.customMjml || '';

      console.log('[EMAIL_ASSISTANT] MJML dans la suggestion (length):', suggestionMjml.length);

      

      if (suggestionMjml) {

        console.log('[EMAIL_ASSISTANT] Chargement du contenu MJML dans l\'éditeur');

        setGeneratedContent(suggestionMjml);

        setEditorContent(suggestionMjml);

        // Réinitialiser l'historique avec seulement le contenu généré

        setEmailHistory([suggestionMjml]);

        setHistoryIndex(0);

        setCanUndo(false);

        setCanRedo(false);

        // Force le mode IA pour déclencher l'éditeur MJML
        console.log('[EMAIL_ASSISTANT] Force selectedMethod à "ai" pour éditeur MJML');
        setSelectedMethod('ai');

        // ✅ Message de succès
        setChatMessages(prev => [...prev, 

          { text: `Template "${suggestion.name || suggestion.subject}" chargé avec succès ! Vous pouvez maintenant le modifier et l'enregistrer.`, sender: 'assistant' }

        ]);

      } else {

        console.error('[EMAIL_ASSISTANT] Aucun contenu MJML trouvé dans la suggestion');

        setChatMessages(prev => [...prev, 

          { text: "Erreur : Aucun contenu MJML trouvé dans cette suggestion.", sender: 'assistant' }

        ]);

      }

    } catch (e) {

      console.error('[EMAIL_ASSISTANT] Erreur chargement suggestion:', e);

      setChatMessages(prev => [...prev, 

        { text: "Erreur lors du chargement du template.", sender: 'assistant' }

      ]);

    }

    

    setIsGenerating(false);

    console.log('[EMAIL_ASSISTANT] === FIN handleSuggestionSelect ===');

  };



  // Fonction pour générer le template MJML de base (header + body + footer)
  const generateBaseTemplate = (bodyContent = '') => {
    return `<mjml>
  <mj-head>
    <!-- CSS-STYLE -->
    <mj-style inline="inline">
      p, li { margin:0 !important; padding:0; line-height:1.4em; }
    </mj-style>
  </mj-head>
  
  <!-- BODY -->
  <mj-body background-color="#d0d0d0">
    
    <!-- HEADER -->
    <mj-section background-color="#ffffff">
      <mj-column>
        <mj-text font-size="11px" align="center">
          <p><span data-fr-verified="true">{webview_text}</span></p>
        </mj-text>
        <mj-spacer></mj-spacer>
        <mj-image src="/app/assets/images/placeholder-logo.png" width="70px" padding-bottom="0px"></mj-image>
      </mj-column>
    </mj-section>
    
    <!-- BODY CONTENT -->
    ${bodyContent}
    
    <!-- FOOTER -->
    <mj-section padding-top="0" background-color="#000000">
      <mj-column>
        <mj-spacer></mj-spacer>
        <mj-social font-size="12px" icon-size="30px" border-radius="12px" mode="horizontal">
          <mj-social-element name="facebook" border-radius="14px" padding-right="10px" color="#000000" background-color="#000000"></mj-social-element>
          <mj-social-element name="instagram" border-radius="14px" padding-right="10px" padding-left="10px" color="#000000" background-color="#000000"></mj-social-element>
          <mj-social-element name="youtube" border-radius="14px" padding-left="10px" color="#000000" background-color="#000000"></mj-social-element>
        </mj-social>
        <mj-spacer></mj-spacer>
        <mj-text font-family="Ubuntu, Helvetica, Arial, sans-serif" line-height="1.5" align="center" padding-top="0px" padding-bottom="0px" font-size="12px" color="#ffffff">
          <p>${currentBrand?.name || '{brand=name}'}<br/>11111 Beautiful City, 1212 Nice Street<br/>Brazil</p>
        </mj-text>
        <mj-spacer></mj-spacer>
        <mj-text font-size="11px" align="center" color="#b3b3b3">
          <p>Fancy seeing you down here. You're getting this email because you gave us your email address.</p>
          <p>Want to change how you receive these emails?</p>
        </mj-text>
        <mj-text font-size="11px" align="center" color="#b3b3b3">
          <p><span data-fr-verified="true">{unsubscribe_text}</span></p>
        </mj-text>
        <mj-spacer></mj-spacer>
      </mj-column>
    </mj-section>
    
  </mj-body>
</mjml>`;
  };

  // Fonction pour gérer la création manuelle

  const handleManualCreation = () => {

    console.log('[EMAIL_ASSISTANT] === DÉBUT handleManualCreation ===');

    console.log('[EMAIL_ASSISTANT] Données manuelles:', formData);

    

    // Créer le body content vide avec juste un placeholder
    const bodyContent = `    <mj-section background-color="#ffffff">
      <mj-column width="550px">
        <mj-text font-size="30px" align="center" font-weight="700">
          <p>Your main headline goes here</p>
        </mj-text>
        <mj-text font-size="16px" align="center">
          <p>Customize these sections with your brand voice, specific products or services, and relevant images. The key is to make each section informative, engaging, and aligned with your email marketing goals.</p>
        </mj-text>
        <mj-spacer></mj-spacer>
      </mj-column>
    </mj-section>`;

    // Utiliser le template de base (header + body + footer)
    const template = generateBaseTemplate(bodyContent);

    

    setEditorContent(template);

    addToHistory(template);

    // Force le mode manuel pour déclencher l'éditeur MJML
    console.log('[EMAIL_ASSISTANT] Force selectedMethod à "manual" pour éditeur MJML');
    setSelectedMethod('manual');

    

    // Mettre à jour les messages de chat
    // ✅ Ne plus afficher le sujet dans le message (il est dans les métadonnées, pas le contenu)

    setChatMessages(prev => [...prev, 

      { text: `Template manuel créé. Vous pouvez maintenant l'éditer !`, sender: 'assistant' }

    ]);

    

    console.log('[EMAIL_ASSISTANT] === FIN handleManualCreation ===');

  };



  // Convertir HTML -> texte brut simple pour plainText

  const htmlToPlainText = (html) => {

    try {

      const tmp = document.createElement('div');

      tmp.innerHTML = html || '';

      return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();

    } catch (_) {

      return '';

    }

  };



  // Fonction pour gérer l'import HTML

  const handleImportCreation = () => {

    console.log('[EMAIL_ASSISTANT] === DÉBUT handleImportCreation ===');

    console.log('[EMAIL_ASSISTANT] Fichier HTML:', formData.htmlFile);

    

    if (formData.htmlFile) {

      const reader = new FileReader();

      reader.onload = (e) => {

        const htmlContent = e.target.result;

        console.log('[EMAIL_ASSISTANT] Contenu HTML importé:', htmlContent.substring(0, 200));

        

        setEditorContent(htmlContent);

        addToHistory(htmlContent);

        // ⚠️ NE PAS synchroniser lastSavedHtmlRef ici car GrapesJS va transformer le HTML
        // La synchronisation se fera automatiquement après l'initialisation de GrapesJS
        // (lignes 2733 et 2827) pour éviter les faux positifs lors du switch de mode

        console.log('[EMAIL_ASSISTANT] Contenu chargé - lastSavedHtmlRef sera synchronisé après initialisation GrapesJS');

        // Essayer de pré-remplir sujet et preheader à partir du HTML

        const { subject, preheader } = extractEmailMetaFromHtml(htmlContent);

        setFormData(prev => ({

          ...prev,

          internalName: prev.internalName || (formData.htmlFile?.name?.replace(/\.(html|htm)$/i, '') || ''),

          subject: prev.subject || subject,

          preheaderText: prev.preheaderText || preheader,

        }));

        

        // Mettre à jour les messages de chat

        setChatMessages(prev => [...prev, 

          { text: `Fichier HTML "${formData.htmlFile.name}" importé avec succès ! Vous pouvez maintenant l'éditer.`, sender: 'assistant' }

        ]);

        // ✅ IMPORTANT : Passer à l'étape 2 APRÈS que le contenu soit chargé
        // Cela permet à GrapesJS de s'initialiser avec le bon contenu dès le départ
        setCurrentStep(2);

        if (mode === 'edit') {
          hasUserChangesRef.current = true;
        }
        console.log('[EMAIL_ASSISTANT] Passage à l\'étape 2 après chargement du contenu');

      };

      reader.readAsText(formData.htmlFile);

    }

    

    console.log('[EMAIL_ASSISTANT] === FIN handleImportCreation (lecture fichier en cours...) ===');

  };



  // Helper: récupérer le contenu depuis GrapesJS
  // En mode MJML : retourne le code MJML source
  // En mode HTML : retourne le HTML + <style>CSS

  const getHtmlFromGrapes = () => {

    try {

      if (!editorInstanceRef.current) return editorContent;

      const html = editorInstanceRef.current.getHtml();

      if (editorType === 'mjml') {
        console.log('[GET_FROM_GRAPES] Mode MJML - Retour du code MJML source');
        try { return editorInstanceRef.current.getMjml ? editorInstanceRef.current.getMjml() : html; } catch (_) { return html; }
      }

      // En mode HTML, ajouter les styles CSS
      const css = editorInstanceRef.current.getCss();
      const combined = css ? `<style>${css}</style>${html}` : html;
      console.log('[GET_FROM_GRAPES] Mode HTML - Retour du HTML + CSS');
      return postProcessEmailHtml(combined);
    } catch (_) {

      return editorContent;

    }

  };


  // Post-traitement pour fiabiliser les boutons/liens pour email HTML
  const postProcessEmailHtml = (html) => {
    try {
      const parser = new window.DOMParser();
      const doc = parser.parseFromString(html || '', 'text/html');

      const ensureInlineStyle = (el, rules) => {
        const current = (el.getAttribute('style') || '').trim();
        const map = {};
        current.split(';').forEach(rule => {
          const [k, v] = rule.split(':');
          if (k && v) map[k.trim().toLowerCase()] = v.trim();
        });
        Object.entries(rules).forEach(([k, v]) => {
          if (!map[k.toLowerCase()]) map[k] = v;
        });
        const styleStr = Object.entries(map).map(([k, v]) => `${k}: ${v}`).join('; ');
        if (styleStr) el.setAttribute('style', styleStr);
      };

      // Normaliser les boutons (a.btn, [role="button"], .gjs-button)
      const buttons = doc.querySelectorAll('a.btn, a[role="button"], .gjs-button, button, .button');
      buttons.forEach(a => {
        ensureInlineStyle(a, {
          display: 'inline-block',
          'text-decoration': 'none',
          'background-color': '#007bff',
          color: '#ffffff',
          padding: '12px 20px',
          'border-radius': '4px',
          border: '1px solid #007bff',
          'line-height': '1.2',
          'text-align': 'center'
        });
        const href = a.getAttribute('href');
        if (!href || href === '#' || href.startsWith('[')) {
          a.setAttribute('href', '#');
        }
        a.setAttribute('target', '_blank');
        a.setAttribute('rel', 'noopener noreferrer');
      });

      // Link block (a.gjs-link-block) -> display block et padding par défaut
      const linkBlocks = doc.querySelectorAll('a.gjs-link-block, [data-gjs-type="link"]');
      linkBlocks.forEach(a => {
        ensureInlineStyle(a, {
          display: 'block',
          padding: '8px 0',
          'text-decoration': 'none',
          color: '#2563eb'
        });
        const href = a.getAttribute('href');
        if (!href || href === '#' || href.startsWith('[')) {
          a.setAttribute('href', '#');
        }
        a.setAttribute('target', '_blank');
        a.setAttribute('rel', 'noopener noreferrer');
      });

      // Retourner HTML avec styles head conservés
      const headStyles = Array.from(doc.head.querySelectorAll('style')).map(s => s.outerHTML).join('');
      return headStyles + (doc.body ? doc.body.innerHTML : html);
    } catch (_) {
      return html;
    }
  };

  // Normalisation pour comparer sans faux positifs (ignore styles/attrs GrapesJS et espaces)
  const normalizeHtmlForCompare = (html) => {
    try {
      const parser = new window.DOMParser();
      const doc = parser.parseFromString(String(html || ''), 'text/html');

      // Supprimer les data-gjs-* et classes gjs-*
      doc.querySelectorAll('[data-gjs-type], [data-gjs-highlightable], [data-gjs]').forEach(el => {
        Array.from(el.attributes).forEach(attr => {
          if (/^data-gjs/i.test(attr.name)) el.removeAttribute(attr.name);
        });
        const cls = (el.getAttribute('class') || '').split(/\s+/).filter(c => c && !/^gjs-/i.test(c) && c !== 'gjs-selected').join(' ');
        if (cls) el.setAttribute('class', cls); else el.removeAttribute('class');
      });

      // Normaliser target/rel sur liens pour éviter différences mineures
      doc.querySelectorAll('a[href]').forEach(a => {
        const href = a.getAttribute('href') || '#';
        a.setAttribute('href', href || '#');
        a.setAttribute('target', '_blank');
        a.setAttribute('rel', 'noopener noreferrer');
      });

      // Supprimer les balises style du head pour comparaison logique
      doc.querySelectorAll('head style').forEach(s => s.parentNode && s.parentNode.removeChild(s));

      const bodyHtml = doc.body ? doc.body.innerHTML : String(html || '');
      return bodyHtml.replace(/\s+/g, ' ').trim();
    } catch (_) {
      return String(html || '').replace(/\s+/g, ' ').trim();
    }
  };

  // Fonction pour compiler le MJML en HTML
  const compileMjmlToHtml = (mjmlContent) => {
    try {
      console.log('[MJML_COMPILE] Début compilation MJML...');
      
      // Compiler le MJML en HTML
      const result = mjml2html(mjmlContent, {
        validationLevel: 'soft', // Ne pas échouer sur les erreurs mineures
        minify: false,
      });

      if (result.errors && result.errors.length > 0) {
        console.warn('[MJML_COMPILE] Avertissements MJML:', result.errors);
      }

      console.log('[MJML_COMPILE] Compilation réussie');
      return result.html;
    } catch (error) {
      console.error('[MJML_COMPILE] Erreur compilation MJML:', error);
      throw new Error(`Erreur de compilation MJML: ${error.message}`);
    }
  };

  // Fonction pour obtenir le HTML de prévisualisation (compile MJML si nécessaire)
  const getPreviewHtml = () => {
    try {
      // En mode Manuel : utiliser GrapesJS
      // En mode IA : utiliser editorContent (avec compilation MJML si nécessaire)
      if (!isNaturalMode && editorInstanceRef.current) {
        // Détecter si on est en mode MJML
        if (editorType === 'mjml') {
          try {
            const mjmlCode = editorInstanceRef.current.getMjml ? editorInstanceRef.current.getMjml() : editorInstanceRef.current.getHtml();
            console.log('[PREVIEW] Compilation MJML depuis GrapesJS (mode Manuel MJML)');
            
            // Compiler avec mjml2html
            const mjmlResult = mjml2html(mjmlCode, {
              validationLevel: 'soft',
              minify: false,
            });
            
            if (mjmlResult && mjmlResult.html) {
              return mjmlResult.html;
            } else {
              console.warn('[PREVIEW] Compilation MJML échouée, fallback');
              return editorContent || '<p>Erreur compilation MJML</p>';
            }
          } catch (e) {
            console.error('[PREVIEW] Erreur compilation MJML:', e);
            return editorContent || '<p>Erreur compilation MJML</p>';
          }
        }
        
        // Mode HTML : retourner le HTML complet avec styles
        console.log('[PREVIEW] HTML depuis GrapesJS (mode Manuel HTML)');
        return getHtmlFromGrapes();
      }
      
      // Mode IA ou GrapesJS non disponible
      // Si on est en mode MJML, compiler le MJML en HTML
      if (editorType === 'mjml') {
        const mjmlContent = editorContent || generatedContent || '';
        
        // Vérifier si le contenu est bien du MJML
        if (mjmlContent.trim().startsWith('<mjml')) {
          try {
            console.log('[PREVIEW] Compilation MJML depuis editorContent (mode IA MJML)');
            const mjmlResult = mjml2html(mjmlContent, {
              validationLevel: 'soft',
              minify: false,
            });
            
            if (mjmlResult && mjmlResult.html) {
              return mjmlResult.html;
            } else {
              console.warn('[PREVIEW] Compilation MJML échouée en mode IA, affichage du code brut');
              return mjmlContent;
            }
          } catch (e) {
            console.error('[PREVIEW] Erreur compilation MJML en mode IA:', e);
            return mjmlContent;
          }
        }
        
        // Si ce n'est pas du MJML, retourner tel quel
        return mjmlContent || '<p>Chargement...</p>';
      }
      
      // Mode HTML : utiliser editorContent directement
      console.log('[PREVIEW] HTML depuis editorContent (mode IA HTML)');
      return editorContent || generatedContent || '<p>Chargement...</p>';
    } catch (error) {
      console.error('[PREVIEW] Erreur lors de la génération de la prévisualisation:', error);
      return editorContent || '<p>Erreur de prévisualisation</p>';
    }
  };

  // Fonction de sauvegarde améliorée

  const handleSave = async () => {

    // Bloquer la sauvegarde en mode Manuel tant que GrapesJS n'est pas complètement prêt
    if (!isNaturalMode && (!editorInstanceRef.current || !editorInitializedRef.current)) {
      showNotification("L'éditeur est en cours d'initialisation, veuillez patienter avant de sauvegarder.", 'warning');
      return false;
    }

    if (mode === 'edit') {

      // Mode édition - sauvegarder l'email existant

    if (!email || !email.id) return;

    setSaving(true);

      

      console.log('[EMAIL_ASSISTANT] === DÉBUT handleSave (ÉDITION) ===');

      console.log('[EMAIL_ASSISTANT] Email ID:', email.id);

      console.log('[EMAIL_ASSISTANT] Contenu à sauvegarder (premiers 200 chars):', editorContent.substring(0, 200));

    

    try {

      // Source du contenu: en mode manuel, récupérer l'état GrapesJS; sinon, utiliser editorContent

      let contentToSave = (!isNaturalMode && editorInstanceRef.current) ? getHtmlFromGrapes() : editorContent;

      // Déterminer la source MJML à persister (uniquement si éditeur MJML)
      let mjmlToPersist = null;
      try {
        if (editorType === 'mjml') {
          if (!isNaturalMode && editorInstanceRef.current) {
            mjmlToPersist = editorInstanceRef.current.getMjml ? editorInstanceRef.current.getMjml() : editorInstanceRef.current.getHtml();
          } else {
            const trimmed = String(editorContent || '').trim();
            mjmlToPersist = trimmed.startsWith('<mjml') ? trimmed : null;
          }
        }
      } catch (_) {}

      // ✅ Compiler MJML en HTML si nécessaire (aligné sur la logique de création)
      try {
        const trimmed = (contentToSave || '').trim();
        const isMJMLContent = trimmed.startsWith('<mjml');
        const shouldCompileMJML = editorType === 'mjml' && isMJMLContent;
        if (shouldCompileMJML) {
          console.log('[MJML][UPDATE] Tentative de conversion MJML → HTML...');
          let mjmlCode = trimmed;
          try {
            mjmlCode = (mjmlCode || '')
              .replace(/\s+text-mode="[^"]*"/gi, '')
              .replace(/\s+display="[^"]*"/gi, '')
              .replace(/<mj-social-element([^>]*?)name="x"/gi, '<mj-social-element$1name="twitter"');
          } catch (_) {}
          const mjmlResult = mjml2html(mjmlCode, { validationLevel: 'soft', minify: false });
          if (mjmlResult && mjmlResult.html) {
            contentToSave = mjmlResult.html;
            if (mjmlResult.errors && mjmlResult.errors.length) {
              console.warn('[MJML][UPDATE] Conversion avec avertissements:', mjmlResult.errors);
              showNotification('Email MJML compilé en HTML (avec avertissements)', 'warning');
            } else {
              console.log('[MJML][UPDATE] Conversion réussie');
            }
          } else {
            console.warn('[MJML][UPDATE] Aucun HTML retourné, conservation du contenu original');
          }
        }
      } catch (e) {
        console.warn('[MJML][UPDATE] Erreur conversion, fallback au contenu original:', e);
      }

      // ✅ Remplacer les variables de brand personnalisées par les vraies valeurs
      // Car Mautic ne connaît pas nos brands custom, seulement celles configurées dans Mautic
      if (currentBrand && currentBrand.name) {
        contentToSave = contentToSave.replace(/{brand=name}/g, currentBrand.name);
        console.log('[BRAND] Variable {brand=name} remplacée par:', currentBrand.name);
      }

      const updateConfig = {

        ...email,

        customHtml: contentToSave,
        mjml: mjmlToPersist,
        builderJson: builderJson || null,
        // Indicateur d'édition HTML pour invalider MJML/Builder côté backend
        htmlEdited: editorType === 'html',

      };

      if (emailDynamicContent && emailDynamicContent.length > 0) {
        const mappedDynamic = mapSlotsToMauticDynamicContent(emailDynamicContent);
        if (mappedDynamic && mappedDynamic.length > 0) {
          updateConfig.dynamicContent = mappedDynamic;
        }
      }

      const res = await api.post('/emails/update', {
        // Pydantic attend un string pour email_id
        email_id: String(email.id),
        email_config: updateConfig,
      }, { signal: requestControllerRef.current?.signal });

      

        console.log('[EMAIL_ASSISTANT] Réponse sauvegarde:', res.data);

      const data = res.data;

        

      if (data.success !== false && !data.error) {

          showNotification('Sauvegarde réussie avec succès !', 'success');

          console.log('[EMAIL_ASSISTANT] Sauvegarde réussie');

          // Activer ponctuellement la propagation live pour refléter le contenu sauvegardé

          allowLivePreviewRef.current = true;

          notifyParentIfAllowed(contentToSave);

          allowLivePreviewRef.current = false;

          // Mettre à jour le contenu local UNIQUEMENT en mode IA
          // En mode manuel, GrapesJS a déjà le bon contenu, pas besoin de setEditorContent
          // qui déclencherait un useEffect et viderait l'éditeur
          if (isNaturalMode) {
            try { setEditorContent(contentToSave); } catch (_) {}
          }

          // Mettre à jour la référence du dernier contenu sauvegardé
          lastSavedHtmlRef.current = contentToSave;

          // Marquer comme sauvegardé
          hasBeenSavedRef.current = true;

          hasUserChangesRef.current = false;

          setSaving(false);

          return true;

      } else {

          showNotification('Erreur lors de la sauvegarde : ' + (data.message || 'inconnue'), 'error');

          console.log('[EMAIL_ASSISTANT] Erreur sauvegarde:', data.message);

          setSaving(false);

          return false;

      }

    } catch (e) {

        console.error('[EMAIL_ASSISTANT] Erreur réseau sauvegarde:', e);

        showNotification('Erreur réseau lors de la sauvegarde.', 'error');

        setSaving(false);

        return false;

    }

    

      console.log('[EMAIL_ASSISTANT] === FIN handleSave (ÉDITION) ===');

    } else {

      // Mode création - créer un nouvel email

      setSaving(true);

      

      console.log('[EMAIL_ASSISTANT] === DÉBUT handleSave (CRÉATION) ===');

      console.log('[EMAIL_ASSISTANT] Méthode sélectionnée:', selectedMethod);
      console.log('[EMAIL_ASSISTANT] Type d\'éditeur:', editorType);

      let contentToSave = (!isNaturalMode && editorInstanceRef.current) ? getHtmlFromGrapes() : editorContent;

      // ✅ FIX: Compiler le MJML en HTML indépendamment du mode (Manuel ou IA)
      // Détecter si le contenu est du MJML en vérifiant le type d'éditeur ET le contenu
      const isMJMLContent = contentToSave.trim().startsWith('<mjml');
      const shouldCompileMJML = editorType === 'mjml' && isMJMLContent;
      
      if (shouldCompileMJML) {
        try {
          console.log('[MJML] Tentative de conversion MJML → HTML...');
          console.log('[MJML] Mode actuel:', isNaturalMode ? 'IA' : 'Manuel');
          
          // Récupérer le code MJML
          let mjmlCode = contentToSave;
          
          // En mode Manuel, récupérer depuis GrapesJS
          if (!isNaturalMode && editorInstanceRef.current) {
            mjmlCode = editorInstanceRef.current.getMjml ? editorInstanceRef.current.getMjml() : editorInstanceRef.current.getHtml();
          }
          // Sanitize ancien MJML (supprimer attributs illégaux, normaliser certains éléments)
          try {
            mjmlCode = (mjmlCode || '')
              .replace(/\s+text-mode="[^"]*"/gi, '')
              .replace(/\s+display="[^"]*"/gi, '')
              .replace(/<mj-list/gi, '<mj-text')
              .replace(/<\/mj-list>/gi, '</mj-text>');
          } catch (_) {}
          
          console.log('[MJML] Code source (premiers 200 chars):', mjmlCode.substring(0, 200));
          
          // Compiler en HTML avec mjml2html
          const mjmlResult = mjml2html(mjmlCode, {
            validationLevel: 'soft',
            minify: false,
          });

          // Utiliser le HTML retourné même s'il y a des avertissements
          if (mjmlResult && mjmlResult.html) {
            contentToSave = mjmlResult.html;
            if (mjmlResult.errors && mjmlResult.errors.length) {
              console.warn('[MJML] ✅ Conversion avec avertissements:', mjmlResult.errors);
              showNotification('Email MJML compilé en HTML (avec avertissements)', 'warning');
            } else {
              console.log('[MJML] ✅ Conversion réussie (premiers 200 chars):', contentToSave.substring(0, 200));
              showNotification('Email MJML compilé en HTML avec succès', 'success');
            }
          } else {
            console.warn('[MJML] ❌ Aucun HTML retourné, on conserve le contenu original');
          }
        } catch (e) {
          console.warn('[MJML] ⚠️ Erreur conversion, fallback au contenu original:', e);
          // contentToSave reste inchangé (contenu original)
          // Ne pas bloquer la sauvegarde
        }
      }

      // ✅ Remplacer les variables de brand personnalisées par les vraies valeurs
      // Car Mautic ne connaît pas nos brands custom, seulement celles configurées dans Mautic
      if (currentBrand && currentBrand.name) {
        contentToSave = contentToSave.replace(/{brand=name}/g, currentBrand.name);
        console.log('[BRAND] Variable {brand=name} remplacée par:', currentBrand.name);
      }

      // Sécurité: tenter une compilation de secours et empêcher l'envoi de MJML brut
      try {
        const trimmed = (contentToSave || '').trim();
        if (trimmed.startsWith('<mjml')) {
          const fallback = mjml2html(trimmed, { validationLevel: 'soft', minify: false });
          if (fallback && fallback.html) {
            contentToSave = fallback.html;
            console.warn('[MJML] Fallback compilation appliquée avant envoi');
          }
        }
      } catch (e) {
        console.warn('[MJML] Erreur lors du fallback de compilation:', e);
      }
      if ((contentToSave || '').trim().startsWith('<mjml')) {
        showNotification('Erreur: la compilation MJML a échoué, envoi annulé.', 'error');
        setSaving(false);
        return false;
      }

      console.log('[EMAIL_ASSISTANT] Contenu final à sauvegarder (premiers 200 chars):', contentToSave.substring(0, 200));

      

      try {

        // ⚠️ Vérification mauticAvailable supprimée car redondante
        // Le backend (/emails/create) gère déjà les credentials
        // Cette vérification causait des fausses alertes dues au timing asynchrone



        // ✅ Utiliser les champs unifiés
        const generatedName = (formData.internalName || 'Nouvel email').slice(0, 190);

        const subject = selectedSuggestion?.subject || formData.subject || 'Sujet de l\'email';

        const preheader = selectedSuggestion?.preheader || formData.preheaderText || '';

        const plainText = htmlToPlainText(contentToSave);



        // Déterminer la source MJML à persister (uniquement si éditeur MJML)
        let mjmlToPersist = null;
        try {
          if (editorType === 'mjml') {
            if (!isNaturalMode && editorInstanceRef.current) {
              mjmlToPersist = editorInstanceRef.current.getMjml ? editorInstanceRef.current.getMjml() : editorInstanceRef.current.getHtml();
            } else {
              const trimmed = String(editorContent || '').trim();
              mjmlToPersist = trimmed.startsWith('<mjml') ? trimmed : null;
            }
          }
        } catch (_) {}

        // Config attendue par le backend / Mautic

        const emailConfig = {

          name: generatedName,

          subject: subject,

          preheader: preheader,

          fromAddress: '',

          fromName: '',

          replyToAddress: '',

          useOwnerAsMailer: false,

          isPublished: true,

          emailType: 'template',

          customHtml: contentToSave,

          plainText: plainText,

          template: 'blank',

          // Sources d'édition pour réouverture fidèle
          mjml: mjmlToPersist,
          builderJson: builderJson || null,

          // Credentials requis par l'endpoint backend (pop avant envoi à Mautic)

          mautic_url: userCredentials.mautic_url,

          mautic_username: userCredentials.mautic_username,

          mautic_password: userCredentials.mautic_password

        };

        if (emailDynamicContent && emailDynamicContent.length > 0) {
          const mappedDynamic = mapSlotsToMauticDynamicContent(emailDynamicContent);
          if (mappedDynamic && mappedDynamic.length > 0) {
            emailConfig.dynamicContent = mappedDynamic;
          }
        }

        

        console.log('[EMAIL_ASSISTANT] Données email à créer:', emailConfig);

        

        const res = await api.post('/emails/create', emailConfig, { signal: requestControllerRef.current?.signal });

        

        console.log('[EMAIL_ASSISTANT] Réponse création:', res.data);

        const data = res.data;

        

        const messageText = (data && (data.message || data.detail || '')) || '';

        const looksSuccessful = (data && data.success === true)

          || /success/i.test(messageText)

          || Boolean(data && (data.email || data.id));



        if (looksSuccessful) {

          showNotification('Email créé avec succès !', 'success');

          console.log('[EMAIL_ASSISTANT] Création réussie');

          // Mettre à jour le contenu local avec la version créée/sauvegardée

          try { setEditorContent(contentToSave); } catch (_) {}

          try { onClose && onClose(); } catch (_) {}
          try { onCreated && onCreated(); } catch (_) {}

          // Rafraîchir toute la page pour refléter immédiatement la nouvelle entrée dans la liste

          setSaving(false);

          return true;

        } else {

          showNotification('Erreur lors de la création : ' + (data.message || 'inconnue'), 'error');

          console.log('[EMAIL_ASSISTANT] Erreur création:', data.message);

          setSaving(false);

          return false;

        }

      } catch (e) {

        console.error('[EMAIL_ASSISTANT] Erreur réseau création:', e);

        showNotification('Erreur réseau lors de la création.', 'error');

      setSaving(false);

        return false;

      }

      console.log('[EMAIL_ASSISTANT] === FIN handleSave (CRÉATION) ===');

    }

  };



  // Détection de modifications non sauvegardées
  // TOUJOURS utiliser GrapesJS comme source unique, même en mode IA

  const getCurrentEditorHtml = () => {

    try {

      // En mode Manuel, utiliser le HTML issu de GrapesJS
      if (!isNaturalMode && editorInstanceRef.current) {
        return getHtmlFromGrapes();
      }

      // En mode IA (isNaturalMode === true) ou si GrapesJS n'est pas initialisé,
      // utiliser le contenu courant de l'éditeur React
      return editorContent;

    } catch (_) {

      return editorContent;

    }

  };



  const attemptSwitchMode = async (currentNaturalMode) => {
    // ✅ Bloquer pendant l'enregistrement
    if (saving) return;

    // Ne pas ouvrir la boîte de switch si la boîte de fermeture est déjà ouverte

    if (confirmCloseOpen) {

      return;

    }

    // Le switch n'existe que dans l'étape 2, mais par sécurité on vérifie
    // Si on n'est pas à l'étape 2, changer de mode directement sans confirmation
    if (currentStep !== 2) {
      setIsNaturalMode(!currentNaturalMode);
      return;
    }

    const nextNaturalMode = !currentNaturalMode;

    // Aucun changement utilisateur détecté :
    // - en mode Manuel, on synchronise systématiquement le HTML courant de GrapesJS
    //   vers editorContent avant de passer en mode IA, afin que la prévisualisation
    //   et les futures sauvegardes IA partent toujours du même état que l'éditeur manuel.
    // - en mode IA, on peut simplement basculer sans synchro supplémentaire.
    if (!hasUserChangesRef.current) {
      if (!currentNaturalMode) {
        try {
          const htmlBeforeSwitch = getCurrentEditorHtml();
          lastSavedHtmlRef.current = htmlBeforeSwitch;
          setEditorContent(htmlBeforeSwitch);
          console.log('[SWITCH] Synchronisation HTML Manuel → IA avant changement de mode');
        } catch (_) {}
      }
      setIsNaturalMode(nextNaturalMode);
      return;
    }

    const currentHtml = normalizeHtmlForCompare(getCurrentEditorHtml());

    const lastSaved = normalizeHtmlForCompare(lastSavedHtmlRef.current || '');

    if ((currentHtml || '') !== (lastSaved || '')) {

      setPendingTargetNaturalMode(nextNaturalMode);

      setConfirmSwitchOpen(true);

      return;

    }

    // Pas de changement détecté : synchroniser les états avant le switch
    const htmlBeforeSwitch = getCurrentEditorHtml();
    lastSavedHtmlRef.current = htmlBeforeSwitch;
    setEditorContent(htmlBeforeSwitch);
    hasUserChangesRef.current = false;
    console.log('[SWITCH] États synchronisés avant changement de mode (pas de modifications)');

    setIsNaturalMode(nextNaturalMode);

  };



  const handleConfirmSwitchSave = async () => {

    // En mode création, ne pas fermer la modale ni créer l'email ici.
    // Effectuer une sauvegarde locale et appliquer uniquement le switch de mode.
    if (mode === 'new') {
      try {
        const currentHtml = getCurrentEditorHtml();
        try { lastSavedHtmlRef.current = currentHtml; } catch (_) {}
        try { setEditorContent(currentHtml); } catch (_) {}
      } catch (_) {}
      if (pendingTargetNaturalMode !== null) {
        setIsNaturalMode(pendingTargetNaturalMode);
        setPendingTargetNaturalMode(null);
      }
      setConfirmSwitchOpen(false);
      hasUserChangesRef.current = false;
      return;
    }

    const ok = await handleSave();

    if (ok && pendingTargetNaturalMode !== null) {
      console.log('[SWITCH] Sauvegarde OK, changement de mode');

      // Cas particulier : on vient du mode Manuel (isNaturalMode === false)
      // et on va vers le mode IA (pendingTargetNaturalMode === true).
      // handleSave a bien persisté le HTML depuis GrapesJS dans lastSavedHtmlRef,
      // mais editorContent n'a PAS été mis à jour en mode Manuel.
      // Pour que la preview IA reflète le contenu réellement sauvegardé,
      // on synchronise donc editorContent avec lastSavedHtmlRef avant le switch.
      if (!isNaturalMode && pendingTargetNaturalMode) {
        try {
          const savedHtml = lastSavedHtmlRef.current || editorContent;
          setEditorContent(savedHtml);
        } catch (_) {}
      }

      // Puis on applique le changement de mode, comme si l'utilisateur avait
      // cliqué sur le bouton Enregistrer principal puis changé de mode.
      setIsNaturalMode(pendingTargetNaturalMode);
      setPendingTargetNaturalMode(null);
      setConfirmSwitchOpen(false);
      hasUserChangesRef.current = false;
    } else {
      console.log('[SWITCH] Échec sauvegarde, annulation changement mode');
      setPendingTargetNaturalMode(null);
      setConfirmSwitchOpen(false);
    }
  };



  const handleConfirmSwitchDiscard = () => {
    // IGNORER les modifications = RESTAURER le contenu sauvegardé
    // et passer au nouveau mode
    
    if (pendingTargetNaturalMode !== null) {
      // RESTAURER le contenu sauvegardé (PAS le contenu actuel modifié !)
      const savedHtml = lastSavedHtmlRef.current || '';
      console.log('[SWITCH] IGNORER modifications - Restauration du contenu sauvegardé, longueur:', savedHtml.length);
      
      // Si on est actuellement en mode Manuel et qu'on passe au mode IA
      // Recharger le contenu sauvegardé dans GrapesJS AVANT le changement de mode
      if (!isNaturalMode && editorInstanceRef.current) {
        console.log('[SWITCH] Rechargement du contenu sauvegardé dans GrapesJS avant passage en mode IA');
        try {
          editorInstanceRef.current.setComponents(savedHtml);
          editorInstanceRef.current.refresh();
        } catch (e) {
          console.error('[SWITCH] Erreur rechargement GrapesJS:', e);
        }
      }
      
      // Restaurer editorContent au contenu sauvegardé
      setEditorContent(savedHtml);
      
      // Petit délai pour s'assurer que GrapesJS a bien rechargé le contenu
      setTimeout(() => {
        // Appliquer le changement de mode
        setIsNaturalMode(pendingTargetNaturalMode);
        
        // Si on passe au mode Manuel (depuis IA), recharger le contenu sauvegardé dans GrapesJS
        if (!pendingTargetNaturalMode) {
          // pendingTargetNaturalMode === false => passage au mode Manuel
          setTimeout(() => {
            try {
              if (editorInstanceRef.current) {
                // Recharger le contenu sauvegardé dans GrapesJS
                editorInstanceRef.current.setComponents(savedHtml);
                setTimeout(() => {
                  try {
                    editorInstanceRef.current.refresh();
                    // Re-synchroniser pour capturer le HTML généré par GrapesJS
                    const htmlFromGrapes = getHtmlFromGrapes();
                    lastSavedHtmlRef.current = htmlFromGrapes;
                    setEditorContent(htmlFromGrapes);
                    console.log('[SWITCH] Contenu sauvegardé rechargé dans GrapesJS (passage en mode Manuel)');
                  } catch (_) {}
                }, 100);
              }
            } catch (_) {}
          }, 500);
        }
      }, 100);
    }
    
    setPendingTargetNaturalMode(null);
    setConfirmSwitchOpen(false);
    hasUserChangesRef.current = false;
  };

  
  const handleConfirmSwitchCancel = () => {

    setPendingTargetNaturalMode(null);

    setConfirmSwitchOpen(false);

  };



  // Handlers pour la confirmation de fermeture

  const handleConfirmCloseSave = async () => {

    const ok = await handleSave();

    if (ok) {

      // Sauvegarde réussie, lastSavedHtmlRef est déjà mis à jour par handleSave

      setConfirmCloseOpen(false);

      abortOngoingRequests();

      onClose();

      hasUserChangesRef.current = false;

    }

    // Si la sauvegarde échoue, on garde la boîte ouverte pour que l'utilisateur réessaie

  };



  const handleConfirmCloseDiscard = () => {

    // Fermer sans sauvegarder = Fermer directement sans modifier les états
    // (Les modifications sont perdues car on quitte la modal)

    console.log('[CLOSE] Fermeture sans sauvegarde');

    setConfirmCloseOpen(false);

    abortOngoingRequests();

    onClose();

    hasUserChangesRef.current = false;

  };



  const handleConfirmCloseCancel = () => {

    // Annuler la fermeture

    setConfirmCloseOpen(false);

  };



  // ============================================================
  // GESTION DES ASSETS MAUTIC (Images)
  // ============================================================

  const assetsPageRef = useRef(1);
  const assetsTotalPagesRef = useRef(null);
  const isLoadingAssetsRef = useRef(false);

  // Helper pour normaliser l'URL d'un asset Mautic
  const resolveMauticAssetSrc = (maybeUrl) => {
    try {
      const raw = String(maybeUrl || '').trim();
      if (!raw) return '';
      // Déjà une URL absolue
      if (/^https?:\/\//i.test(raw) || raw.startsWith('data:')) return raw;
      const base = (userCredentials && userCredentials.mautic_url) || '';
      if (!base) return raw; // fallback brut si on n'a pas l'URL Mautic
      // Assurer un seul slash
      const sep = base.endsWith('/') || raw.startsWith('/') ? '' : '/';
      return `${base}${sep}${raw.replace(/^\/+/, '')}`;
    } catch (_) {
      return maybeUrl;
    }
  };

  // Charger les assets depuis Mautic API
  const loadMauticAssets = async (page = 1) => {
    try {
      console.log('[ASSET_MANAGER] Début chargement assets, page:', page);
      const response = await api.get('/emails/mautic/assets', {
        params: {
          // Essayer différentes stratégies de recherche
          // search: 'extension:gif OR extension:jpg OR extension:jpeg OR extension:png OR extension:tif OR extension:tiff',
          // search: 'extension:jpg OR extension:png',
          // search: 'extension:jpg',
          // Pas de filtre de recherche pour voir tous les assets
          limit: 30,
          start: (page - 1) * 30,
          orderBy: 'dateAdded',
          orderByDir: 'DESC'
        }
      });

      console.log('[ASSET_MANAGER] Réponse API:', response.data);

      if (response.data && response.data.assets) {
        const assets = Object.values(response.data.assets).map(asset => ({
          type: 'image',
          src: resolveMauticAssetSrc(asset.downloadUrl || asset.path || asset.url),
          id: asset.id,
          name: asset.title || asset.originalFileName
        }));

        console.log('[ASSET_MANAGER] Assets normalisés:', assets.length, 'assets');

        // Calculer le nombre total de pages
        if (response.data.total) {
          assetsTotalPagesRef.current = Math.ceil(response.data.total / 30);
          console.log('[ASSET_MANAGER] Total pages:', assetsTotalPagesRef.current);
        }

        return assets;
      }

      console.log('[ASSET_MANAGER] Aucun asset trouvé dans la réponse');
      return [];
    } catch (error) {
      console.error('[ASSET_MANAGER] Erreur chargement assets:', error);
      return [];
    }
  };

  // Vérifier si tous les assets ont été chargés
  const hasLoadedAllAssets = () => {
    return assetsTotalPagesRef.current !== null && assetsPageRef.current >= assetsTotalPagesRef.current;
  };

  // Charger la page suivante d'assets
  const loadNextAssetsPage = async (editor) => {
    if (isLoadingAssetsRef.current || hasLoadedAllAssets()) {
      return;
    }

    isLoadingAssetsRef.current = true;
    assetsPageRef.current++;

    const newAssets = await loadMauticAssets(assetsPageRef.current);
    
    if (newAssets.length > 0 && editor.AssetManager) {
      const currentAssets = editor.AssetManager.getAll();
      const combinedAssets = [...currentAssets, ...newAssets];
      
      editor.AssetManager.getAll().reset(combinedAssets);
      editor.AssetManager.render();
    }

    isLoadingAssetsRef.current = false;
  };

  // Supprimer un asset de Mautic
  const deleteMauticAsset = async (assetId) => {
    try {
      await api.delete(`/emails/mautic/assets/${assetId}`);
      return true;
    } catch (error) {
      console.error('[ASSET_MANAGER] Erreur suppression asset:', error);
      showNotification("Erreur lors de la suppression de l'image", 'error');
      return false;
    }
  };

  // ============================================================


  // Raccourcis clavier et initialisation GrapesJS pour le mode manuel

  useEffect(() => {

    let mutationObserver = null;
    let cancelled = false;

    console.log('[GRAPESJS_EFFECT] Déclenchement useEffect:', { open, isNaturalMode, editorType, mode, currentStep, hasContainer: !!grapesContainerRef.current });

    // Init/Détruire GrapesJS selon le mode manuel

    // En mode création, ne s'initialiser que si on est à l'étape 2 (édition)
    // En mode édition, ne s'initialiser qu'une fois le mode auto-sélectionné, l'état chargé
    // et sans erreur de chargement de l'état éditeur.
    const isEditAndReady = mode === 'edit' && autoModeReady && !editorStateLoading && !editorStateError;
    const isNewAndReady = mode === 'new' && currentStep === 2;
    const shouldInitialize = open && !isNaturalMode && (isEditAndReady || isNewAndReady);
    
    console.log('[GRAPESJS_EFFECT] shouldInitialize:', shouldInitialize, { open, isNaturalMode, mode, currentStep, isEditAndReady, isNewAndReady });
    
    if (shouldInitialize) {
      
      // Fonction async pour gérer la destruction propre de l'éditeur
      const initEditor = async () => {
        
        // Attendre que le conteneur soit disponible
        let attempts = 0;
        while (!grapesContainerRef.current && attempts < 10) {
          if (cancelled) return;
          console.log('[GRAPESJS_INIT] Attente du conteneur, tentative:', attempts + 1);
          await new Promise(r => setTimeout(r, 100));
          attempts++;
        }
        
        if (cancelled) return;
        if (!grapesContainerRef.current) {
          console.error('[GRAPESJS_INIT] Conteneur GrapesJS non disponible après 10 tentatives');
          return;
        }
        
        console.log('[GRAPESJS_INIT] Conteneur trouvé, initialisation...');
        // Détruire proprement un éditeur existant seulement si présent
        try {
          if (editorInstanceRef.current && editorInstanceRef.current.destroy) {
            console.log('[GRAPESJS_CLEANUP] Destruction de l\'éditeur précédent...');
            editorInstanceRef.current.destroy();
            editorInstanceRef.current = null;
            await new Promise((r) => setTimeout(r, 50));
            console.log('[GRAPESJS_CLEANUP] Éditeur détruit, prêt pour réinitialisation');
          }
        } catch (err) {
          console.warn('[GRAPESJS_CLEANUP] Erreur lors de la destruction:', err);
        }

        // Vérification visuelle et logs de mode (debug)
        const isMJMLMode = editorType === 'mjml';
        const isHtmlMode = editorType === 'html';
        
        console.log('[EDITOR MODE]', {
          mode,
          selectedMethod,
          editorType,
          isMJMLMode,
          isHtmlMode,
        });

        // Déterminer les plugins à utiliser selon le type d'éditeur
        const plugins = editorType === 'mjml' 
          ? [mjml] 
          : [newsletter, postcss];

        const pluginsOpts = editorType === 'mjml'
        ? {
            [mjml]: {
              // Configuration MJML pour création
              resetBlocks: true,
              resetDevices: false,
              resetStyleManager: true,
              hideSelector: true,
              columnsPadding: '10px 0',
            }
          }
        : {
            [newsletter]: {
              useCustomTheme: false,
            }
          };

        console.log(`[GRAPESJS_INIT] Initialisation avec éditeur ${editorType}`, { plugins: plugins.map(p => p.name || 'unknown') });

        if (cancelled) return;

        const editor = grapesjs.init({

        container: grapesContainerRef.current,

        fromElement: false,

        height: '100%',

        width: '100%',

        storageManager: false,

        plugins: plugins,
        pluginsOpts: pluginsOpts,

        assetManager: {

          assets: [],  // Sera rempli dynamiquement au chargement

          noAssets: 'Aucune image disponible. Uploadez votre première image !',

          upload: true, // ✅ Activé pour utiliser uploadFile personnalisé

          uploadName: 'file',

          multiUpload: true,  // ❗ Eviter les uploads parallèles qui saturent le serveur

          embedAsBase64: false,  // ✅ Pas de base64, utiliser URLs serveur

          openAssetsOnDrop: 1,  // ✅ Ouvrir l'asset manager au drag & drop

          autoAdd: 1,  // ✅ Ajouter automatiquement après upload

          uploadFile: async (e) => {

            console.log('[ASSET_MANAGER] uploadFile appelé, événement:', e);

            const files = Array.isArray(e) ? e : (e && (e.dataTransfer && e.dataTransfer.files || e.target && e.target.files) || []);

            console.log('[ASSET_MANAGER] Fichiers détectés:', files.length);

            const addedAssets = [];

            for (const file of files) {

              try {

                // Validation côté client: uniquement images <= 6 Mo (limite Mautic)

                const MAX_IMAGE_SIZE_BYTES = 6 * 1024 * 1024;

                // Extensions autorisées par Mautic (config.php)

                const ALLOWED_EXTENSIONS = ['gif', 'jpg', 'jpeg', 'png', 'tif', 'tiff'];

                // Validation du type de fichier

                if (!file.type || !file.type.startsWith('image/')) {

                  showNotification('Seuls les fichiers image sont acceptés.', 'error');

                  continue;

                }

                // Validation de l'extension

                const fileName = file.name || '';

                const fileExtension = fileName.split('.').pop()?.toLowerCase();

                if (!fileExtension || !ALLOWED_EXTENSIONS.includes(fileExtension)) {

                  showNotification(`Extension non autorisée. Extensions acceptées : ${ALLOWED_EXTENSIONS.join(', ')}`, 'error');

                  continue;

                }

                // Validation de la taille (6 MB max comme Mautic)

                if (file.size > MAX_IMAGE_SIZE_BYTES) {

                  const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);

                  showNotification(`Image trop volumineuse (${fileSizeMB} Mo). Taille maximale : 6 Mo.`, 'error');

                  continue;

                }

              } catch (_) {}

              // Préparer FormData pour Mautic API

              const fd = new FormData();

              fd.append('file', file);
              // Donner un titre explicite à l'asset côté Mautic pour générer une URL alias correcte
              try { fd.append('title', file.name); } catch (_) {}

              try {

                // Upload vers Mautic API: POST /api/v1/emails/mautic/assets

                const doUpload = async () => api.post('/emails/mautic/assets', fd, { 

                  headers: { 'Content-Type': 'multipart/form-data' } 

                });

                let res;
                try {
                  res = await doUpload();
                } catch (firstErr) {
                  // Retry unique en cas de reset/timeout
                  await new Promise(r => setTimeout(r, 700));
                  res = await doUpload();
                }

                if (res.data && (res.data.asset || res.data.assets)) {

                  // Certains backends renvoient { asset }, d'autres un tableau/objet { assets }
                  const list = res.data.asset ? [res.data.asset] : Object.values(res.data.assets || {});
                  const normalized = list.map(mauticAsset => {
                    const id = mauticAsset.id;
                    const originalName = mauticAsset.originalFileName || mauticAsset.title || `asset-${id}.png`;
                    const safeName = encodeURIComponent(originalName);
                    let rawUrl = mauticAsset.downloadUrl || mauticAsset.url || mauticAsset.path;
                    if (!rawUrl && id) rawUrl = `/asset/${id}:${originalName}`;
                    let src = resolveMauticAssetSrc(rawUrl);
                    // Bust caches si serveur est lent à propager le fichier
                    const cacheBuster = `t=${Date.now()}`;
                    src = src.includes('?') ? `${src}&${cacheBuster}` : `${src}?${cacheBuster}`;
                    return {
                    type: 'image', 
                      src,
                      id,
                      name: mauticAsset.title || originalName
                    };
                  });

                  editor.AssetManager.add(normalized);
                  try { editor.AssetManager.render(); } catch (_) {}
                  addedAssets.push(...normalized);
                  showNotification('Image uploadée avec succès !', 'success');

                } else {

                  const msg = (res && res.data && (res.data.message || res.data.detail)) || "Erreur lors de l'upload de l'image";
                  showNotification(msg, 'error');

                }

              } catch (err) {

                console.error('[ASSET_MANAGER] Erreur upload:', err);
                try {
                  const serverMsg = err?.response?.data?.message || err?.message || "Erreur lors de l'upload de l'image";
                  showNotification(serverMsg, 'error');
                } catch (_) {
                showNotification("Erreur lors de l'upload de l'image", 'error');
                }

              }

            }

            console.log('[ASSET_MANAGER] Upload terminé, assets ajoutés:', addedAssets.length);

            return addedAssets;

          }

        },

        canvas: {

          styles: [],

          scripts: []

        },

        // Configuration officielle du color picker GrapesJS
        // On l'attache au parent du champ ("parent") pour qu'il suive correctement
        // les panneaux de style, y compris quand l'éditeur passe en fullscreen.
        colorPicker: {

          appendTo: 'parent',

        }

        });

        editorInstanceRef.current = editor;
        try {
          const modal = editor.Modal;
          if (modal && !gjsDynamicContentModalElRef.current) {
            const el = document.createElement('div');
            gjsDynamicContentModalElRef.current = el;
            modal.setTitle('Configurer le contenu dynamique Mautic');
            modal.setContent(el);
          }
        } catch (_) {}

        // Enregistrer les types de composants dédiés pour le contenu dynamique
        // avant tout chargement de contenu ou setComponents
        try {
          const domc = editor.DomComponents;
          if (domc) {
            if (editorType === 'html') {
              const baseType = domc.getType('text') || domc.getType('default');
              if (baseType && !domc.getType('mautic-dynamic-content')) {
                const baseModel = baseType.model;
                const baseView = baseType.view;

                domc.addType('mautic-dynamic-content', {
                  model: baseModel.extend({
                    defaults: {
                      ...baseModel.prototype.defaults,
                      name: 'Dynamic Content',
                      droppable: false,
                      editable: false,
                    },
                  }, {
                    isComponent(el) {
                      try {
                        if (!el || !el.getAttribute) return false;
                        const attr = el.getAttribute('data-mautic-dynamic');
                        const txt = (el.textContent || '').trim();
                        if (attr === '1' || txt.indexOf('{dynamiccontent=') !== -1) {
                          return { type: 'mautic-dynamic-content' };
                        }
                      } catch (_) {}
                      return false;
                    },
                  }),
                  view: baseView.extend({
                    events: {
                      dblclick: 'onActive',
                    },
                    onActive(ev) {
                      try {
                        const el = this.el;
                        if (!el) return;
                        const text = (el.textContent || el.innerHTML || '').trim();
                        if (!text || text.indexOf('{dynamiccontent=') === -1) return;

                        const match = text.match(/\{dynamiccontent=\"([^\"]+)\"\}/);
                        if (!match || !match[1]) return;
                        const alias = (match[1] || '').trim();
                        if (!alias) return;

                        try { console.log('[DC_HTML_ONACTIVE] Open modal for alias', alias); } catch (_) {}

                        setDynamicContentError('');
                        setSelectedDynamicContentId(null);
                        handleDynamicContentAliasChange({ target: { value: alias } });
                        openDynamicContentDialog();

                        if (ev && typeof ev.stopPropagation === 'function') ev.stopPropagation();
                        if (ev && typeof ev.preventDefault === 'function') ev.preventDefault();
                      } catch (e) {
                        try {
                          console.error('[DC_HTML_ONACTIVE] Error in onActive', e);
                        } catch (_) {}
                      }
                    },
                  }),
                });
              }
            } else if (editorType === 'mjml') {
              const mjmlType = domc.getType('mj-text');
              if (mjmlType && !domc.getType('mj-mautic-dynamic-content')) {
                const mjModel = mjmlType.model;
                const mjView = mjmlType.view;

                domc.addType('mj-mautic-dynamic-content', {
                  model: mjModel.extend({
                    defaults: {
                      ...mjModel.prototype.defaults,
                      name: 'Dynamic Content',
                      droppable: false,
                      editable: false,
                    },
                  }, {
                    isComponent(el) {
                      try {
                        if (!el) return false;
                        const tag = (el.tagName || '').toLowerCase();
                        if (tag !== 'mj-text') return false;
                        const attr = el.getAttribute && el.getAttribute('data-mautic-dynamic');
                        const txt = (el.textContent || '').trim();
                        if (attr === '1' || txt.indexOf('{dynamiccontent=') !== -1) {
                          return { type: 'mj-mautic-dynamic-content' };
                        }
                      } catch (_) {}
                      return false;
                    },
                  }),
                  view: mjView.extend({
                    events: {
                      dblclick: 'onActive',
                    },
                    onActive(ev) {
                      try {
                        const el = this.el;
                        if (!el) return;
                        const text = (el.textContent || el.innerHTML || '').trim();
                        if (!text || text.indexOf('{dynamiccontent=') === -1) return;

                        const match = text.match(/\{dynamiccontent=\"([^\"]+)\"\}/);
                        if (!match || !match[1]) return;
                        const alias = (match[1] || '').trim();
                        if (!alias) return;

                        try { console.log('[DC_MJML_ONACTIVE] Open modal for alias', alias); } catch (_) {}

                        setDynamicContentError('');
                        setSelectedDynamicContentId(null);
                        handleDynamicContentAliasChange({ target: { value: alias } });
                        openDynamicContentDialog();

                        if (ev && typeof ev.stopPropagation === 'function') ev.stopPropagation();
                        if (ev && typeof ev.preventDefault === 'function') ev.preventDefault();
                      } catch (e) {
                        try {
                          console.error('[DC_MJML_ONACTIVE] Error in onActive', e);
                        } catch (_) {}
                      }
                    },
                  }),
                });
              }
            }
          }
        } catch (_) {}

        try { if (initialBuilderJson) { let proj = initialBuilderJson; try { if (typeof proj === 'string') proj = JSON.parse(proj); } catch (_) {}; editor.loadProjectData(proj); } } catch (_) {}
        
        console.log('[GRAPESJS_INIT] GrapesJS initialisé avec succès!', { editorType, plugins: plugins.map(p => p.name || 'unknown') });

        // Capturer périodiquement l'état du builder + MJML source (si applicable)
        try {
          editor.on('update', () => {
            try { if (builderSaveTimeoutRef.current) clearTimeout(builderSaveTimeoutRef.current); } catch (_) {}
            builderSaveTimeoutRef.current = setTimeout(() => {
              try {
                if (editor.getProjectData) {
                  const project = editor.getProjectData();
                  setBuilderJson(project);
                }
              } catch (_) {}
              try {
                if (editorType === 'mjml') {
                  const mj = editor.getMjml ? editor.getMjml() : editor.getHtml();
                  setMjmlSource(mj);
                }
              } catch (_) {}
            }, 400);
          });
        } catch (_) {}

        try {
          const attachDynamicContentDblClick = (cmp) => {
            try {
              if (!cmp || !cmp.view || !cmp.view.el) return;
              const view = cmp.view;
              const el = view.el;

              if (view.__dcDblClickBound) return;

              let html = '';
              try {
                html = el.innerHTML || el.textContent || '';
              } catch (_) {}

              if (!html || html.indexOf('{dynamiccontent=') === -1) return;

              const handler = (ev) => {
                try {
                  let inner = '';
                  try {
                    inner = el.innerHTML || el.textContent || '';
                  } catch (_) {}

                  if (!inner || inner.indexOf('{dynamiccontent=') === -1) return;

                  const match = inner.match(/\{dynamiccontent=\"([^\"]+)\"\}/);
                  if (!match || !match[1]) return;

                  const alias = (match[1] || '').trim();
                  if (!alias) return;

                  setDynamicContentError('');
                  setSelectedDynamicContentId(null);
                  handleDynamicContentAliasChange({ target: { value: alias } });
                  openDynamicContentDialog();

                  if (ev && typeof ev.stopPropagation === 'function') ev.stopPropagation();
                  if (ev && typeof ev.preventDefault === 'function') ev.preventDefault();

                  try {
                    console.log('[DC_DBLCLICK_DOM] Opened DC modal for alias', alias);
                  } catch (_) {}
                } catch (e) {
                  try {
                    console.error('[DC_DBLCLICK_DOM] Error in dblclick handler', e);
                  } catch (_) {}
                }
              };

              el.addEventListener('dblclick', handler);
              view.__dcDblClickBound = true;
              view.__dcDblClickHandler = handler;
            } catch (_) {}
          };

          editor.on('component:add', (cmp) => {
            attachDynamicContentDblClick(cmp);
          });

          editor.on('component:selected', (component) => {
            attachDynamicContentDblClick(component);
          });
        } catch (_) {}

        // Remplacer le bouton par défaut par un bouton style Mautic (uniquement en mode HTML)
        if (editorType === 'html') {
        try {
          const bm = editor.BlockManager;
          const buttonBlock = bm.get('button');
          if (buttonBlock) {
            buttonBlock.set({
              content:
                '<a href="#" target="_blank" style="display:inline-block;text-decoration:none;border-color:#4e5d9d;border-width: 10px 20px;border-style:solid; text-decoration: none; -webkit-border-radius: 3px; -moz-border-radius: 3px; border-radius: 3px; background-color: #4e5d9d; display: inline-block;font-size: 16px; color: #ffffff; ">Button</a>',
            });
          }
        } catch (_) {}
        }

        // Organisation des blocs selon le type d'éditeur
        try {
        const bm = editor.BlockManager;
        const blocks = bm.getAll();
        
        console.log(`[BLOCKS] Organisation des blocs pour éditeur ${editorType}:`, blocks.map(b => b.attributes.id));

        if (editorType === 'html') {
          // ÉDITEUR HTML : Organisation Mautic (Sections + Basic)
          const categories = bm.getCategories();
          
          // Ajouter la catégorie Sections si elle n'existe pas
          if (categories && !categories.find(c => c.get('id') === 'sections')) {
            categories.add({
              id: 'sections',
              label: 'Sections',
              order: -1,
              open: true
            });
          }
          
          // Ajouter la catégorie Basic si elle n'existe pas
          if (categories && !categories.find(c => c.get('id') === 'basic')) {
            categories.add({
              id: 'basic',
              label: 'Blocks',
              order: 0,
              open: true
            });
          }
          
          // Réorganiser les blocs HTML
          blocks.map(block => {
            const blockId = block.attributes.id;
            
            // Colonnes et layouts (sections) → catégorie "Sections"
            if(blockId && (
              blockId.indexOf('column') !== -1 || 
              blockId === 'sect100' || 
              blockId === 'sect50' || 
              blockId === 'sect30' || 
              blockId === 'sect37'
            )) {
              bm.get(blockId).set('category', 'sections');
            }
            // Tout le reste → catégorie "Basic"
            else {
              bm.get(blockId).set('category', 'basic');
            }
          });
          
          console.log('[BLOCKS] Blocs HTML organisés en Sections + Basic');
        } 
        else if (editorType === 'mjml') {
          // ÉDITEUR MJML : Garder les catégories natives du plugin MJML
          console.log('[BLOCKS] Blocs MJML - Catégories natives conservées');
          
          // Logger les catégories MJML pour debug
          const categories = bm.getCategories();
          if (categories) {
            console.log('[BLOCKS] Catégories MJML:', categories.map(c => c.get('id')));
          }
        }
        } catch (err) {
          console.error('[BLOCKS] Erreur organisation des blocs:', err);
        }

        // ============================================================
        // AJOUT DES BLOCS VARIABLES MAUTIC
        // ============================================================
        try {
          console.log('[MAUTIC_BLOCKS] Ajout des blocs Variables Mautic...');
          
          const bm = editor.BlockManager;
          
          // Créer la catégorie "Variables Mautic"
          const categories = bm.getCategories();
          if (categories && !categories.find(c => c.get('id') === 'mautic-variables')) {
            categories.add({
              id: 'mautic-variables',
              label: '🔗 Variables Mautic',
              order: 10,
              open: false
            });
          }
          
          // Fonction helper pour créer le contenu selon le type d'éditeur
          const createMauticVarContent = (variable, label, isLink = false) => {
            if (editorType === 'mjml') {
              // En MJML : envelopper dans <mj-text>
              if (isLink) {
                return `<mj-text font-size="14px" align="center" color="#999">
                  <p style="margin:0;">${variable}</p>
                </mj-text>`;
              }
              return `<mj-text font-size="16px">
                <p style="margin:0;">${variable}</p>
              </mj-text>`;
            } else {
              // En HTML : paragraphe simple
              if (isLink) {
                return `<p style="font-size:14px; text-align:center; color:#999; margin:10px 0;">${variable}</p>`;
              }
              return `<p style="font-size:16px; margin:10px 0;">${variable}</p>`;
            }
          };
          
          // Définition des blocs de variables Mautic
          const mauticBlocks = [
            {
              id: 'mautic-firstname',
              label: '👤 Prénom du contact',
              variable: '{contactfield=firstname}',
              icon: '👤'
            },
            {
              id: 'mautic-lastname',
              label: '👥 Nom du contact',
              variable: '{contactfield=lastname}',
              icon: '👥'
            },
            {
              id: 'mautic-email',
              label: '📧 Email du contact',
              variable: '{contactfield=email}',
              icon: '📧'
            },
            {
              id: 'mautic-phone',
              label: '📞 Téléphone',
              variable: '{contactfield=phone}',
              icon: '📞'
            },
            {
              id: 'mautic-company',
              label: '🏢 Entreprise',
              variable: '{contactfield=company}',
              icon: '🏢'
            },
            {
              id: 'mautic-brand-name',
              label: '🏷️ Nom de la marque',
              variable: '{brand=name}',
              icon: '🏷️'
            },
            {
              id: 'mautic-brand-address',
              label: '📍 Adresse de la marque',
              variable: '{brand=address}',
              icon: '📍'
            },
            {
              id: 'mautic-webview',
              label: '👁️ Lien Webview',
              variable: '{webview_text}',
              icon: '👁️',
              isLink: true
            },
            {
              id: 'mautic-unsubscribe',
              label: '🚫 Lien Désabonnement',
              variable: '{unsubscribe_text}',
              icon: '🚫',
              isLink: true
            },
            {
              id: 'mautic-dynamic-content',
              label: '⚙️ Contenu dynamique',
              variable: '{dynamiccontent="DYNAMIC_SLOT"}',
              icon: '⚙️'
            }
          ];
          
          // Ajouter chaque bloc
          mauticBlocks.forEach(block => {
            let content;

            if (block.id === 'mautic-dynamic-content') {
              if (editorType === 'mjml') {
                // Bloc Dynamic Content en MJML : simple mj-text avec le token visible (sans attribut custom MJML)
                content = '<mj-text>{dynamiccontent="DYNAMIC_SLOT"}</mj-text>';
              } else if (editorType === 'html') {
                // Bloc Dynamic Content en HTML : simple span avec attribut de détection
                // Le type 'mautic-dynamic-content' sera appliqué via isComponent
                content = '<span data-mautic-dynamic="1">{dynamiccontent="DYNAMIC_SLOT"}</span>';
              } else {
                // Fallback théorique (autre type d'éditeur)
                content = createMauticVarContent(block.variable, block.label, block.isLink);
              }
            } else {
              content = createMauticVarContent(block.variable, block.label, block.isLink);
            }

            bm.add(block.id, {
              label: block.label,
              category: 'mautic-variables',
              content,
              attributes: {
                class: 'fa fa-tag',
                title: `Insérer ${block.label}`,
              },
            });
          });
          
          console.log(`[MAUTIC_BLOCKS] ✅ ${mauticBlocks.length} blocs Variables Mautic ajoutés`);
        } catch (err) {
          console.error('[MAUTIC_BLOCKS] Erreur ajout blocs Variables Mautic:', err);
        }

        // Lorsqu'un bloc est déposé, ouvrir la modale uniquement si c'est le bloc
        // "⚙️ Contenu dynamique" qui vient d'être déposé.
        try {
          editor.on('block:drag:stop', (component, block) => {
            try {
              if (!block) return;

              const blockId =
                typeof block.get === 'function'
                  ? block.get('id')
                  : block.id || block;

              if (blockId !== 'mautic-dynamic-content') {
                // Tout autre bloc (image, texte, etc.) doit garder son comportement natif
                // (ex: double-clic image ouvre l'Asset Manager).
                return;
              }

              // Préparer la modale de configuration Dynamic Content (création)
              setDynamicContentError('');
              setSelectedDynamicContentId(null);
              setDynamicContentAlias('');
              setCurrentDcSlot(null);
              setCurrentDcTab('default');
              openDynamicContentDialog();
            } catch (_) {}
          });

          // Double-clic sur un composant déjà inséré pour rouvrir la modale
          editor.on('component:dblclick', (component, ev) => {
            try {
              if (!component) return;

              const tokenRegex = /\{dynamiccontent=\"([^\"]+)\"\}/;

              const openForAlias = (rawAlias) => {
                const alias = (rawAlias || '').trim();
                if (!alias) return false;

                setDynamicContentError('');
                setSelectedDynamicContentId(null);
                handleDynamicContentAliasChange({ target: { value: alias } });
                openDynamicContentDialog();

                if (ev && typeof ev.stopPropagation === 'function') ev.stopPropagation();
                if (ev && typeof ev.preventDefault === 'function') ev.preventDefault();

                try {
                  console.log('[DC_DBLCLICK] Opened DC modal for alias', alias);
                } catch (_) {}

                return true;
              };

              const debugComponent = () => {
                try {
                  const type = typeof component.get === 'function' ? component.get('type') : null;
                  const attrs = typeof component.getAttributes === 'function' ? component.getAttributes() : {};
                  const content = typeof component.get === 'function' ? component.get('content') : undefined;
                  console.log('[DC_DBLCLICK] component:dblclick fired', {
                    editorType,
                    type,
                    attrs,
                    contentSnippet: typeof content === 'string' ? content.slice(0, 200) : content,
                  });
                } catch (_) {}
              };

              debugComponent();

              // Helper générique : récupérer le texte HTML/innerText d'un composant
              // et détecter la présence d'un token {dynamiccontent="..."}
              const getDcTextFromCmp = (cmp) => {
                if (!cmp) return '';
                let text = '';
                try {
                  if (typeof cmp.get === 'function') {
                    const c = cmp.get('content');
                    if (typeof c === 'string') text = c;
                  }
                } catch (_) {}

                if ((!text || text.indexOf('{dynamiccontent=') === -1) && cmp.view && cmp.view.el) {
                  try {
                    text = cmp.view.el.textContent || cmp.view.el.innerHTML || text;
                  } catch (_) {}
                }

                return text || '';
              };

              // Partir du composant double-cliqué et remonter ses parents
              // jusqu'à trouver un contenu qui contient le token dynamiccontent
              let target = component;
              let text = getDcTextFromCmp(target);

              try {
                console.log('[DC_DBLCLICK] initial text from component', (text || '').slice(0, 200));
              } catch (_) {}

              while (target && (!text || text.indexOf('{dynamiccontent=') === -1)) {
                if (typeof target.parent === 'function') {
                  target = target.parent();
                  text = getDcTextFromCmp(target);
                } else {
                  target = null;
                }
              }

              try {
                console.log('[DC_DBLCLICK] resolved target text', target ? (text || '').slice(0, 200) : null);
              } catch (_) {}

              if (!target || !text || text.indexOf('{dynamiccontent=') === -1) return;

              const match = text.match(tokenRegex);
              if (!match || !match[1]) return;

              openForAlias(match[1]);
            } catch (e) {
              try {
                console.error('[DC_DBLCLICK] Error in component:dblclick handler', e);
              } catch (_) {}
            }
          });
        } catch (_) {}

        // Charger le contenu initial (MJML ou HTML)
        try {
          if (initialBuilderJson) {
            try { let proj = initialBuilderJson; try { if (typeof proj === 'string') proj = JSON.parse(proj); } catch (_) {}; editor.loadProjectData(proj); console.log('[CONTENT_INJECTION] Chargement du projet GrapesJS depuis builderJson'); } catch (_) {}
          } else {
            const isMJMLMode = editorType === 'mjml';
            let injectedContent;
            if (isMJMLMode) {
              if (editorContent && editorContent.trim().startsWith('<mjml')) {
                injectedContent = editorContent;
                console.log('[CONTENT_INJECTION] Chargement MJML généré');
              } else {
                injectedContent = `
                  <mjml>
                    <mj-body>
                      <mj-section>
                        <mj-column>
                          <mj-text>
                            <h1>Commencez à créer votre email</h1>
                            <p>Utilisez les blocs MJML sur la gauche pour construire votre email.</p>
                          </mj-text>
                        </mj-column>
                      </mj-section>
                    </mj-body>
                  </mjml>
                `;
                console.log('[CONTENT_INJECTION] Chargement starter template MJML');
              }
            } else {
              if (mode === 'edit' && email) {
                const saved = email.customHtml || email.content || '';
                const ec = editorContent || '';
                try {
                  if (ec && normalizeHtmlForCompare(ec) !== normalizeHtmlForCompare(saved)) {
                    injectedContent = ec;
                    console.log('[CONTENT_INJECTION] Chargement HTML depuis editorContent (préféré car différent de email.customHtml)', {
                      emailId: email?.id,
                      emailName: email?.name,
                      contentLength: injectedContent.length
                    });
                  } else {
                    injectedContent = saved;
                    console.log('[CONTENT_INJECTION] Chargement HTML depuis email.customHtml', {
                      emailId: email?.id,
                      emailName: email?.name,
                      contentLength: injectedContent.length
                    });
                  }
                } catch (_) {
                  injectedContent = saved || ec || '';
                  console.log('[CONTENT_INJECTION] Chargement HTML (fallback) en mode édition', {
                    emailId: email?.id,
                    emailName: email?.name,
                    contentLength: injectedContent.length
                  });
                }
              } else {
                injectedContent = editorContent || '';
                console.log('[CONTENT_INJECTION] Chargement HTML depuis editorContent', { 
                  contentLength: injectedContent.length 
                });
              }
            }
            console.log(`[CONTENT_INJECTION] Mode: ${editorType}, MJML: ${isMJMLMode}, Longueur contenu: ${injectedContent.length}`);
            editor.setComponents(injectedContent);
          }

        // Forcer le rafraîchissement du canvas (sinon parfois vide)
        setTimeout(() => {
          try {
            editor.refresh();
            console.log('[CONTENT_INJECTION] Canvas rafraîchi');
            
            // Synchroniser lastSavedHtmlRef ET editorContent avec le HTML généré par GrapesJS
            // pour éviter les faux positifs de "modifications non sauvegardées"
            // lors des switch de mode sans modification réelle
            setTimeout(() => {
              try {
                const htmlFromGrapes = getHtmlFromGrapes();
                lastSavedHtmlRef.current = htmlFromGrapes;
                setEditorContent(htmlFromGrapes);
                editorInitializedRef.current = true;
                hasUserChangesRef.current = false;
                console.log('[CONTENT_INJECTION] lastSavedHtmlRef ET editorContent synchronisés après initialisation');
              } catch (_) {}
            }, 100);
          } catch (_) {}
        }, 200);

        // Injecter les styles <style> présents dans le head du contenu initial dans le canvas

        try {

          const frameEl = editor.Canvas.getFrameEl();

          const doc = frameEl && frameEl.contentDocument;

          if (doc) {

            const parser = new DOMParser();

            const parsed = parser.parseFromString(editorContent || '', 'text/html');

            const styleTags = parsed.head ? parsed.head.querySelectorAll('style') : [];

            styleTags.forEach(tag => {

              const cloned = doc.createElement('style');

              cloned.type = 'text/css';

              cloned.textContent = tag.textContent || '';

              doc.head.appendChild(cloned);

            });

            // Préserver les styles par défaut pour les boutons
            const builtin = doc.createElement('style');
            builtin.type = 'text/css';
            builtin.textContent = `
              a, button { cursor: pointer; }
              .btn, .gjs-btn, .gjs-button, a[role="button"] {
                display: inline-block; text-align: center; line-height: 1.2;
              }
            `;
            doc.head.appendChild(builtin);
        }

      } catch (_) {}

        } catch (_) {}
        // Ne pas sanitizer dans le canvas GrapesJS pour éviter de casser les styles de blocs (ex: Button)
      // Désactiver notre synchronisation automatique en mode manuel

      // GrapesJS gère l'historique (undo/redo) et son propre état interne.

      

      
        // Fix des styles de boutons (GrapesJS injecte parfois des resets)
        try {
        const frameEl = editor.Canvas.getFrameEl();
        const doc = frameEl && frameEl.contentDocument;
        if (doc) {
          const styleEl = doc.createElement('style');
          styleEl.type = 'text/css';
          styleEl.textContent = `
            .gjs-selected { outline: 2px solid #3b82f6 !important; }
            a.btn, .gjs-button, button, .button {
              display: inline-block;
              padding: 12px 20px;
              background-color: #007bff;
              color: #fff; text-decoration: none;
              border-radius: 4px; border: 1px solid #007bff;
            }
            a.btn:hover, .gjs-button:hover, button:hover, .button:hover { opacity: 0.95; }
          `;
          doc.head.appendChild(styleEl);
        }
        } catch (_) {}
        

        // Écouter les changements de contenu dans le canvas

      // Pas d'écouteurs de sync personnalisés pour ne pas interférer avec GrapesJS

        // Synchroniser lastSavedHtmlRef après le chargement initial de GrapesJS
        editor.on('load', () => {
        try {
          // Mettre à jour lastSavedHtmlRef avec le HTML de GrapesJS après chargement
          // pour éviter les faux positifs lors du premier switch de mode
          setTimeout(() => {
            try {
              const initialHtml = getHtmlFromGrapes();
              lastSavedHtmlRef.current = initialHtml;
              editorInitializedRef.current = true;
              hasUserChangesRef.current = false;
            } catch (_) {}
          }, 200);
        } catch (_) {}
        });

        // Listeners pour mettre à jour le preview en temps réel
        const updatePreview = () => {
          if (editorInitializedRef.current) {
            hasUserChangesRef.current = true;
          }
          setPreviewKey(prev => prev + 1);
        };
        
        editor.on('component:add', updatePreview);
        editor.on('component:update', updatePreview);
        editor.on('component:remove', updatePreview);
        editor.on('component:drag:end', updatePreview);
        editor.on('component:styleUpdate', updatePreview);

        // ============================================================
        // ÉVÉNEMENTS ASSET MANAGER (Mautic API)
        // ============================================================

        // Event: Ouverture de l'Asset Manager → Préparer le scroll infini et précharger si vide
      editor.on('asset:open', async () => {
        console.log('[ASSET_MANAGER] Modal ouverte, initialisation scroll infini...');
        
        // Réinitialiser la pagination à chaque ouverture
        assetsPageRef.current = 1;
        assetsTotalPagesRef.current = null;
        
        const assetsContainer = document.querySelector('.gjs-am-assets');
        if (assetsContainer) {
          // Attacher le listener de scroll UNE seule fois, sans cloner/remplacer le noeud (préserve les handlers internes GrapesJS)
          if (!assetsContainer.getAttribute('data-scroll-listener-attached')) {
            assetsContainer.addEventListener('scroll', function() {
            const hasScrolledToBottom = this.scrollTop + this.clientHeight >= this.scrollHeight - 5;
            if (hasScrolledToBottom && !hasLoadedAllAssets()) {
              console.log('[ASSET_MANAGER] Scroll bas détecté, chargement page suivante...');
              loadNextAssetsPage(editor);
            }
          });
            assetsContainer.setAttribute('data-scroll-listener-attached', '1');
          }
        } else {
          console.warn('[ASSET_MANAGER] Container .gjs-am-assets non trouvé');
        }

        // Précharger immédiatement si aucune image présente dans le manager
        try {
          const current = editor.AssetManager.getAll();
          const hasAny = Array.isArray(current) ? current.length > 0 : (current?.length || current?.models?.length || 0) > 0;
          if (!hasAny) {
            console.log('[ASSET_MANAGER] Aucun asset présent, chargement initial à l\'ouverture...');
            const firstPage = await loadMauticAssets(1);
            if (firstPage && firstPage.length > 0) {
              editor.AssetManager.add(firstPage);
              try { editor.AssetManager.render(); } catch (_) {}
              console.log(`[ASSET_MANAGER] ${firstPage.length} assets chargés à l\'ouverture`);
            }
          }
        } catch (e) {
          console.warn('[ASSET_MANAGER] Préchargement à l\'ouverture échoué:', e);
        }
        });

        // Event: Suppression d'un asset → Supprimer de Mautic
      editor.on('asset:remove', async (asset) => {
        console.log('[ASSET_MANAGER] Suppression demandée pour asset:', asset);
        
        try {
          const assetId = asset.get('id');
          
          if (assetId) {
            const success = await deleteMauticAsset(assetId);
            if (success) {
              showNotification('Image supprimée avec succès', 'success');
            }
          } else {
            console.warn('[ASSET_MANAGER] Asset sans ID, impossible de supprimer de Mautic');
          }
        } catch (error) {
          console.error('[ASSET_MANAGER] Erreur suppression asset:', error);
        }
        });

        // Event: Erreur d'upload
        editor.on('asset:upload:error', (err) => { 
          console.error('[ASSET_MANAGER] Erreur upload:', err);
          showNotification("Erreur lors de l'upload de l'image", 'error'); 
        });

        // Event: Upload réussi → recharger/rafraîchir la liste des assets
        editor.on('asset:upload:response', async (response) => {
          try {
            console.log('[ASSET_MANAGER] Upload réussi:', response);
            // Après upload, on tente de récupérer les dernières images pour les ajouter si manquantes
            const freshAssets = await loadMauticAssets(1);
            if (Array.isArray(freshAssets) && freshAssets.length > 0) {
              const current = editor.AssetManager.getAll().map(a => a.get && a.get('src'));
              const missing = freshAssets.filter(a => !current.includes(a.src));
              if (missing.length > 0) editor.AssetManager.add(missing);
            }
          } catch (e) {
            console.warn('[ASSET_MANAGER] Rafraîchissement post-upload échoué', e);
          }
        });

      // ============================================================
      }; // Fin de la fonction initEditor

      // Appeler la fonction async
      initEditor().catch(err => {
        console.error('[GRAPESJS_INIT] Erreur initialisation:', err);
      });

    }

    // Cleanup si on quitte le mode manuel ou ferme

    return () => {

      cancelled = true;

      if (!open || isNaturalMode) {

        try {

          if (editorInstanceRef.current && editorInstanceRef.current.destroy) editorInstanceRef.current.destroy();

        } catch (_) {}

        editorInstanceRef.current = null;

      }

      try { if (mutationObserver) mutationObserver.disconnect(); } catch (_) {}

    };

  }, [open, isNaturalMode, editorType, currentStep, mode, email?.id, autoModeReady, editorStateLoading, editorStateError]);



  useEffect(() => {

    // Si contenu change côté IA/import, pousser dans Grapes si actif

    if (open && !isNaturalMode && editorInstanceRef.current) {

      try {

        updatingFromGrapesRef.current = true;

        editorInstanceRef.current.setComponents(editorContent || '');

        // Ré-injecter les styles <style> du contenu afin de garder les arrière-plans/bannières

        try {

          const frameEl = editorInstanceRef.current.Canvas.getFrameEl();

          const doc = frameEl && frameEl.contentDocument;

          if (doc) {

            // Nettoyer uniquement les styles injectés précédemment par nous (optionnel)

            // et ré-injecter ceux du contenu courant

            const parser = new DOMParser();

            const parsed = parser.parseFromString(editorContent || '', 'text/html');

            const styleTags = parsed.head ? parsed.head.querySelectorAll('style') : [];

            styleTags.forEach(tag => {

              const cloned = doc.createElement('style');

              cloned.type = 'text/css';

              cloned.textContent = tag.textContent || '';

              doc.head.appendChild(cloned);

            });

          }

        } catch (_) {}

      } catch (_) {}

      setTimeout(() => { updatingFromGrapesRef.current = false; }, 0);

    }

  }, [editorContent, open, isNaturalMode]);

  
  // ✅ Forcer la mise à jour de la preview quand editorContent change EN MODE IA
  // En mode Manuel, les listeners GrapesJS (component:update, etc.) gèrent déjà ça
  useEffect(() => {
    if (open && isNaturalMode) {
      // Forcer le re-render de la preview
      setPreviewKey(prev => prev + 1);
      console.log('[PREVIEW] Mise à jour de la preview en mode IA');
    }
  }, [editorContent, open, isNaturalMode]);


  // Synchroniser lastSavedHtmlRef ET editorContent quand le mode change pour éviter les faux positifs
  // MAIS SEULEMENT en mode Manuel (pas en mode IA où GrapesJS n'est pas actif)
  useEffect(() => {
    if (!open || isNaturalMode) return;

    // Attendre un peu que GrapesJS soit complètement initialisé en mode Manuel
    const timer = setTimeout(() => {
      try {
        // Synchroniser avec GrapesJS seulement en mode Manuel
        if (editorInstanceRef.current) {
          const htmlFromGrapes = getHtmlFromGrapes();
          lastSavedHtmlRef.current = htmlFromGrapes;
          setEditorContent(htmlFromGrapes);
          editorInitializedRef.current = true;
          hasUserChangesRef.current = false;
          console.log('[SYNC] lastSavedHtmlRef ET editorContent synchronisés depuis GrapesJS (mode Manuel)');
        }
      } catch (_) {}
    }, 300);

    return () => clearTimeout(timer);
  }, [isNaturalMode, open]);


  useEffect(() => {

    const handleKeyDown = (e) => {

      // Seulement si on est en mode manuel et que la modale est ouverte

      if (!open || isNaturalMode) return;

      

      // Ctrl+S : Sauvegarder

      if (e.ctrlKey && e.key === 's') {

        e.preventDefault();

        if (mode === 'edit' && email) {

          handleSave();

        }

      }

      

      // Ctrl+Z : Undo

      if (e.ctrlKey && e.key === 'z' && !e.shiftKey) {

        e.preventDefault();

        handleUndo();

      }

      

      // Ctrl+Y ou Ctrl+Shift+Z : Redo

      if ((e.ctrlKey && e.key === 'y') || (e.ctrlKey && e.shiftKey && e.key === 'Z')) {

        e.preventDefault();

        handleRedo();

      }

      

      // Ctrl+Shift+I : Insérer une image

      if (e.ctrlKey && e.shiftKey && e.key === 'I') {

        e.preventDefault();

        const input = document.createElement('input');

        input.type = 'file';

        input.accept = 'image/*';

        input.onchange = (e) => {

          const files = e.target && e.target.files;

          const file = files && files[0];

          if (file) {

            const formData = new FormData();

            formData.append('file', file);

            api.post('/emails/upload_image', formData, {

              headers: { 'Content-Type': 'multipart/form-data' }

            }).then(res => {

              if (res.data.success && res.data.url) {

                const imgHtml = `<img src="${res.data.url}" alt="Image" style="max-width: 100%; height: auto;" />`;

                if (!isNaturalMode && editorInstanceRef.current) {

                  try { editorInstanceRef.current.addComponents(imgHtml); } catch (_) {}

                } else {

                  try {

                    const newContent = (editorContent || '') + imgHtml;

                    addToHistory(editorContent);

                    setEditorContent(newContent);

                    notifyParentIfAllowed(newContent);

                  } catch (_) {}

                }

              }

            }).catch(() => showNotification('Erreur lors de l\'upload de l\'image', 'error'));

          }

        };

        input.click();

      }

    };



    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);

  }, [open, isNaturalMode, mode, email?.id]);

  

  // Handler pour ouvrir le menu d'import depuis la barre de prompt

  const openPromptImportMenu = (e) => setImportPromptAnchorEl(e.currentTarget);

  const closePromptImportMenu = () => setImportPromptAnchorEl(null);

  

  // Handler pour sélectionner un fichier HTML (amélioré)

  const handleImportHtmlFile = (e) => {

    const file = e.target.files && e.target.files[0];

    if (!file) return;

    

    console.log('[EMAIL_ASSISTANT] === DÉBUT handleImportHtmlFile ===');

    console.log('[EMAIL_ASSISTANT] Fichier sélectionné:', file.name, file.type);

    

    if (!(/\.(html?|HTML?)$/.test(file.name))) {

      setImportHtmlError('Veuillez sélectionner un fichier .html ou .htm');

      console.log('[EMAIL_ASSISTANT] Erreur: fichier non HTML');

      return;

    }

    if (file.size > 2 * 1024 * 1024) { // 2MB

      setImportHtmlError('Le fichier est trop volumineux (max 2 Mo).');

      console.log('[EMAIL_ASSISTANT] Erreur: fichier trop volumineux');

      return;

    }

    

    const reader = new FileReader();

    reader.onload = (evt) => {

      console.log('[EMAIL_ASSISTANT] Fichier HTML lu avec succès');

      const newContent = evt.target.result;

      // Stocker temporairement le contenu et ouvrir la modal de confirmation
      setPendingHtmlContent(newContent);
      setPendingHtmlFileName(file.name);
      setImportHtmlError('');
      setHtmlImportConfirmOpen(true);

      console.log('[EMAIL_ASSISTANT] Modal de confirmation ouverte');

    };

    reader.onerror = () => {

      console.error('[EMAIL_ASSISTANT] Erreur lecture fichier HTML');

      setImportHtmlError('Erreur lors de la lecture du fichier HTML.');

    };

    reader.readAsText(file);

    closePromptImportMenu();

    e.target.value = '';

    console.log('[EMAIL_ASSISTANT] === FIN handleImportHtmlFile ===');

  };

  

  // Handler pour sélectionner une image (amélioré)

  const handleImportImageFile = (e) => {

    const file = e.target.files && e.target.files[0];

    if (!file) return;

    

    console.log('[EMAIL_ASSISTANT] === DÉBUT handleImportImageFile ===');

    console.log('[EMAIL_ASSISTANT] Fichier image sélectionné:', file.name, file.type, file.size);

    

    // Vérification type

    if (!file.type.startsWith('image/')) {

      setImageUploadError('Veuillez sélectionner un fichier image.');

      console.log('[EMAIL_ASSISTANT] Erreur: fichier non image');

      return;

    }

    // Limite de taille (5 Mo)

    const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;

    if (file.size > MAX_IMAGE_SIZE_BYTES) {

      setImageUploadError('Image trop volumineuse (max 5 Mo).');

      console.log('[EMAIL_ASSISTANT] Erreur: image trop volumineuse');

      return;

    }

    

    setImageUploading(true);

    setImageUploadError('');

    

    const formData = new FormData();

    formData.append('file', file);

    // Ajouter le titre pour Mautic Asset Manager
    formData.append('title', file.name.replace(/\.[^/.]+$/, '')); // Nom sans extension

    

    console.log('[EMAIL_ASSISTANT] Upload image vers Mautic Asset Manager...');

    // ✅ AMÉLIORATION 2 : Upload vers Mautic (stockage permanent)
    api.post('/emails/mautic/assets', formData, {

      headers: {

        'Content-Type': 'multipart/form-data',

      },

    })

      .then(res => {

        console.log('[EMAIL_ASSISTANT] Réponse upload image Mautic:', res.data);

        const data = res.data;

        // Gérer la réponse Mautic (peut être data.asset ou data.assets)
        if (data.asset || data.assets) {

          const assetData = data.asset || (data.assets ? Object.values(data.assets)[0] : null);

          if (assetData) {

            // Construire l'URL avec resolveMauticAssetSrc
            const imageUrl = resolveMauticAssetSrc(

              assetData.downloadUrl || assetData.url || assetData.path || `/asset/${assetData.id}:${assetData.originalFileName}`

            );

            console.log('[EMAIL_ASSISTANT] Image uploadée vers Mautic avec succès:', imageUrl);

            // Si l'upload a été déclenché depuis l'éditeur de contenu dynamique,
            // on insère directement l'URL dans cet éditeur au lieu d'ouvrir la modale IA.
            if (dcImageInsertCallbackRef.current) {
              try {
                const cb = dcImageInsertCallbackRef.current;
                dcImageInsertCallbackRef.current = null;
                cb(imageUrl);
                showNotification('Image uploadée et insérée dans le contenu dynamique.', 'success');
              } catch (err) {
                console.error('[EMAIL_ASSISTANT] Erreur callback image dynamique:', err);
              }
            } else {
              setUploadedImageUrl(imageUrl);
              setImageUploadModalOpen(true);
              // ✅ Notification améliorée
              showNotification('Image uploadée et enregistrée dans votre bibliothèque !', 'success');
            }

          } else {

            setImageUploadError('Erreur: données asset manquantes');

            console.log('[EMAIL_ASSISTANT] Erreur: asset non trouvé dans la réponse');

          }

        } else {

          setImageUploadError(data.message || 'Erreur lors de l\'upload de l\'image.');

          console.log('[EMAIL_ASSISTANT] Erreur upload:', data.message);

        }

      })

      .catch(e => {

        console.error('[EMAIL_ASSISTANT] Erreur réseau upload image:', e);

        setImageUploadError('Erreur réseau lors de l\'upload.');

      })

      .finally(() => {

        setImageUploading(false);

        console.log('[EMAIL_ASSISTANT] Upload terminé');

      });

    

    closePromptImportMenu();

    e.target.value = '';

    console.log('[EMAIL_ASSISTANT] === FIN handleImportImageFile ===');

  };

  

  // Handler pour insérer l'image via l'IA

  const handleInsertImageWithPrompt = async () => {

    if (!imageInsertPrompt.trim() || !uploadedImageUrl) return;

    

    setLoadingAI(true);

    setImageUploadModalOpen(false);

    

    try {

      const res = await api.post('/emails/edit_with_ai', {

        prompt: imageInsertPrompt + ` (insérer l'image suivante : ${uploadedImageUrl})`,

        current_html: editorContent,

        instructions: chatMessages.filter(msg => msg.sender === 'user').map(msg => msg.text),

        image_url: uploadedImageUrl,

      }, { signal: requestControllerRef.current?.signal });

      

      const data = res.data;

      if (data.success && data.suggestion) {

        setChatMessages(prev => [...prev, { text: 'Image insérée dans l\'email ! (prévisualisation à jour)', sender: 'assistant' }]);

        addToHistory(editorContent); // sauvegarde l'ancien contenu

        setEditorContent(data.suggestion);

        hasUserChangesRef.current = true;

        notifyParentIfAllowed(data.suggestion);

      } else {

        // Fallback: insérer l'image même si l'IA ne renvoie pas de suggestion

        const imgHtml = `<img src="${uploadedImageUrl}" alt="Image" style="max-width: 100%; height: auto;" />`;

        let inserted = false;

        if (!isNaturalMode && editorInstanceRef.current) {

          try {

            editorInstanceRef.current.addComponents(imgHtml);

            inserted = true;

          } catch (_) {}

        }

        if (!inserted) {

          try {

            const newContent = (editorContent || '') + imgHtml;

            addToHistory(editorContent);

            setEditorContent(newContent);

            hasUserChangesRef.current = true;

            notifyParentIfAllowed(newContent);

            inserted = true;

          } catch (_) {}

        }

        setChatMessages(prev => [

          ...prev,

          { text: inserted ? "Image insérée (sans modification IA)." : "Erreur IA lors de l'insertion de l'image : " + (data.message || 'inconnue'), sender: 'assistant' }

        ]);

      }

    } catch (e) {

      // Fallback en cas d'erreur réseau: tenter d'insérer l'image localement

      const imgHtml = `<img src="${uploadedImageUrl}" alt="Image" style="max-width: 100%; height: auto;" />`;

      let inserted = false;

      if (!isNaturalMode && editorInstanceRef.current) {

        try { editorInstanceRef.current.addComponents(imgHtml); inserted = true; } catch (_) {}

      }

      if (!inserted) {

        try {

          const newContent = (editorContent || '') + imgHtml;

          addToHistory(editorContent);

          setEditorContent(newContent);

          hasUserChangesRef.current = true;

          notifyParentIfAllowed(newContent);

          inserted = true;

        } catch (_) {}

      }

      setChatMessages(prev => [

        ...prev,

        { text: inserted ? "Image insérée malgré l'erreur réseau IA." : "Erreur réseau ou serveur IA lors de l'insertion de l'image.", sender: 'assistant' }

      ]);

    }

    

    setLoadingAI(false);

    setUploadedImageUrl('');

    setImageInsertPrompt('');

  };

  
  // ========== GALERIE D'IMAGES EN MODE IA ==========

  // Charger les images depuis Mautic pour la galerie
  const loadGalleryImages = async (page = 1) => {
    if (loadingGallery) return;
    
    setLoadingGallery(true);
    
    try {
      console.log('[IMAGE_GALLERY] Chargement page:', page);
      const assets = await loadMauticAssets(page);
      
      if (assets && assets.length > 0) {
        if (page === 1) {
          setGalleryImages(assets);
        } else {
          setGalleryImages(prev => [...prev, ...assets]);
        }
        
        // Si on a reçu moins de 30 images, c'est qu'il n'y en a plus
        setGalleryHasMore(assets.length === 30);
        console.log('[IMAGE_GALLERY]', assets.length, 'images chargées');
      } else {
        setGalleryHasMore(false);
        console.log('[IMAGE_GALLERY] Aucune image supplémentaire');
      }
    } catch (error) {
      console.error('[IMAGE_GALLERY] Erreur chargement:', error);
      showNotification('Erreur lors du chargement de la galerie', 'error');
    } finally {
      setLoadingGallery(false);
    }
  };

  // Ouvrir la galerie
  const handleOpenGallery = () => {
    setImageGalleryOpen(true);
    setGalleryPage(1);
    // Charger les images si la galerie est vide
    if (galleryImages.length === 0) {
      loadGalleryImages(1);
    }
  };

  // Fermer la galerie
  const handleCloseGallery = () => {
    setImageGalleryOpen(false);
  };

  // Charger plus d'images (scroll infini)
  const handleLoadMoreGalleryImages = () => {
    if (!loadingGallery && galleryHasMore) {
      const nextPage = galleryPage + 1;
      setGalleryPage(nextPage);
      loadGalleryImages(nextPage);
    }
  };

  // Sélectionner une image de la galerie
  const handleSelectGalleryImage = (imageUrl) => {
    console.log('[IMAGE_GALLERY] Image sélectionnée:', imageUrl);

    // Priorité 1 : insertion dans un éditeur de contenu dynamique ouvert
    if (dcImageInsertCallbackRef.current) {
      try {
        const cb = dcImageInsertCallbackRef.current;
        dcImageInsertCallbackRef.current = null;
        cb(imageUrl);
      } catch (err) {
        console.error('[IMAGE_GALLERY] Erreur callback contenu dynamique:', err);
      }
      setImageGalleryOpen(false);
      return;
    }

    // Priorité 2 : sélection depuis les templates (header/footer)
    if (window.currentImageElement) {
      const { section, id } = window.currentImageElement;
      updateElementProps(section, id, { src: imageUrl });
      window.currentImageElement = null;
      setImageGalleryOpen(false);
      return;
    }

    // Priorité 3 : mode IA classique
    setUploadedImageUrl(imageUrl);
    setImageGalleryOpen(false);
    setImageUploadModalOpen(true);
  };

  
  // ========== INSERTION HTML INTELLIGENTE AVEC IA ==========

  // Handler pour insérer le HTML importé avec l'IA
  const handleInsertHtmlWithAI = async () => {
    if (!htmlInsertPrompt.trim() || !pendingHtmlContent) return;
    
    setIsInsertingHtml(true);
    setHtmlImportConfirmOpen(false);
    
    try {
      console.log('[HTML_IMPORT] Insertion intelligente avec IA...');
      const res = await api.post('/emails/edit_with_ai', {
        prompt: htmlInsertPrompt + ` (intégrer le contenu HTML suivant : ${pendingHtmlContent.substring(0, 500)}...)`,
        current_html: editorContent,
        instructions: chatMessages.filter(msg => msg.sender === 'user').map(msg => msg.text),
        html_content: pendingHtmlContent,
      }, { signal: requestControllerRef.current?.signal });
      
      const data = res.data;
      if (data.success && data.suggestion) {
        console.log('[HTML_IMPORT] Insertion IA réussie');
        
        // Sauvegarder l'ancien contenu et appliquer le nouveau
        addToHistory(editorContent);
        setEditorContent(data.suggestion);
        notifyParentIfAllowed(data.suggestion);

        hasUserChangesRef.current = true;
        
        // Extraire les métadonnées du HTML importé
        const { subject, preheader } = extractEmailMetaFromHtml(pendingHtmlContent);
        if (subject || preheader) {
          setFormData(prev => ({
            ...prev,
            internalName: prev.internalName || pendingHtmlFileName.replace(/\.(html|htm)$/i, ''),
            subject: prev.subject || subject,
            preheaderText: prev.preheaderText || preheader,
          }));
        }
        
        setChatMessages(prev => [...prev, { 
          text: `Le contenu HTML a été intégré intelligemment dans l'email !`, 
          sender: 'assistant' 
        }]);
        
        showNotification('HTML intégré avec succès !', 'success');
      } else {
        // Fallback: remplacer complètement
        console.log('[HTML_IMPORT] IA non disponible, remplacement complet');
        handleReplaceHtmlDirectly();
      }
    } catch (e) {
      console.error('[HTML_IMPORT] Erreur insertion IA:', e);
      
      // Fallback en cas d'erreur: demander si on veut remplacer
      setChatMessages(prev => [...prev, { 
        text: "Erreur lors de l'insertion intelligente. Le contenu sera remplacé complètement.", 
        sender: 'assistant' 
      }]);
      
      handleReplaceHtmlDirectly();
    } finally {
      setIsInsertingHtml(false);
      setPendingHtmlContent('');
      setPendingHtmlFileName('');
      setHtmlInsertPrompt('');
    }
  };

  // Handler pour remplacer directement le HTML (comportement original)
  const handleReplaceHtmlDirectly = () => {
    if (!pendingHtmlContent) return;
    
    console.log('[HTML_IMPORT] Remplacement complet du contenu');
    
    // Sauvegarder l'ancien contenu
    addToHistory(editorContent);
    
    // Remplacer complètement
    setEditorContent(pendingHtmlContent);
    notifyParentIfAllowed(pendingHtmlContent);

    hasUserChangesRef.current = true;
    
    // Extraire les métadonnées
    const { subject, preheader } = extractEmailMetaFromHtml(pendingHtmlContent);
    setFormData(prev => ({
      ...prev,
      internalName: prev.internalName || pendingHtmlFileName.replace(/\.(html|htm)$/i, ''),
      subject: prev.subject || subject,
      preheaderText: prev.preheaderText || preheader,
    }));
    
    setChatMessages(prev => [...prev, { 
      text: `Le contenu HTML importé a remplacé l'email. Sujet détecté: "${(subject || '—')}", préheader: "${(preheader || '—')}".`, 
      sender: 'assistant' 
    }]);
    
    showNotification('HTML importé avec succès !', 'success');
    
    // Fermer la modal et nettoyer
    setHtmlImportConfirmOpen(false);
    setPendingHtmlContent('');
    setPendingHtmlFileName('');
    setHtmlInsertPrompt('');
  };

  

  // Fonction utilitaire pour forcer target="_blank" sur tous les liens de l'aperçu (améliorée)

  const TRANSPARENT_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';



  const isPlaceholderUrl = (url) => {

    if (!url) return false;

    const u = String(url).trim();

    return u.startsWith('[') || u === '';

  };



  const sanitizePlaceholdersInHtml = (html) => {

    try {

      const parser = new window.DOMParser();

      const doc = parser.parseFromString(html || '', 'text/html');

      // Neutraliser liens placeholders

      const links = doc.querySelectorAll('a[href]');

      links.forEach(link => {

        const href = link.getAttribute('href') || '';

        if (isPlaceholderUrl(href)) link.setAttribute('href', '#');

      });

      // Images

      const imgs = doc.querySelectorAll('img[src]');

      imgs.forEach(img => {

        const src = img.getAttribute('src') || '';

        if (isPlaceholderUrl(src)) img.setAttribute('src', TRANSPARENT_PIXEL);

      });

      // Background images dans styles inline

      const styled = doc.querySelectorAll('[style]');

      styled.forEach(el => {

        const style = el.getAttribute('style') || '';

        const m = style.match(/background(?:-image)?\s*:\s*url\(([^)]+)\)/i);

        if (m) {

          const raw = m[1].replace(/^["']|["']$/g, '').trim();

          if (isPlaceholderUrl(raw)) {

            const cleaned = style.replace(/background(?:-image)?\s*:\s*url\([^)]+\)\s*;?/ig, '');

            el.setAttribute('style', cleaned);

          }

        }

      });

      // Conserver style du body en le transférant sur un wrapper

      const bodyEl = doc.body;

      const bodyStyle = bodyEl && bodyEl.getAttribute ? (bodyEl.getAttribute('style') || '') : '';

      const wrapper = doc.createElement('div');

      if (bodyStyle) wrapper.setAttribute('style', bodyStyle);

      while (bodyEl.firstChild) wrapper.appendChild(bodyEl.firstChild);

      bodyEl.appendChild(wrapper);

      return doc.body.innerHTML;

    } catch (_) {

      return html || '';

    }

  };



  const sanitizePlaceholdersInDoc = (doc) => {

    try {

      // Images avec src placeholder

      const imgs = doc.querySelectorAll('img[src]');

      imgs.forEach(img => {

        const src = img.getAttribute('src') || '';

        if (isPlaceholderUrl(src)) {

          img.setAttribute('data-src-original', src);

          img.setAttribute('src', TRANSPARENT_PIXEL);

        }

      });

      // Styles inline avec background / background-image

      const styled = doc.querySelectorAll('[style]');

      styled.forEach(el => {

        const style = el.getAttribute('style') || '';

        const m = style.match(/background(?:-image)?\s*:\s*url\(([^)]+)\)/i);

        if (m) {

          const raw = m[1].replace(/^["']|["']$/g, '').trim();

          if (isPlaceholderUrl(raw)) {

            el.setAttribute('data-bg-original', raw);

            // Retirer l'image de fond mais conserver les autres styles

            const cleaned = style.replace(/background(?:-image)?\s*:\s*url\([^)]+\)\s*;?/ig, '');

            el.setAttribute('style', cleaned);

          }

        }

      });

    } catch (_) {}

  };



  function addTargetBlankToLinks(html) {

    if (!html) return html;

    

    console.log('[EMAIL_ASSISTANT] addTargetBlankToLinks - HTML input (premiers 200 chars):', html.substring(0, 200));

    

    try {

      const parser = new window.DOMParser();

      const doc = parser.parseFromString(html, 'text/html');

      const links = doc.querySelectorAll('a[href]');

      

      console.log('[EMAIL_ASSISTANT] Liens trouvés:', links.length);

      

      links.forEach((link, index) => {

        const originalHref = link.getAttribute('href');

        // Neutraliser les href placeholders pour éviter toute requête

        if (isPlaceholderUrl(originalHref)) {

          link.setAttribute('href', '#');

        }

        link.setAttribute('target', '_blank');

        link.setAttribute('rel', 'noopener noreferrer');

        console.log(`[EMAIL_ASSISTANT] Lien ${index + 1} modifié: ${originalHref}`);

      });

      

      // Neutraliser les placeholders (img/background) pour l'affichage uniquement

      sanitizePlaceholdersInDoc(doc);



      // Conserver d'éventuels styles du head pour que l'aperçu manuel soit fidèle

      let headStyles = '';

      try {

        const styleTags = doc.head ? Array.from(doc.head.querySelectorAll('style')) : [];

        if (styleTags.length > 0) {

          headStyles = '<style>' + styleTags.map(s => s.innerHTML || '').join('\n') + '</style>';

        }

      } catch (_) {}

      // Préserver le fond/bannières en important les styles inline existants

      const bodyEl = doc.body;

      const bodyStyle = bodyEl && bodyEl.getAttribute ? (bodyEl.getAttribute('style') || '') : '';

      const wrapperStyle = bodyStyle ? ` style="${bodyStyle}"` : '';

      const bodyHtmlInner = (bodyEl && bodyEl.innerHTML) || html;

      const bodyHtml = `<div${wrapperStyle}>${bodyHtmlInner}</div>`;

      const result = headStyles + bodyHtml;

      console.log('[EMAIL_ASSISTANT] addTargetBlankToLinks - HTML output (premiers 200 chars):', result.substring(0, 200));

      

      return result;

    } catch (e) {

      console.error('[EMAIL_ASSISTANT] Erreur addTargetBlankToLinks:', e);

      return html;

    }

  }



  // Validation des conditions préalables

  const validateStep = (step) => {

    if (step === 0) return true; // Toujours accessible

    

    if (step === 1) {

      // Étape 1 accessible seulement si méthode AI et étape 0 validée

      if (selectedMethod !== 'ai') return false;

      if (selectedMethod === 'ai') {

        // Vérifier les champs requis pour AI

        return true; // Pour l'instant, on assume que c'est valide

      }

    }

    

    if (step === 2) {

      // Étape 2 accessible selon la méthode

      if (selectedMethod === 'ai') {

        return currentStep >= 1; // Doit passer par l'étape 1

      } else {

        return currentStep >= 0; // Peut venir directement de l'étape 0

      }

    }

    

    return false;

  };



  const handleStepClick = (targetStep) => {
    if (typeof targetStep !== 'number' || targetStep === currentStep) return;

    // Aller à l'étape 0: toujours autorisé, CONSERVER toutes les données
    if (targetStep === 0) {
      setCurrentStep(0);
      // ✅ PAS de réinitialisation - conserver internalName, subject, preheaderText, etc.
      // L'utilisateur veut juste revenir en arrière pour modifier quelque chose
      return;
    }

    // Aller à l'étape 1: uniquement pour la méthode IA et champs requis remplis
    if (targetStep === 1) {
      const internalName = (formData && typeof formData.internalName === 'string') ? formData.internalName : '';
      const subject = (formData && typeof formData.subject === 'string') ? formData.subject : '';
      const objective = (formData && typeof formData.objective === 'string') ? formData.objective : '';
      if (selectedMethod !== 'ai') {
        showNotification("L'étape Contenu IA est disponible uniquement en mode IA.", 'warning');
        return;
      }
      
      // Bloquer si validation en cours
      if (isValidatingName) {
        showNotification('Vérification du nom en cours... Veuillez patienter.', 'info');
        return;
      }
      
      // Vérifier si le nom existe déjà
      if (nameValidationError) {
        showNotification('Le nom interne est déjà utilisé. Veuillez en choisir un autre.', 'error');
        return;
      }
      
      if (!internalName.trim() || !subject.trim() || !objective.trim()) {
        showNotification("Veuillez remplir le nom interne, le sujet et l'objectif.", 'error');
        return;
      }
      // ✅ Conserver les suggestions générées et le contenu sélectionné
      // Pas de réinitialisation - l'utilisateur peut revenir à l'étape 1 pour choisir une autre suggestion
      setCurrentStep(1);
      return;
    }

    // Aller à l'étape 2: dépend de la méthode choisie
    if (targetStep === 2) {
      // ✅ TOUJOURS réinitialiser le mode à Manuel à l'arrivée à l'étape 2
      setIsNaturalMode(false);
      
      if (selectedMethod === 'ai') {
        const internalName = (formData && typeof formData.internalName === 'string') ? formData.internalName : '';
        const subject = (formData && typeof formData.subject === 'string') ? formData.subject : '';
        const objective = (formData && typeof formData.objective === 'string') ? formData.objective : '';
        
        // Bloquer si validation en cours
        if (isValidatingName) {
          showNotification('Vérification du nom en cours... Veuillez patienter.', 'info');
          return;
        }
        
        // Vérifier si le nom existe déjà
        if (nameValidationError) {
          showNotification('Le nom interne est déjà utilisé. Veuillez en choisir un autre.', 'error');
          return;
        }
        
        if (!internalName.trim() || !subject.trim() || !objective.trim()) {
          showNotification("Veuillez remplir le nom interne, le sujet et l'objectif.", 'error');
          return;
        }
        if (!generatedContent.trim()) {
          showNotification("Veuillez d'abord générer un contenu en sélectionnant une suggestion.", 'error');
          return;
        }
        // Assurer que le contenu généré est appliqué avant d'entrer en étape 2 (même logique que handleNext)
        try {
          setEditorContent(generatedContent);
          setChatMessages([
            {
              text: `Email "${selectedSuggestion?.name || selectedSuggestion?.subject}" généré avec succès ! Vous pouvez maintenant le personnaliser ou le sauvegarder.`,
              sender: 'assistant'
            }
          ]);
        } catch (_) {}
        // ✅ Conserver tout le contenu édité
        setCurrentStep(2);
        return;
      }

      if (selectedMethod === 'manual') {
        // En mode manuel, exiger les champs obligatoires avant de passer à l'étape 2
        const internalName = (formData && typeof formData.internalName === 'string') ? formData.internalName : '';
        const subject = (formData && typeof formData.subject === 'string') ? formData.subject : '';
        if (currentStep === 0) {
          
          // Bloquer si validation en cours
          if (isValidatingName) {
            showNotification('Vérification du nom en cours... Veuillez patienter.', 'info');
            return;
          }
          
          // Vérifier si le nom existe déjà
          if (nameValidationError) {
            showNotification('Le nom interne est déjà utilisé. Veuillez en choisir un autre.', 'error');
            return;
          }
          
          if (!internalName.trim() || !subject.trim()) {
            showNotification('Veuillez remplir le nom interne et le sujet.', 'error');
            return;
          }
          try { handleManualCreation(); } catch (_) {}
        }
        // ✅ Conserver tout le contenu édité
        setCurrentStep(2);
        return;
      }

      if (selectedMethod === 'import') {
        if (currentStep === 0) {
          const internalName = (formData && typeof formData.internalName === 'string') ? formData.internalName : '';
          const subject = (formData && typeof formData.subject === 'string') ? formData.subject : '';
          const htmlFile = formData && formData.htmlFile;
          
          // Bloquer si validation en cours
          if (isValidatingName) {
            showNotification('Vérification du nom en cours... Veuillez patienter.', 'info');
            return;
          }
          
          // Vérifier si le nom existe déjà
          if (nameValidationError) {
            showNotification('Le nom interne est déjà utilisé. Veuillez en choisir un autre.', 'error');
            return;
          }
          
          // Vérifier que tous les champs sont remplis
          if (!internalName.trim() || !subject.trim()) {
            showNotification('Veuillez remplir le nom interne et le sujet.', 'error');
            return;
          }
          
          if (!htmlFile) {
            showNotification('Veuillez sélectionner un fichier HTML à importer.', 'error');
            return;
          }
          
          try { 
            handleImportCreation(); 
            // ✅ Le passage à l'étape 2 est maintenant géré dans handleImportCreation
            // après que le fichier soit complètement lu (ligne 1462)
          } catch (_) {}
          return;
        }
        // Si on est déjà à l'étape 2, simplement y rester
        setCurrentStep(2);
        return;
      }
    }
  };



  const renderStepContent = () => {

    switch (currentStep) {

      case 0:

        return (

          <Box sx={{ maxWidth: '800px', mx: 'auto', p: 4 }}>

            <Typography variant="h5" sx={{ mb: 4, fontWeight: 500, color: '#334155' }}>

              1. Choisissez votre méthode

            </Typography>

            

            {/* Radio cards */}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3, mb: 4 }}>

              {/* IA */}

              <Box 

                onClick={() => setSelectedMethod('ai')}

                sx={{

                  position: 'relative',

                  p: 3,

                  border: selectedMethod === 'ai' ? '2px solid #007B8A' : '1px solid #E2E8F0',

                  borderRadius: 3,

                  cursor: 'pointer',

                  transition: 'all 0.2s',

                  bgcolor: selectedMethod === 'ai' ? 'rgba(0, 123, 138, 0.05)' : 'white',

                  '&:hover': { borderColor: '#007B8A' },

                  textAlign: 'center'

                }}

              >

                <input type="radio" name="mode" value="ai" checked={selectedMethod === 'ai'} style={{ display: 'none' }} readOnly />

                <Memory sx={{ fontSize: 28, color: '#007B8A', mb: 1 }} />

                <Typography sx={{ fontWeight: 500, color: '#334155' }}>Créer avec l'IA</Typography>

              </Box>



              {/* Manuel */}

              <Box 

                onClick={() => setSelectedMethod('manual')}

                sx={{

                  position: 'relative',

                  p: 3,

                  border: selectedMethod === 'manual' ? '2px solid #007B8A' : '1px solid #E2E8F0',

                  borderRadius: 3,

                  cursor: 'pointer',

                  transition: 'all 0.2s',

                  bgcolor: selectedMethod === 'manual' ? 'rgba(0, 123, 138, 0.05)' : 'white',

                  '&:hover': { borderColor: '#007B8A' },

                  textAlign: 'center'

                }}

              >

                <input type="radio" name="mode" value="manual" checked={selectedMethod === 'manual'} style={{ display: 'none' }} readOnly />

                <Edit sx={{ fontSize: 28, color: '#007B8A', mb: 1 }} />

                <Typography sx={{ fontWeight: 500, color: '#334155' }}>Créer manuellement</Typography>

              </Box>



              {/* Import */}

              <Box 

                onClick={() => setSelectedMethod('import')}

                sx={{

                  position: 'relative',

                  p: 3,

                  border: selectedMethod === 'import' ? '2px solid #007B8A' : '1px solid #E2E8F0',

                  borderRadius: 3,

                  cursor: 'pointer',

                  transition: 'all 0.2s',

                  bgcolor: selectedMethod === 'import' ? 'rgba(0, 123, 138, 0.05)' : 'white',

                  '&:hover': { borderColor: '#007B8A' },

                  textAlign: 'center'

                }}

              >

                <input type="radio" name="mode" value="import" checked={selectedMethod === 'import'} style={{ display: 'none' }} readOnly />

                <Memory sx={{ fontSize: 28, color: '#007B8A', mb: 1 }} />

                <Typography sx={{ fontWeight: 500, color: '#334155' }}>Importer HTML</Typography>

              </Box>

            </Box>



            {/* Sélection de template */}
            {(selectedMethod === 'ai' || selectedMethod === 'manual') && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: '#334155' }}>
                  Choisissez un template de départ
                </Typography>
                
                {loadingTemplates ? (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3, maxHeight: 500, overflowY: 'auto', pb: 2 }}>
                    {/* Starter template par défaut */}
                    <Box
                      onClick={() => setSelectedTemplate(null)}
                      sx={{
                        p: 2,
                        border: !selectedTemplate ? '2px solid #007B8A' : '1px solid #E2E8F0',
                        borderRadius: 2,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        bgcolor: !selectedTemplate ? 'rgba(0, 123, 138, 0.05)' : 'white',
                        '&:hover': { borderColor: '#007B8A' },
                        minHeight: '120px',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative'
                      }}
                    >
                      <Typography sx={{ fontWeight: 500, color: '#334155', mb: 0.5 }}>🚀 Starter template</Typography>
                      <Typography variant="caption" sx={{ color: '#64748B', textAlign: 'center', mb: 1 }}>Template de base avec header et footer</Typography>
                      <Box sx={{ mt: 'auto', display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                        <Button
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation(); // Empêcher la sélection du template
                            // Créer un objet template fictif pour la prévisualisation
                            const starterTemplate = {
                              id: 'starter',
                              name: 'Starter template',
                              type: 'mjml',
                              content: generateBaseTemplate()
                            };
                            handlePreviewTemplate(starterTemplate);
                          }}
                          sx={{ 
                            textTransform: 'none', 
                            fontSize: '11px', 
                            minWidth: 'auto',
                            px: 1,
                            py: 0.5
                          }}
                        >
                          👁️ Prévisualiser
                        </Button>
                      </Box>
                    </Box>

                    {/* Templates utilisateur */}
                    {userTemplates.map((template) => (
                      <Box
                        key={template.id}
                        onClick={() => setSelectedTemplate(template)}
                        sx={{
                          p: 2,
                          border: selectedTemplate?.id === template.id ? '2px solid #007B8A' : '1px solid #E2E8F0',
                          borderRadius: 2,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          bgcolor: selectedTemplate?.id === template.id ? 'rgba(0, 123, 138, 0.05)' : 'white',
                          '&:hover': { borderColor: '#007B8A' },
                          minHeight: '120px',
                          display: 'flex',
                          flexDirection: 'column',
                          position: 'relative'
                        }}
                      >
                        {template.is_default && (
                          <Box sx={{ position: 'absolute', top: 8, right: 8, bgcolor: '#007B8A', color: 'white', px: 1, py: 0.5, borderRadius: 1, fontSize: '10px' }}>
                            Par défaut
                          </Box>
                        )}
                        <Typography sx={{ fontWeight: 500, color: '#334155', mb: 0.5, fontSize: '14px' }}>
                          {template.name}
                        </Typography>
                        {template.description && (
                          <Typography variant="caption" sx={{ color: '#64748B', mb: 1, fontSize: '12px' }}>
                            {template.description.length > 60 ? template.description.substring(0, 60) + '...' : template.description}
                          </Typography>
                        )}
                        <Box sx={{ mt: 'auto', display: 'flex', gap: 1, justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" sx={{ color: '#64748B', fontSize: '11px' }}>
                            {template.type.toUpperCase()}
                          </Typography>
                          <Button
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation(); // Empêcher la sélection du template
                              handlePreviewTemplate(template);
                            }}
                            sx={{ 
                              textTransform: 'none', 
                              fontSize: '11px', 
                              minWidth: 'auto',
                              px: 1,
                              py: 0.5
                            }}
                          >
                            👁️ Prévisualiser
                          </Button>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}

                <Button
                  size="small"
                  onClick={() => setTemplateManagerOpen(true)}
                  sx={{ textTransform: 'none', color: '#007B8A' }}
                >
                  + Gérer mes templates
                </Button>
              </Box>
            )}

            {/* Formulaires conditionnels */}

            {/* ✅ Formulaire unifié pour IA et Manuel */}
            {(selectedMethod === 'ai' || selectedMethod === 'manual') && (

              <Box sx={{ display: (selectedMethod === 'ai' || selectedMethod === 'manual') ? 'block' : 'none' }}>

                <TextField

                  fullWidth

                  label="Internal Name *"

                  variant="outlined"

                  size="small"

                  value={formData.internalName}

                  onChange={(e) => {
                    const newName = e.target.value;
                    setFormData({ ...formData, internalName: newName });
                    
                    // Déclencher la validation avec debounce
                    if (newName.trim()) {
                      validateInternalName(newName);
                    } else {
                      // ✅ Annuler tout timeout en attente quand le champ devient vide
                      if (validationTimeoutRef.current) {
                        clearTimeout(validationTimeoutRef.current);
                        validationTimeoutRef.current = null;
                      }
                      setNameValidationError('');
                      setNameValidationSuccess(false);
                      setNameValidationWarning('');
                      setIsValidatingName(false);
                    }
                  }}

                  error={!!nameValidationError}

                  helperText={
                    isValidatingName 
                      ? "Vérification de la disponibilité du nom..." 
                      : nameValidationError 
                        ? nameValidationError
                        : nameValidationWarning
                          ? nameValidationWarning
                          : nameValidationSuccess
                            ? "✓ Ce nom est disponible"
                            : "Nom unique pour identifier cet email dans Mautic"
                  }

                  FormHelperTextProps={{
                    sx: {
                      color: nameValidationSuccess ? '#4caf50' : nameValidationWarning ? '#ff9800' : undefined
                    }
                  }}

                  sx={{ mb: 2.5 }}

                  placeholder="Nom interne de l'email"

                  InputProps={{
                    endAdornment: isValidatingName ? (
                      <CircularProgress size={20} sx={{ color: '#007B8A' }} />
                    ) : nameValidationSuccess ? (
                      <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                    ) : null
                  }}

                />

                <TextField

                  fullWidth

                  label="Subject *"

                  variant="outlined"

                  size="small"

                  value={formData.subject}

                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}

                  sx={{ mb: 2.5 }}

                  placeholder="Objet de l'email"

                />

                <TextField

                  fullWidth

                  label="Preheader Text"

                  variant="outlined"

                  size="small"

                  value={formData.preheaderText}

                  onChange={(e) => setFormData({ ...formData, preheaderText: e.target.value })}

                  placeholder="Texte de prévisualisation"

                  helperText="Court résumé qui suit l'objet dans la boîte de réception (idéalement entre 85 et 100 caractères)"

                  sx={{ mb: selectedMethod === 'ai' ? 2.5 : 0 }}

                />

                {/* ✅ Champ Objectif uniquement pour la méthode IA */}
                {selectedMethod === 'ai' && (
                  <TextField

                    fullWidth

                    label="Objectif de l'email *"

                    variant="outlined"

                    size="small"

                    multiline

                    rows={3}

                    value={formData.objective}

                    onChange={(e) => setFormData({ ...formData, objective: e.target.value })}

                    placeholder="Décrivez l'objectif de cet email (ex: Promouvoir le lancement d'un nouveau produit, annoncer une offre spéciale, etc.)"

                    helperText="Donnez une description claire de ce que vous voulez accomplir avec cet email"

                  />
                )}

              </Box>

            )}



            {selectedMethod === 'import' && (

              <Box sx={{ display: selectedMethod === 'import' ? 'block' : 'none' }}>

                {/* ✅ Champs communs pour Import : Internal Name, Subject, Preheader */}
                <TextField

                  fullWidth

                  label="Internal Name *"

                  variant="outlined"

                  size="small"

                  value={formData.internalName}

                  onChange={(e) => {
                    const newName = e.target.value;
                    setFormData({ ...formData, internalName: newName });
                    
                    // Déclencher la validation avec debounce
                    if (newName.trim()) {
                      validateInternalName(newName);
                    } else {
                      // ✅ Annuler tout timeout en attente quand le champ devient vide
                      if (validationTimeoutRef.current) {
                        clearTimeout(validationTimeoutRef.current);
                        validationTimeoutRef.current = null;
                      }
                      setNameValidationError('');
                      setNameValidationSuccess(false);
                      setNameValidationWarning('');
                      setIsValidatingName(false);
                    }
                  }}

                  error={!!nameValidationError}

                  helperText={
                    isValidatingName 
                      ? "Vérification de la disponibilité du nom..." 
                      : nameValidationError 
                        ? nameValidationError
                        : nameValidationWarning
                          ? nameValidationWarning
                          : nameValidationSuccess
                            ? "✓ Ce nom est disponible"
                            : "Nom unique pour identifier cet email dans Mautic"
                  }

                  FormHelperTextProps={{
                    sx: {
                      color: nameValidationSuccess ? '#4caf50' : nameValidationWarning ? '#ff9800' : undefined
                    }
                  }}

                  sx={{ mb: 2.5 }}

                  placeholder="Nom interne de l'email"

                  InputProps={{
                    endAdornment: isValidatingName ? (
                      <CircularProgress size={20} sx={{ color: '#007B8A' }} />
                    ) : nameValidationSuccess ? (
                      <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                    ) : null
                  }}

                />

                <TextField

                  fullWidth

                  label="Subject *"

                  variant="outlined"

                  size="small"

                  value={formData.subject}

                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}

                  sx={{ mb: 2.5 }}

                  placeholder="Objet de l'email"

                />

                <TextField

                  fullWidth

                  label="Preheader Text"

                  variant="outlined"

                  size="small"

                  value={formData.preheaderText}

                  onChange={(e) => setFormData({ ...formData, preheaderText: e.target.value })}

                  placeholder="Texte de prévisualisation"

                  helperText="Court résumé qui suit l'objet dans la boîte de réception"

                  sx={{ mb: 2.5 }}

                />

                <Typography variant="body2" sx={{ mb: 2, color: '#64748B', fontWeight: 500 }}>

                  Fichier HTML à importer

                </Typography>

                <Button

                  variant="contained"

                  component="label"

                  startIcon={<Upload />}

                  sx={{ 

                    bgcolor: '#007B8A', 

                    '&:hover': { bgcolor: '#006B75' },

                    textTransform: 'none',

                    fontWeight: 500

                  }}

                >

                  {formData.htmlFile ? formData.htmlFile.name : 'Choisir un fichier'}

                  <input 

                    type="file" 

                    hidden 

                    accept=".html"

                    onChange={(e) => {
                      const file = e.target.files[0];
                      setFormData({ 
                        ...formData, 
                        htmlFile: file,
                        // Pré-remplir le nom interne avec le nom du fichier si vide
                        internalName: formData.internalName || (file?.name?.replace(/\.(html|htm)$/i, '') || '')
                      });
                      
                      // Déclencher la validation si un nom a été pré-rempli
                      if (!formData.internalName && file?.name) {
                        const fileName = file.name.replace(/\.(html|htm)$/i, '');
                        if (fileName.trim()) {
                          validateInternalName(fileName);
                        }
                      }
                    }}

                  />

                </Button>

              </Box>

            )}

          </Box>

        );



      case 1:

        return (

          <Box sx={{ maxWidth: '800px', mx: 'auto', p: 4 }}>

            <Typography variant="h5" sx={{ mb: 4, fontWeight: 500, color: '#334155' }}>

              2. Génération IA

            </Typography>

            {/* ✅ Afficher les informations saisies */}
            <Box sx={{ mb: 4, p: 3, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#334155' }}>
                Informations de votre email
              </Typography>
              <Box sx={{ display: 'grid', gap: 1.5 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500 }}>Nom interne :</Typography>
                  <Typography variant="body2" sx={{ color: '#334155' }}>{formData.internalName}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500 }}>Sujet :</Typography>
                  <Typography variant="body2" sx={{ color: '#334155' }}>{formData.subject}</Typography>
                </Box>
                {formData.preheaderText && (
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500 }}>Preheader :</Typography>
                    <Typography variant="body2" sx={{ color: '#334155' }}>{formData.preheaderText}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500 }}>Objectif :</Typography>
                  <Typography variant="body2" sx={{ color: '#334155' }}>{formData.objective}</Typography>
                </Box>
              </Box>
            </Box>

            

            <Box sx={{ mb: 4 }}>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>

                <Typography variant="body1" sx={{ color: '#64748B', fontWeight: 500 }}>

                  Suggestions d'emails générées par l'IA

              </Typography>

                <Button

                  variant="contained"

                  onClick={generateEmailSuggestions}

                  disabled={isLoadingSuggestions || !formData.internalName.trim() || !formData.subject.trim() || !formData.objective.trim()}

                  sx={{

                    bgcolor: '#007B8A',

                    '&:hover': { bgcolor: '#006B75' },

                    textTransform: 'none',

                    fontWeight: 500

                  }}

                >

                  {isLoadingSuggestions ? 'Génération...' : 'Générer des suggestions'}

                </Button>

              </Box>

              

              {!formData.internalName.trim() || !formData.subject.trim() || !formData.objective.trim() ? (

                <Typography variant="body2" color="error" sx={{ mb: 2 }}>

                  Veuillez d'abord remplir le nom interne, le sujet et l'objectif pour générer des suggestions.

                </Typography>

              ) : null}

              

              <Box sx={{ mb: 4 }}>

                {emailSuggestions.length > 0 ? (

                  emailSuggestions.map((suggestion, index) => (

                  <Box 

                    key={index} 

                      onClick={() => handleSuggestionSelect(suggestion)}

                    sx={{ 

                      p: 2.5, 

                      mb: 2, 

                      cursor: 'pointer', 

                        border: selectedSuggestion === suggestion ? '2px solid #007B8A' : '1px solid #E2E8F0',

                      borderRadius: 2,

                        bgcolor: selectedSuggestion === suggestion ? 'rgba(0, 123, 138, 0.05)' : 'white',

                      '&:hover': { bgcolor: '#F8FAFC', borderColor: '#007B8A' },

                      transition: 'all 0.2s'

                    }}

                  >

                    <Typography variant="body1" sx={{ fontWeight: 500, color: '#334155', mb: 0.5 }}>

                        {suggestion.name || suggestion.subject}

                    </Typography>

                      <Typography variant="body2" sx={{ color: '#64748B', mb: 1 }}>

                        <strong>Sujet:</strong> {suggestion.subject}

                    </Typography>

                      <Typography variant="body2" sx={{ color: '#64748B', mb: 1 }}>

                        <strong>Pré-header:</strong> {suggestion.preheader}

                      </Typography>

                      {suggestion.description && (

                        <Typography variant="body2" sx={{ color: '#64748B', mb: 1 }}>

                          <strong>Description:</strong> {suggestion.description}

                        </Typography>

                      )}

                      {selectedSuggestion === suggestion && (

                        <Typography variant="caption" sx={{ color: '#007B8A', fontWeight: 500, display: 'block', mt: 1 }}>

                          ✓ Sélectionné

                        </Typography>

                      )}

                  </Box>

                  ))

                ) : (

                  <Box sx={{ 

                    p: 3, 

                    textAlign: 'center', 

                    border: '1px dashed #E2E8F0', 

                    borderRadius: 2,

                    bgcolor: '#F8FAFC'

                  }}>

                    <Typography variant="body2" color="#64748B">

                      Cliquez sur "Générer des suggestions" pour voir les propositions d'emails

                    </Typography>

                  </Box>

                )}

              </Box>

            </Box>



            <Box sx={{ mb: 3 }}>

              <Typography variant="body1" sx={{ mb: 2, color: '#64748B', fontWeight: 500 }}>

                Corps de l'email (aperçu)

              </Typography>

              <Box 

                sx={{ 

                  height: 360, 

                  border: '1px solid #E2E8F0', 

                  borderRadius: 2, 

                  bgcolor: 'white',

                  overflow: 'auto',

                  p: 2

                }}

              >

                {isGenerating ? (

                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>

                    <CircularProgress size={40} sx={{ color: '#007B8A', mb: 2 }} />

                    <Typography variant="body2" sx={{ color: '#64748B' }}>

                      Génération de l'email en cours...

                    </Typography>

                  </Box>

                ) : generatedContent ? (

                  <div 

                    key={previewKey}

                    dangerouslySetInnerHTML={{ __html: addTargetBlankToLinks(getPreviewHtml()) }}

                    style={{ 

                      fontFamily: 'Arial, sans-serif',

                      fontSize: '14px',

                      lineHeight: '1.6',

                      color: '#333'

                    }}

                  />

                ) : (

                  <Box sx={{ 

                  display: 'flex',

                  alignItems: 'center',

                  justifyContent: 'center',

                    height: '100%',

                    color: '#94A3B8',

                    textAlign: 'center'

                  }}>

                    <Typography variant="body2">

                      Sélectionnez une suggestion ci-dessus pour générer l'email

                    </Typography>

                  </Box>

                )}

              </Box>

            </Box>

          </Box>

        );



      case 2: {

        const ModernSwitch = ({ checked, onChange, id, disabled }) => (

          <Box

            component="label"

            htmlFor={id}

            sx={{

              position: 'relative',

              display: 'inline-flex',

              alignItems: 'center',

              cursor: 'pointer',

              userSelect: 'none',

              background: '#f8fafc',

              borderRadius: '12px',

              padding: '6px',

              minWidth: '200px',

              height: '48px',

              border: '1px solid #E2E8F0',

              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',

              transition: 'all 0.3s ease',

              '&:hover': { 

                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',

                transform: 'translateY(-1px)'

              },

            }}

          >

            <input

              type="checkbox"

              id={id}

              checked={checked}

              onChange={disabled ? undefined : onChange}

              disabled={disabled}

              style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}

            />

            <Box

              sx={{

                position: 'absolute',

                width: 'calc(50% - 6px)',

                height: 'calc(100% - 12px)',

                background: checked ? '#f3e5f5' : '#e3f2fd',

                borderRadius: '8px',

                left: '6px',

                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',

                border: `1px solid ${checked ? '#9c27b0' : '#2196f3'}`,

                zIndex: 5,

                transform: checked ? 'translateX(calc(100% + 2px))' : 'translateX(0)',

              }}

            />

            <Box

              sx={{

                position: 'relative',

                width: '100%',

                height: '100%',

                display: 'flex',

                justifyContent: 'space-around',

                alignItems: 'center',

                zIndex: 10,

                pointerEvents: 'none',

              }}

            >

              <Box sx={{ 

                display: 'flex', 

                alignItems: 'center', 

                gap: 1,

                px: 1,

                py: 0.5,

                borderRadius: 1,

                transition: 'all 0.3s ease',

                background: !checked ? 'rgba(33, 150, 243, 0.1)' : 'transparent',

              }}>

                <Typography sx={{ fontSize: '16px' }}>🤖</Typography>

              <Typography

                component="span"

                sx={{

                    fontWeight: 600,

                    fontSize: '12px',

                  transition: 'color 0.3s ease',

                    color: !checked ? '#1976d2' : '#5E6C84',

                }}

              >

                  IA

              </Typography>

              </Box>

              <Box sx={{ 

                display: 'flex', 

                alignItems: 'center', 

                gap: 1,

                px: 1,

                py: 0.5,

                borderRadius: 1,

                transition: 'all 0.3s ease',

                background: checked ? 'rgba(156, 39, 176, 0.1)' : 'transparent',

              }}>

                <Typography sx={{ fontSize: '16px' }}>✏️</Typography>

              <Typography

                component="span"

                sx={{

                    fontWeight: 600,

                    fontSize: '12px',

                  transition: 'color 0.3s ease',

                    color: checked ? '#7b1fa2' : '#5E6C84',

                }}

              >

                  Manuel

              </Typography>

              </Box>

            </Box>

          </Box>

        );



        return (

          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 3 }}>

            <Typography variant="h6" sx={{ fontWeight: 500, color: '#334155', mb: 3 }}>3. Éditer votre e-mail</Typography>

            

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', gap: isNaturalMode ? 4 : 0, height: '100%', overflow: 'hidden' }}>

              {/* Panneau gauche : contenu d'édition (plein écran en mode Manuel) */}

               <Box sx={{ flex: isNaturalMode ? 1 : 'auto', width: isNaturalMode ? 'auto' : '100%', height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>

                {/* Toggle IA / Manuel réactivé */}

                <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 2 }}>

                  <Tooltip

                    title={isManualInitInProgress ? "L'éditeur manuel est en cours d'initialisation..." : "Basculer entre IA et mode manuel"}

                    arrow

                  >

                    <span>

                      <ModernSwitch

                        id="editor-mode-toggle"

                        checked={!isNaturalMode}

                        onChange={() => attemptSwitchMode(isNaturalMode)}

                        disabled={isManualInitInProgress}

                      />

                    </span>

                  </Tooltip>

                </Box>



                {/* Conteneur pour les panneaux d'édition */}

                <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>

                  {isNaturalMode ? (

                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', border: '1px solid #E2E8F0', borderRadius: 2, overflow: 'hidden' }}>

                      <Box sx={{ flex: 1, minHeight: 0, p: 2, overflowY: 'auto', bgcolor: '#F8FAFC' }}>

                        {chatMessages.map((msg, index) => (

                          <Box key={index} sx={{ mb: 2, display: 'flex', justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start' }}>

                            <Paper elevation={0} sx={{

                              p: '10px 14px',

                              borderRadius: '16px',

                              maxWidth: '75%',

                              bgcolor: msg.sender === 'user' ? '#007B8A' : 'white',

                              color: msg.sender === 'user' ? 'white' : '#334155',

                              border: msg.sender === 'assistant' ? '1px solid #E2E8F0' : 'none'

                            }}>

                              <Typography variant="body2">{msg.text}</Typography>

                            </Paper>

                          </Box>

                        ))}

                        <div ref={chatEndRef} />

                      </Box>

                      <Box sx={{ p: 2, borderTop: '1px solid #E2E8F0', bgcolor: 'white' }}>

                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>

                          {/* Icône d'import à gauche de la barre de prompt */}

                          <Tooltip title="Importer un fichier">

                            <span>

                              <IconButton

                                onClick={openPromptImportMenu}

                                sx={{ borderRadius: '50%', border: '2px solid #6f42c1', color: '#6f42c1', bgcolor: 'white', '&:hover': { bgcolor: '#f3eaff' } }}

                              >

                                <AttachFile />

                              </IconButton>

                            </span>

                          </Tooltip>

                          {/* Gestion du contenu dynamique Mautic en mode IA */}

                          <Tooltip title="Configurer le contenu dynamique">

                            <span>

                              <Button

                                variant="outlined"

                                size="small"

                                onClick={handleOpenDynamicContentFromAi}

                                disabled={loadingAI || imageUploading}

                                sx={{

                                  textTransform: 'none',

                                  borderRadius: '20px',

                                  borderColor: '#4B5563',

                                  color: '#111827',

                                  '&:hover': { borderColor: '#111827', bgcolor: '#F3F4F6' },

                                }}

                              >

                                ⚙️ Contenu dynamique

                              </Button>

                            </span>

                          </Tooltip>

                          <Menu

                            anchorEl={dcFromAiAnchorEl}

                            open={Boolean(dcFromAiAnchorEl)}

                            onClose={handleCloseDynamicContentFromAiMenu}

                            PaperProps={{
                              sx: {
                                maxHeight: 320,
                                overflowY: 'auto',
                              },
                            }}

                          >

                            <MenuItem onClick={handleCreateNewDynamicSlotFromAi}>

                              Créer un nouveau contenu dynamique

                            </MenuItem>

                            {Array.isArray(emailDynamicContent) && emailDynamicContent.length > 0 && (

                              <>

                                <Divider />

                                {emailDynamicContent.map((slot, index) => (

                                  <MenuItem

                                    key={slot.tokenName || index}

                                    onClick={() => handleEditDynamicSlotFromAi(slot)}

                                  >

                                    Éditer {slot.tokenName || `slot ${index + 1}`}

                                  </MenuItem>

                                ))}

                              </>

                            )}

                          </Menu>

                          <Menu anchorEl={importPromptAnchorEl} open={Boolean(importPromptAnchorEl)} onClose={closePromptImportMenu}>

                            {/* Import HTML uniquement en mode EDIT */}
                            {mode === 'edit' && (
                              <MenuItem onClick={() => { if (promptFileInputRef.current) { promptFileInputRef.current.setAttribute('accept', '.html,.htm'); promptFileInputRef.current.click(); } }}>Importer un fichier HTML</MenuItem>
                            )}

                            <MenuItem onClick={() => { if (promptFileInputRef.current) { promptFileInputRef.current.setAttribute('accept', 'image/*'); promptFileInputRef.current.click(); } }}>Importer une nouvelle image</MenuItem>

                            <MenuItem onClick={() => { closePromptImportMenu(); handleOpenGallery(); }}>Bibliothèque d'images</MenuItem>

                          </Menu>

                          {/* Input caché pour l'import de fichier HTML ou image */}

                          <input

                            ref={promptFileInputRef}

                            type="file"

                            accept=".html,.htm, image/*"

                            style={{ display: 'none' }}

                            onChange={(e) => {

                              const file = e.target.files && e.target.files[0];

                              if (!file) return;

                              if (/(\.(html?|HTML?))$/.test(file.name)) {

                                handleImportHtmlFile(e);

                                if (promptFileInputRef.current) promptFileInputRef.current.setAttribute('accept', '.html,.htm, image/*');

                              } else if (file.type.startsWith('image/')) {

                                handleImportImageFile(e);

                                if (promptFileInputRef.current) promptFileInputRef.current.setAttribute('accept', '.html,.htm, image/*');

                              }

                            }}

                          />

                          {/* Affichage d'une erreur d'import HTML ou image si besoin */}

                          {(importHtmlError || imageUploadError) && (

                            <Typography variant="caption" color="error" sx={{ ml: 2 }}>{importHtmlError || imageUploadError}</Typography>

                          )}

                          <TextField

                            fullWidth

                            multiline

                            maxRows={5}

                            variant="outlined"

                            placeholder="Décrivez votre modification... (Maj+Entrée pour une nouvelle ligne)"

                            value={chatInput}

                            onChange={(e) => setChatInput(e.target.value)}

                            onKeyDown={(e) => {

                              if (e.key === 'Enter' && !e.shiftKey) {

                                e.preventDefault();

                                handleSendMessage();

                              }

                            }}

                            sx={{

                              '& .MuiOutlinedInput-root': {

                                borderRadius: '20px',

                                padding: '10px 15px',

                                '& fieldset': {

                                  borderColor: '#ccc',

                                },

                                '&:hover fieldset': {

                                  borderColor: '#888',

                                },

                                '&.Mui-focused fieldset': {

                                  borderColor: 'primary.main',

                                },

                              },

                            }}

                            disabled={loadingAI || imageUploading}

                          />

                          {/* Affichage du chargement lors de l'upload image */}

                          {imageUploading && (

                            <Box display="flex" alignItems="center" ml={2}>

                              <CircularProgress size={24} />

                              <Typography variant="caption" sx={{ ml: 1 }}>Upload en cours...</Typography>

                            </Box>

                          )}

                          <Tooltip title="Envoyer">

                            <span>

                              <IconButton

                                onClick={handleSendMessage}

                                disabled={!chatInput.trim() || loadingAI}

                                sx={{

                                  borderRadius: '50%',

                                  border: '2px solid',

                                  borderColor: 'primary.main',

                                  color: 'white',

                                  bgcolor: 'primary.main',

                                  '&:hover': { bgcolor: 'primary.dark' },

                                  '&.Mui-disabled': {

                                    bgcolor: '#ccc',

                                    color: '#fff',

                                    borderColor: '#ccc',

                                  }

                                }}

                              >

                                <ArrowUpward />

                              </IconButton>

                            </span>

                          </Tooltip>

                          {loadingAI && <CircularProgress size={24} sx={{ ml: 2 }} />}

                        </Box>

                      </Box>

                    </Box>

                  ) : (

                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

                      {/* En mode manuel, on n'affiche que l'éditeur GrapesJS avec ses contrôles */}

                      

                      {/* Input caché pour l'import HTML */}

                      <input

                        ref={grapesFileInputRef}

                        type="file"

                        accept=".html,.htm"

                        style={{ display: 'none' }}

                        onChange={handleImportHtmlFile}

                      />

                      

                      {/* Titre simple pour le mode manuel - AUCUN bouton */}

                      <Box sx={{ 

                        display: 'flex', 

                        alignItems: 'center', 

                        justifyContent: 'space-between',

                        p: 2, 

                        borderBottom: '1px solid #E2E8F0',

                        bgcolor: '#F8FAFC',

                        flexShrink: 0

                      }}>

                        <Typography variant="h6" sx={{ fontWeight: 500, color: '#334155' }}>

                          Éditeur d'email

                        </Typography>

                        {(((mode === 'edit' && email) || mode === 'new') && !isNaturalMode) ? (
                          <>
                          <Tooltip

                            title={isManualInitInProgress ? "L'éditeur manuel est en cours d'initialisation..." : (mode === 'edit' ? "Sauvegarder" : "Créer l'email")}

                            arrow

                          >

                            <span>

                              <IconButton

                                onClick={handleSave}

                                disabled={saving || isManualInitInProgress}

                                sx={{

                                  border: '1px solid #007bff',

                                  color: 'white',

                                  bgcolor: '#007bff',

                                  '&:hover': { bgcolor: '#0056b3' },

                                  '&.Mui-disabled': {

                                    bgcolor: '#ccc',

                                    color: '#fff',

                                    borderColor: '#ccc',

                                  }

                                }}

                              >

                                <Save />

                              </IconButton>

                            </span>

                          </Tooltip>
                          </>
                        ) : null}

                      </Box>

                      

                      <Box sx={{ flex: 1, position: 'relative' }}>

                        <div ref={grapesContainerRef} style={{ height: '100%', width: '100%' }} />

                        {showGrapesLoader && (
                          <Box
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'rgba(248,250,252,0.85)',
                              zIndex: 10,
                            }}
                          >
                            <CircularProgress size={40} />
                          </Box>
                        )}

                      </Box>

                    </Box>

                  )}

                </Box>

              </Box>



              {/* Panneau droit : pré-visualisation (masqué en mode manuel) */}

              {isNaturalMode && (

               <Box sx={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexShrink: 0, p: 2, borderBottom: '1px solid #eee' }}>

                  <Typography variant="h6">Prévisualisation</Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

                    {/* Boutons Undo/Redo - uniquement en mode naturel */}

                    {isNaturalMode && (

                      <>

                    <Tooltip title="Annuler" arrow>

                      <span>

                        <IconButton

                          onClick={handleUndo}

                          disabled={!canUndo}

                          sx={{ 

                            border: '1px solid #ccc', 

                            color: canUndo ? '#333' : '#ccc',

                            '&:hover': { bgcolor: canUndo ? '#f5f5f5' : 'transparent' }

                          }}

                        >

                          <Undo />

                        </IconButton>

                      </span>

                    </Tooltip>

                    <Tooltip title="Rétablir" arrow>

                      <span>

                        <IconButton

                          onClick={handleRedo}

                          disabled={!canRedo}

                          sx={{ 

                            border: '1px solid #ccc', 

                            color: canRedo ? '#333' : '#ccc',

                            '&:hover': { bgcolor: canRedo ? '#f5f5f5' : 'transparent' }

                          }}

                        >

                          <Redo />

                        </IconButton>

                      </span>

                    </Tooltip>

                      </>

                    )}

                    {/* Bouton Sauvegarder/Créer - dans les deux modes */}

                    {(mode === 'edit' && email) || mode === 'new' ? (

                      <Tooltip

                        title={(!isNaturalMode && isManualInitInProgress) ? "L'éditeur manuel est en cours d'initialisation..." : (mode === 'edit' ? "Sauvegarder" : "Créer l'email")}

                        arrow

                      >

                        <span>

                          <IconButton

                            onClick={handleSave}

                            disabled={saving || (!isNaturalMode && isManualInitInProgress)}

                            sx={{

                              border: '1px solid #007bff',

                              color: 'white',

                              bgcolor: '#007bff',

                              '&:hover': { bgcolor: '#0056b3' },

                              '&.Mui-disabled': {

                                bgcolor: '#ccc',

                                color: '#fff',

                                borderColor: '#ccc',

                              }

                            }}

                          >

                            <Save />

                          </IconButton>

                        </span>

                      </Tooltip>

                    ) : null}

                    {/* Boutons de prévisualisation - uniquement en mode naturel */}

                    {isNaturalMode && (

                      <>

                    <Tooltip title="Vue Desktop" arrow>

                      <IconButton

                        onClick={() => setIsPreviewMobile(false)}

                        sx={{ border: '1px solid', borderColor: 'primary.main', bgcolor: !isPreviewMobile ? 'primary.main' : 'transparent', color: !isPreviewMobile ? 'white' : 'primary.main' }}

                      >

                        <DesktopWindows />

                      </IconButton>

                    </Tooltip>

                    <Tooltip title="Vue Mobile" arrow>

                      <IconButton

                        onClick={() => setIsPreviewMobile(true)}

                        sx={{ border: '1px solid', borderColor: 'primary.main', bgcolor: isPreviewMobile ? 'primary.main' : 'transparent', color: isPreviewMobile ? 'white' : 'primary.main' }}

                      >

                        <Smartphone />

                      </IconButton>

                    </Tooltip>

                      </>

                    )}

                </Box>

                </Box>

                <Box sx={{ 

                  flex: 1, 

                  border: '1px solid #E2E8F0', 

                  borderRadius: 2, 

                  bgcolor: '#F8FAFC', 

                  p: 2, 

                  display: 'flex', 

                  justifyContent: 'center', 

                  alignItems: 'center',

                  minWidth: 0,

                  overflow: 'hidden'

                }}>

                  <Box sx={{

                    width: '100%',

                    height: '100%',

                    maxWidth: isPreviewMobile ? '375px' : '100%',

                    maxHeight: isPreviewMobile ? '667px' : '100%',

                    bgcolor: 'white',

                    border: '1px solid #E2E8F0',

                    borderRadius: isPreviewMobile ? '40px' : '8px',

                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',

                    transition: 'all 0.3s ease',

                    overflow: 'hidden',

                    p: isPreviewMobile ? 2 : 1

                  }}>

                    <Box sx={{ width: '100%', height: '100%', overflow: 'auto' }}>

                      <div

                        key={previewKey}

                        style={{ maxWidth: '100%', overflowX: 'auto', overflowY: 'auto' }}

                        dangerouslySetInnerHTML={{ __html: addTargetBlankToLinks(getPreviewHtml()) || '<p>Contenu de l\'email non disponible.</p>' }}

                      />

                    </Box>

                  </Box>

                </Box>

              </Box>

              )}

            </Box>

          </Box>

        );

      }



      default:

        return null;

    }

  };



  return (

    <Dialog

      open={open}

      onClose={(event, reason) => { 

        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {

          // On ignore la fermeture via clic extérieur ou Échap

          return;

        }

        handleCloseModal();

      }}

      disableEscapeKeyDown

      maxWidth={false}

      fullWidth
      
      PaperProps={{

        sx: {

          width: '95vw',

          height: '90vh',

          maxWidth: 'none',

          display: 'flex',

          flexDirection: 'column',

          borderRadius: 3,

          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.04)'

        }

      }}

    >

      {/* Loader pendant l'initialisation pour éviter le flash visuel */}
      {!isInitialized && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100%',
          width: '100%'
        }}>
          <CircularProgress size={40} sx={{ color: '#007B8A' }} />
        </Box>
      )}

      {/* Overlay de blocage pendant l'enregistrement */}
      {saving && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            gap: 3
          }}
        >
          <CircularProgress size={60} sx={{ color: '#007B8A' }} />
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#334155',
              fontWeight: 500,
              textAlign: 'center'
            }}
          >
            Enregistrement en cours, patienter un instant...
          </Typography>
        </Box>
      )}

      {/* Contenu principal uniquement après initialisation */}
      {isInitialized && (
        <>
      {/* Bouton de fermeture */}

      <IconButton 

        onClick={handleCloseModal}

        sx={{ 

          position: 'absolute', 

          top: 16, 

          right: 24, 

          color: '#6B7280',

          '&:hover': { color: '#374151' },

          zIndex: 50

        }}

      >

        <Close sx={{ fontSize: 24 }} />

      </IconButton>



      {/* Navigation */}

      {!isEditMode && (

        <Box sx={{ 

          display: 'flex', 

          justifyContent: 'center', 

          gap: { xs: 1.5, sm: 3 }, 

          p: 3, 

          borderBottom: '1px solid #F1F5F9',

          fontSize: '14px'

        }}>

          <Box

            onClick={() => handleStepClick(0)}
            sx={{

              color: currentStep === 0 ? '#007B8A' : '#64748B',

              bgcolor: currentStep === 0 ? 'rgba(0, 123, 138, 0.1)' : 'transparent',

              fontWeight: currentStep === 0 ? 600 : 400,

              fontSize: '14px',

              px: 2,

              py: 1,

              borderRadius: 1,

              cursor: 'pointer',
              '&:hover': { bgcolor: currentStep === 0 ? 'rgba(0, 123, 138, 0.15)' : 'rgba(0,0,0,0.03)' }
            }}

          >

            1. Méthode

          </Box>

          <Box

            onClick={() => handleStepClick(1)}
            sx={{

              color: currentStep === 1 ? '#007B8A' : '#64748B',

              bgcolor: currentStep === 1 ? 'rgba(0, 123, 138, 0.1)' : 'transparent',

              fontWeight: currentStep === 1 ? 600 : 400,

              fontSize: '14px',

              px: 2,

              py: 1,

              borderRadius: 1,

              cursor: 'pointer',
              '&:hover': { bgcolor: currentStep === 1 ? 'rgba(0, 123, 138, 0.15)' : 'rgba(0,0,0,0.03)' }
            }}

          >

            2. Contenu IA

          </Box>

          <Box

            onClick={() => handleStepClick(2)}
            sx={{

              color: currentStep === 2 ? '#007B8A' : '#64748B',

              bgcolor: currentStep === 2 ? 'rgba(0, 123, 138, 0.1)' : 'transparent',

              fontWeight: currentStep === 2 ? 600 : 400,

              fontSize: '14px',

              px: 2,

              py: 1,

              borderRadius: 1,

              cursor: 'pointer',
              '&:hover': { bgcolor: currentStep === 2 ? 'rgba(0, 123, 138, 0.15)' : 'rgba(0,0,0,0.03)' }
            }}

          >

            3. Éditer / Voir

          </Box>

        </Box>

      )}



      {/* Contenu */}

      <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, md: 4 } }}>

        {mode === 'edit' && editorStateError ? (

          <Box sx={{ maxWidth: 600, mx: 'auto', mt: 8 }}>

            <Alert severity="error" sx={{ mb: 2 }}>
              {editorStateError}
            </Alert>

            <Typography sx={{ mb: 3, color: '#4B5563', textAlign: 'center' }}>
              Impossible de charger l'état de l'éditeur pour cet email. Vous pouvez réessayer ou fermer l'éditeur.
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>

              {typeof onRetryEditorState === 'function' && (
                <Button
                  variant="contained"
                  onClick={onRetryEditorState}
                  disabled={editorStateLoading}
                >
                  Réessayer
                </Button>
              )}

              <Button
                variant="outlined"
                onClick={handleCloseModal}
              >
                Fermer
              </Button>

            </Box>

          </Box>

        ) : (

          renderStepContent()

        )}

      </Box>



      {/* Footer */}

      <Box sx={{ 

        display: 'flex', 

        justifyContent: 'space-between', 

        alignItems: 'center', 

        px: 5, 

        py: 3, 

        borderTop: '1px solid #F1F5F9',

        bgcolor: '#F8FAFC'

      }}>

        <Button

          onClick={handlePrevious}

          disabled={currentStep === 0}

          sx={{ 

            visibility: (currentStep === 0 || isEditMode) ? 'hidden' : 'visible',

            display: 'flex',

            alignItems: 'center',

            gap: 1.5,

            bgcolor: '#F1F5F9',

            color: '#64748B',

            px: 3,

            py: 1.5,

            borderRadius: 2,

            fontWeight: 500,

            '&:hover': { bgcolor: '#E2E8F0' },

            fontSize: '14px',

            textTransform: 'none'

          }}

        >

          <ArrowLeft sx={{ fontSize: 14 }} />

          Précédent

        </Button>

        

        <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>

          <Button 

            variant="outlined" 

            onClick={handleCloseModal}

            sx={{

              borderColor: '#E2E8F0',

              color: '#64748B',

              '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' },

              textTransform: 'none'

            }}

          >

            {isEditMode ? 'Fermer' : 'Annuler'}

          </Button>

          {currentStep < 2 ? (

            <Button 

              variant="contained" 

              onClick={handleNext}

              disabled={
                currentStep === 0 && 
                (selectedMethod === 'ai' || selectedMethod === 'manual' || selectedMethod === 'import') && 
                (isValidatingName || !!nameValidationError)
              }

              sx={{

                bgcolor: '#007B8A',

                '&:hover': { bgcolor: '#006B75' },

                textTransform: 'none',

                '&:disabled': {
                  bgcolor: '#94A3B8',
                  color: '#FFFFFF',
                  opacity: 0.6
                }

              }}

            >

              Suivant

            </Button>

          ) : null}

        </Box>

      </Box>

      </>
      )}

      {/* Modal pour prompt d'insertion d'image */}

      <Modal open={imageUploadModalOpen} onClose={() => setImageUploadModalOpen(false)}>

        <Box bgcolor="white" p={4} borderRadius={2} maxWidth={500} mx="auto" my={10} boxShadow={10}>

          <Typography variant="h6" gutterBottom>Où insérer l'image ?</Typography>

          <img src={uploadedImageUrl} alt="Aperçu" style={{ maxWidth: '100%', maxHeight: 200, marginBottom: 16 }} />

          <TextField

            fullWidth

            label="Décrivez où insérer l'image dans l'email (ex: après le titre, remplacer la bannière, etc.)"

            value={imageInsertPrompt}

            onChange={e => setImageInsertPrompt(e.target.value)}

            multiline

            minRows={2}

            sx={{ mb: 2 }}

          />

          <Box display="flex" justifyContent="flex-end" gap={2}>

            <Button onClick={() => setImageUploadModalOpen(false)} color="inherit">Annuler</Button>

            <Button onClick={handleInsertImageWithPrompt} variant="contained" disabled={!imageInsertPrompt.trim() || loadingAI}>

              Insérer l'image

            </Button>

          </Box>

        </Box>

      </Modal>

      {/* Modal de galerie d'images en mode IA */}
      <Dialog 
        open={imageGalleryOpen} 
        onClose={handleCloseGallery}
        maxWidth="md"
        fullWidth
        container={dynamicContentDialogOpen && gjsDynamicContentModalElRef.current ? gjsDynamicContentModalElRef.current : undefined}
        sx={{
          zIndex: 30000,
          '& .MuiBackdrop-root': {
            zIndex: 30000,
          },
          '& .MuiDialog-paper': {
            zIndex: 30001,
          },
        }}
      >
        <Box p={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Bibliothèque d'images</Typography>
            <IconButton onClick={handleCloseGallery} size="small">
              <Close />
            </IconButton>
          </Box>

          {galleryImages.length === 0 && !loadingGallery ? (
            <Typography color="textSecondary" textAlign="center" py={4}>
              Aucune image disponible. Uploadez votre première image !
            </Typography>
          ) : (
            <Box 
              sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                gap: 2,
                maxHeight: 500,
                overflowY: 'auto',
                pb: 2
              }}
              onScroll={(e) => {
                const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
                if (scrollHeight - scrollTop <= clientHeight + 50) {
                  handleLoadMoreGalleryImages();
                }
              }}
            >
              {galleryImages.map((image, index) => (
                <Paper
                  key={index}
                  elevation={2}
                  sx={{
                    cursor: 'pointer',
                    overflow: 'hidden',
                    aspectRatio: '1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                      elevation: 6,
                      transform: 'scale(1.05)',
                      transition: 'all 0.2s'
                    }
                  }}
                  onClick={() => handleSelectGalleryImage(image.src)}
                >
                  <img 
                    src={image.src} 
                    alt={image.name || 'Image'} 
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover' 
                    }}
                    onError={(e) => {
                      e.target.src = TRANSPARENT_PIXEL;
                      e.target.style.opacity = '0.3';
                    }}
                  />
                </Paper>
              ))}
            </Box>
          )}

          {loadingGallery && (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={30} />
            </Box>
          )}

          {!loadingGallery && !galleryHasMore && galleryImages.length > 0 && (
            <Typography color="textSecondary" textAlign="center" py={2} variant="caption">
              Toutes les images ont été chargées
            </Typography>
          )}
        </Box>
      </Dialog>

      {/* Input dédié aux uploads d'images depuis le contenu dynamique */}
      <input
        ref={dcImageFileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleImportImageFile}
      />

      {/* Modal de configuration du contenu dynamique Mautic */}
      {!isNaturalMode && dynamicContentDialogOpen && gjsDynamicContentModalElRef.current &&
        createPortal(
          <Box
            sx={{
              p: 2,
              bgcolor: '#111827',
              color: '#F9FAFB',
              '& .MuiTypography-root': { color: '#F9FAFB' },
              '& .MuiFormLabel-root': { color: '#D1D5DB' },
              '& .MuiInputBase-input': { color: '#F9FAFB' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#4B5563' },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#6366F1',
              },
              '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#9CA3AF',
              },
              '& .MuiButton-text': { color: '#E5E7EB' },
              '& .MuiButton-outlined': { borderColor: '#4B5563', color: '#E5E7EB' },
              '& .MuiButton-contained': { backgroundColor: '#6366F1' },
              '& .MuiButton-contained:hover': { backgroundColor: '#4F46E5' },
              '& .MuiPaper-outlined': {
                backgroundColor: '#020617',
                borderColor: '#1E293B',
              },
            }}
          >
            <Stack spacing={2}>
              {dynamicContentError && (
                <Alert severity="warning">
                  {dynamicContentError}
                </Alert>
              )}

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Nom du contenu dynamique
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="ex: offre_principale"
                  value={dynamicContentAlias}
                  onChange={handleDynamicContentAliasChange}
                  helperText='Nom interne du contenu dynamique dans Mautic (champ "Nom").'
                />
                {(dynamicContentAlias || '').trim() && (
                  <Typography
                    variant="caption"
                    sx={{ color: '#6B7280', mt: 1, display: 'block' }}
                  >
                   
                  </Typography>
                )}
              </Box>

              <Box>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleAddDcVariation}
                    disabled={!(dynamicContentAlias || '').trim()}
                  >
                    Ajouter une variation
                  </Button>
                  <Button
                    size="small"
                    variant={currentDcTab === 'default' ? 'contained' : 'outlined'}
                    onClick={() => setCurrentDcTab('default')}
                  >
                    Contenu par défaut
                  </Button>
                  {dcVariations.map((variation, vIndex) => (
                    <Button
                      key={variation.id || vIndex}
                      size="small"
                      variant={
                        currentDcTab === 'variations' && activeDcVariationIndex === vIndex
                          ? 'contained'
                          : 'outlined'
                      }
                      onClick={() => {
                        setCurrentDcTab('variations');
                        setCurrentDcVariationIndex(vIndex);
                      }}
                    >
                      {`Variation ${vIndex + 1}`}
                    </Button>
                  ))}
                </Stack>
              </Box>

              {currentDcTab === 'default' && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Contenu par défaut
                  </Typography>
                  <SimpleRichTextEditor
                    value={currentDcSlot?.defaultContent || ''}
                    onChange={(content) =>
                      handleDcDefaultContentChange({ target: { value: content || '' } })
                    }
                    menuContainer={gjsDynamicContentModalElRef.current}
                    onPickImageFromLibrary={(cb) => {
                      dcImageInsertCallbackRef.current = cb;
                      handleOpenGallery();
                    }}
                    onUploadImageFromDisk={(cb) => {
                      dcImageInsertCallbackRef.current = cb;
                      if (dcImageFileInputRef.current) {
                        dcImageFileInputRef.current.value = '';
                        dcImageFileInputRef.current.click();
                      }
                    }}
                  />
                </Box>
              )}

              {currentDcTab === 'variations' && (
                <Stack spacing={2}>
                  {!hasDcVariations && (
                    <Typography variant="body2" color="textSecondary">
                      Aucune variation définie pour le moment.
                    </Typography>
                  )}

                  {hasDcVariations && activeDcVariation && activeDcVariationIndex !== null && (
                    <Paper
                      key={activeDcVariation.id || activeDcVariationIndex}
                      variant="outlined"
                      sx={{ p: 2 }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle2">
                          Variation {activeDcVariationIndex + 1}
                        </Typography>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => handleRemoveDcVariation(activeDcVariationIndex)}
                        >
                          Supprimer
                        </Button>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          Contenu de la variation
                        </Typography>
                        <SimpleRichTextEditor
                          value={activeDcVariation.content || ''}
                          onChange={(content) =>
                            handleUpdateDcVariationContent(activeDcVariationIndex, content || '')
                          }
                          minHeight={140}
                          menuContainer={gjsDynamicContentModalElRef.current}
                          onPickImageFromLibrary={(cb) => {
                            dcImageInsertCallbackRef.current = cb;
                            handleOpenGallery();
                          }}
                          onUploadImageFromDisk={(cb) => {
                            dcImageInsertCallbackRef.current = cb;
                            if (dcImageFileInputRef.current) {
                              dcImageFileInputRef.current.value = '';
                              dcImageFileInputRef.current.click();
                            }
                          }}
                        />
                      </Box>

                      <Typography variant="subtitle2" gutterBottom>
                        Conditions d'affichage
                      </Typography>

                      <Stack spacing={1}>
                        {Array.isArray(activeDcVariation.conditions) && activeDcVariation.conditions.map((cond, cIndex) => {
                          const fieldDef = getMauticFieldById(cond.field || '');
                          const fieldType = cond.type || (fieldDef && fieldDef.type) || 'text';
                          const operators = getMauticOperatorsForType(fieldType);
                          const operatorDef = getMauticOperatorById(cond.operator || '');
                          const requiresValue = !operatorDef || operatorDef.requiresValue !== false;
                          const isValid = isMauticConditionValid(cond);

                          return (
                            <React.Fragment key={cIndex}>
                              {cIndex > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
                                  <TextField
                                    select
                                    size="small"
                                    label="Opérateur entre conditions"
                                    value={cond.glue || 'and'}
                                    onChange={(e) =>
                                      handleUpdateDcCondition(activeDcVariationIndex, cIndex, 'glue', e.target.value)
                                    }
                                    sx={{
                                      width: 180,
                                      '& .MuiInputBase-root': {
                                        backgroundColor: 'rgba(15,23,42,0.9)',
                                        color: '#e5e7eb',
                                      },
                                      '& .MuiSvgIcon-root': { color: '#e5e7eb' },
                                    }}
                                    SelectProps={{
                                      MenuProps: {
                                        disablePortal: true,
                                        container: gjsDynamicContentModalElRef.current || undefined,
                                        PaperProps: {
                                          sx: {
                                            backgroundColor: '#020617',
                                            color: '#e5e7eb',
                                          },
                                        },
                                      },
                                    }}
                                  >
                                    <MenuItem value="and">AND</MenuItem>
                                    <MenuItem value="or">OR</MenuItem>
                                  </TextField>
                                </Box>
                              )}
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 1.5,
                                  borderColor: isValid ? 'rgba(148,163,184,0.4)' : '#F97316',
                                  backgroundColor: 'rgba(15,23,42,0.9)',
                                }}
                              >
                                <Grid container spacing={1} alignItems="center">
                                  <Grid item xs={12} sm={3}>
                                    <TextField
                                      select
                                      fullWidth
                                      size="small"
                                      label="Champ"
                                      value={cond.field || ''}
                                      onChange={(e) =>
                                        handleUpdateDcCondition(activeDcVariationIndex, cIndex, 'field', e.target.value)
                                      }
                                      sx={{
                                        '& .MuiInputBase-root': {
                                          backgroundColor: 'rgba(15,23,42,0.9)',
                                          color: '#e5e7eb',
                                        },
                                        '& .MuiSvgIcon-root': { color: '#e5e7eb' },
                                      }}
                                      SelectProps={{
                                        MenuProps: {
                                          disablePortal: true,
                                          container: gjsDynamicContentModalElRef.current || undefined,
                                          PaperProps: {
                                            sx: {
                                              backgroundColor: '#020617',
                                              color: '#e5e7eb',
                                            },
                                          },
                                        },
                                      }}
                                    >
                                      <MenuItem value="" disabled>
                                        Sélectionner
                                      </MenuItem>
                                      {MAUTIC_DYNAMIC_FIELDS.map((f) => (
                                        <MenuItem key={f.id} value={f.id}>
                                          {f.label}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                  </Grid>
                                  <Grid item xs={12} sm={3}>
                                    <TextField
                                      select
                                      fullWidth
                                      size="small"
                                      label="Opérateur"
                                      value={cond.operator || ''}
                                      onChange={(e) =>
                                        handleUpdateDcCondition(activeDcVariationIndex, cIndex, 'operator', e.target.value)
                                      }
                                      sx={{
                                        '& .MuiInputBase-root': {
                                          backgroundColor: 'rgba(15,23,42,0.9)',
                                          color: '#e5e7eb',
                                        },
                                        '& .MuiSvgIcon-root': { color: '#e5e7eb' },
                                      }}
                                      SelectProps={{
                                        MenuProps: {
                                          disablePortal: true,
                                          container: gjsDynamicContentModalElRef.current || undefined,
                                          PaperProps: {
                                            sx: {
                                              backgroundColor: '#020617',
                                              color: '#e5e7eb',
                                            },
                                          },
                                        },
                                      }}
                                    >
                                      <MenuItem value="" disabled>
                                        Sélectionner
                                      </MenuItem>
                                      {operators.map((op) => (
                                        <MenuItem key={op.id} value={op.id}>
                                          {op.label}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                  </Grid>
                                  <Grid item xs={12} sm={4}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Valeur"
                                      type={fieldType === 'number' ? 'number' : 'text'}
                                      value={cond.filter || ''}
                                      onChange={(e) =>
                                        handleUpdateDcCondition(activeDcVariationIndex, cIndex, 'filter', e.target.value)
                                      }
                                      disabled={!requiresValue}
                                      helperText={!isValid ? 'Champ ou valeur manquant(e) pour Mautic.' : ' '}
                                      sx={{
                                        '& .MuiInputBase-root': {
                                          backgroundColor: 'rgba(15,23,42,0.9)',
                                          color: '#e5e7eb',
                                        },
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={2}>
                                    <Button
                                      size="small"
                                      color="error"
                                      onClick={() => handleRemoveDcCondition(activeDcVariationIndex, cIndex)}
                                    >
                                      Supprimer
                                    </Button>
                                  </Grid>
                                </Grid>
                              </Paper>
                            </React.Fragment>
                          );
                        })}
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => handleAddDcCondition(activeDcVariationIndex)}
                        >
                          Ajouter une condition
                        </Button>
                      </Stack>
                    </Paper>
                  )}
                </Stack>
              )}
            </Stack>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              <Button onClick={handleCloseDynamicContentDialog}>
                Annuler
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmDynamicContent}
                disabled={
                  !(dynamicContentAlias || '').trim() ||
                  hasInvalidDynamicContentConditions(currentDcSlot)
                }
              >
                Enregistrer
              </Button>
            </Box>
          </Box>,
          gjsDynamicContentModalElRef.current
        )
      }

      {isNaturalMode && dynamicContentDialogOpen && (
        <Dialog
          open={dynamicContentDialogOpen}
          onClose={handleCloseDynamicContentDialog}
          maxWidth="md"
          fullWidth
          sx={{
            zIndex: 30000,
            '& .MuiDialog-paper': {
              backgroundColor: '#111827',
              color: '#F9FAFB',
            },
          }}
        >
          <DialogTitle>Configurer le contenu dynamique Mautic</DialogTitle>
          <DialogContent dividers>
            <Box
              sx={{
                p: 1,
                '& .MuiTypography-root': { color: '#F9FAFB' },
                '& .MuiFormLabel-root': { color: '#D1D5DB' },
                '& .MuiInputBase-input': { color: '#F9FAFB' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#4B5563' },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#6366F1',
                },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#9CA3AF',
                },
                '& .MuiButton-text': { color: '#E5E7EB' },
                '& .MuiButton-outlined': { borderColor: '#4B5563', color: '#E5E7EB' },
                '& .MuiButton-contained': { backgroundColor: '#6366F1' },
                '& .MuiButton-contained:hover': { backgroundColor: '#4F46E5' },
                '& .MuiPaper-outlined': {
                  backgroundColor: '#020617',
                  borderColor: '#1E293B',
                },
              }}
            >
              <Stack spacing={2}>
                {dynamicContentError && (
                  <Alert severity="warning">
                    {dynamicContentError}
                  </Alert>
                )}

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Nom du contenu dynamique
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="ex: offre_principale"
                    value={dynamicContentAlias}
                    onChange={handleDynamicContentAliasChange}
                    helperText='Nom interne du contenu dynamique dans Mautic (champ "Nom").'
                  />
                  {(dynamicContentAlias || '').trim() && (
                    <Typography
                      variant="caption"
                      sx={{ color: '#9CA3AF', mt: 1, display: 'block' }}
                    >
                      
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleAddDcVariation}
                      disabled={!(dynamicContentAlias || '').trim()}
                    >
                      Ajouter une variation
                    </Button>
                    <Button
                      size="small"
                      variant={currentDcTab === 'default' ? 'contained' : 'outlined'}
                      onClick={() => setCurrentDcTab('default')}
                    >
                      Contenu par défaut
                    </Button>
                    {dcVariations.map((variation, vIndex) => (
                      <Button
                        key={variation.id || vIndex}
                        size="small"
                        variant={
                          currentDcTab === 'variations' && activeDcVariationIndex === vIndex
                            ? 'contained'
                            : 'outlined'
                        }
                        onClick={() => {
                          setCurrentDcTab('variations');
                          setCurrentDcVariationIndex(vIndex);
                        }}
                      >
                        {`Variation ${vIndex + 1}`}
                      </Button>
                    ))}
                  </Stack>
                </Box>

                {currentDcTab === 'default' && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Contenu par défaut
                    </Typography>
                    <SimpleRichTextEditor
                      value={currentDcSlot?.defaultContent || ''}
                      onChange={(content) =>
                        handleDcDefaultContentChange({ target: { value: content || '' } })
                      }
                      menuContainer={null}
                      onPickImageFromLibrary={(cb) => {
                        dcImageInsertCallbackRef.current = cb;
                        handleOpenGallery();
                      }}
                      onUploadImageFromDisk={(cb) => {
                        dcImageInsertCallbackRef.current = cb;
                        if (dcImageFileInputRef.current) {
                          dcImageFileInputRef.current.value = '';
                          dcImageFileInputRef.current.click();
                        }
                      }}
                    />
                  </Box>
                )}

                {currentDcTab === 'variations' && (
                  <Stack spacing={2}>
                    {!hasDcVariations && (
                      <Typography variant="body2" color="textSecondary">
                        Aucune variation définie pour le moment.
                      </Typography>
                    )}

                    {hasDcVariations && activeDcVariation && activeDcVariationIndex !== null && (
                      <Paper
                        key={activeDcVariation.id || activeDcVariationIndex}
                        variant="outlined"
                        sx={{ p: 2 }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="subtitle2">
                            Variation {activeDcVariationIndex + 1}
                          </Typography>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleRemoveDcVariation(activeDcVariationIndex)}
                          >
                            Supprimer
                          </Button>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            Contenu de la variation
                          </Typography>
                          <SimpleRichTextEditor
                            value={activeDcVariation.content || ''}
                            onChange={(content) =>
                              handleUpdateDcVariationContent(activeDcVariationIndex, content || '')
                            }
                            minHeight={140}
                            menuContainer={null}
                            onPickImageFromLibrary={(cb) => {
                              dcImageInsertCallbackRef.current = cb;
                              handleOpenGallery();
                            }}
                            onUploadImageFromDisk={(cb) => {
                              dcImageInsertCallbackRef.current = cb;
                              if (dcImageFileInputRef.current) {
                                dcImageFileInputRef.current.value = '';
                                dcImageFileInputRef.current.click();
                              }
                            }}
                          />
                        </Box>

                        <Typography variant="subtitle2" gutterBottom>
                          Conditions d'affichage
                        </Typography>

                        <Stack spacing={1}>
                          {Array.isArray(activeDcVariation.conditions) && activeDcVariation.conditions.map((cond, cIndex) => {
                            const fieldDef = getMauticFieldById(cond.field || '');
                            const fieldType = cond.type || (fieldDef && fieldDef.type) || 'text';
                            const operators = getMauticOperatorsForType(fieldType);
                            const operatorDef = getMauticOperatorById(cond.operator || '');
                            const requiresValue = !operatorDef || operatorDef.requiresValue !== false;
                            const isValid = isMauticConditionValid(cond);

                            return (
                              <React.Fragment key={cIndex}>
                                {cIndex > 0 && (
                                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
                                    <TextField
                                      select
                                      size="small"
                                      label="Opérateur entre conditions"
                                      value={cond.glue || 'and'}
                                      onChange={(e) =>
                                        handleUpdateDcCondition(activeDcVariationIndex, cIndex, 'glue', e.target.value)
                                      }
                                      sx={{
                                        width: 180,
                                        '& .MuiInputBase-root': {
                                          backgroundColor: 'rgba(15,23,42,0.9)',
                                          color: '#e5e7eb',
                                        },
                                        '& .MuiSvgIcon-root': { color: '#e5e7eb' },
                                      }}
                                    >
                                      <MenuItem value="and">AND</MenuItem>
                                      <MenuItem value="or">OR</MenuItem>
                                    </TextField>
                                  </Box>
                                )}
                                <Paper
                                  variant="outlined"
                                  sx={{
                                    p: 1.5,
                                    borderColor: isValid ? 'rgba(148,163,184,0.4)' : '#F97316',
                                    backgroundColor: 'rgba(15,23,42,0.9)',
                                  }}
                                >
                                  <Grid container spacing={1} alignItems="center">
                                    <Grid item xs={12} sm={3}>
                                      <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Champ"
                                        value={cond.field || ''}
                                        onChange={(e) =>
                                          handleUpdateDcCondition(activeDcVariationIndex, cIndex, 'field', e.target.value)
                                        }
                                        sx={{
                                          '& .MuiInputBase-root': {
                                            backgroundColor: 'rgba(15,23,42,0.9)',
                                            color: '#e5e7eb',
                                          },
                                          '& .MuiSvgIcon-root': { color: '#e5e7eb' },
                                        }}
                                      >
                                        <MenuItem value="" disabled>
                                          Sélectionner
                                        </MenuItem>
                                        {MAUTIC_DYNAMIC_FIELDS.map((f) => (
                                          <MenuItem key={f.id} value={f.id}>
                                            {f.label}
                                          </MenuItem>
                                        ))}
                                      </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                      <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Opérateur"
                                        value={cond.operator || ''}
                                        onChange={(e) =>
                                          handleUpdateDcCondition(activeDcVariationIndex, cIndex, 'operator', e.target.value)
                                        }
                                        sx={{
                                          '& .MuiInputBase-root': {
                                            backgroundColor: 'rgba(15,23,42,0.9)',
                                            color: '#e5e7eb',
                                          },
                                          '& .MuiSvgIcon-root': { color: '#e5e7eb' },
                                        }}
                                      >
                                        <MenuItem value="" disabled>
                                          Sélectionner
                                        </MenuItem>
                                        {operators.map((op) => (
                                          <MenuItem key={op.id} value={op.id}>
                                            {op.label}
                                          </MenuItem>
                                        ))}
                                      </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        label="Valeur"
                                        type={fieldType === 'number' ? 'number' : 'text'}
                                        value={cond.filter || ''}
                                        onChange={(e) =>
                                          handleUpdateDcCondition(activeDcVariationIndex, cIndex, 'filter', e.target.value)
                                        }
                                        disabled={!requiresValue}
                                        helperText={!isValid ? 'Champ ou valeur manquant(e) pour Mautic.' : ' '}
                                        sx={{
                                          '& .MuiInputBase-root': {
                                            backgroundColor: 'rgba(15,23,42,0.9)',
                                            color: '#e5e7eb',
                                          },
                                        }}
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={2}>
                                      <Button
                                        size="small"
                                        color="error"
                                        onClick={() => handleRemoveDcCondition(activeDcVariationIndex, cIndex)}
                                      >
                                        Supprimer
                                      </Button>
                                    </Grid>
                                  </Grid>
                                </Paper>
                              </React.Fragment>
                            );
                          })}
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => handleAddDcCondition(activeDcVariationIndex)}
                          >
                            Ajouter une condition
                          </Button>
                        </Stack>
                      </Paper>
                    )}
                  </Stack>
                )}
              </Stack>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDynamicContentDialog}>
              Annuler
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmDynamicContent}
              disabled={
                !(dynamicContentAlias || '').trim() ||
                hasInvalidDynamicContentConditions(currentDcSlot)
              }
            >
              Enregistrer
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal de confirmation d'import HTML */}
      <Dialog 
        open={htmlImportConfirmOpen} 
        onClose={() => {
          setHtmlImportConfirmOpen(false);
          setPendingHtmlContent('');
          setPendingHtmlFileName('');
          setHtmlInsertPrompt('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Importer du contenu HTML</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            <Alert severity="info" sx={{ mb: 1 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Fichier :</strong> {pendingHtmlFileName}
              </Typography>
              <Typography variant="body2">
                Vous pouvez <strong>remplacer</strong> complètement votre email ou <strong>insérer intelligemment</strong> ce contenu avec l'aide de l'IA.
              </Typography>
            </Alert>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Option 1 : Insertion intelligente avec l'IA (recommandé)
              </Typography>
              <TextField
                fullWidth
                label="Décrivez où et comment intégrer ce HTML (ex: dans la section héros, après le header, etc.)"
                value={htmlInsertPrompt}
                onChange={e => setHtmlInsertPrompt(e.target.value)}
                multiline
                minRows={2}
                placeholder="Insérer dans la section principale..."
                variant="outlined"
              />
            </Box>

            <Divider>OU</Divider>

            <Box>
              <Typography variant="subtitle2" gutterBottom color="warning.main">
                Option 2 : Remplacement complet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ⚠️ Cela remplacera <strong>tout le contenu actuel</strong> de l'email par le fichier importé.
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: '16px 24px', gap: 1 }}>
          <Button 
            onClick={() => {
              setHtmlImportConfirmOpen(false);
              setPendingHtmlContent('');
              setPendingHtmlFileName('');
              setHtmlInsertPrompt('');
            }}
            disabled={isInsertingHtml}
          >
            Annuler
          </Button>
          <Button 
            onClick={handleReplaceHtmlDirectly}
            color="warning"
            variant="outlined"
            disabled={isInsertingHtml}
            startIcon={isInsertingHtml ? <CircularProgress size={16} /> : null}
          >
            Remplacer tout
          </Button>
          <Button 
            onClick={handleInsertHtmlWithAI}
            variant="contained"
            disabled={!htmlInsertPrompt.trim() || isInsertingHtml}
            startIcon={isInsertingHtml ? <CircularProgress size={16} /> : <SmartToyIcon />}
          >
            Insérer avec l'IA
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation de changement de mode si modifications non sauvegardées */}

      <Dialog open={confirmSwitchOpen} onClose={handleConfirmSwitchCancel} maxWidth="xs" fullWidth>

        <Box p={3}>

          <Typography variant="h6" sx={{ mb: 1 }}>Modifications non sauvegardées</Typography>

          <Typography variant="body2" sx={{ color: '#64748B', mb: 2 }}>

            Vous avez des modifications non sauvegardées. Souhaitez-vous les enregistrer avant de changer de mode ?

          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>

            <Button onClick={handleConfirmSwitchCancel}>Annuler</Button>

            <Button onClick={handleConfirmSwitchDiscard} color="warning">Ignorer</Button>

            <Button onClick={handleConfirmSwitchSave} variant="contained" disabled={saving}>Enregistrer</Button>

          </Box>

        </Box>

      </Dialog>



      {/* Confirmation de fermeture si modifications non sauvegardées */}

      <Dialog open={confirmCloseOpen} onClose={handleConfirmCloseCancel} maxWidth="xs" fullWidth>

        <Box p={3}>

          <Typography variant="h6" sx={{ mb: 1 }}>
            {mode === 'new' ? 'Email non sauvegardé' : 'Modifications non sauvegardées'}
          </Typography>

          <Typography variant="body2" sx={{ color: '#64748B', mb: 2 }}>

            {mode === 'new' 
              ? 'Vous avez créé un email qui n\'a pas encore été enregistré. Souhaitez-vous le sauvegarder avant de fermer ?' 
              : 'Vous avez des modifications non sauvegardées. Souhaitez-vous les enregistrer avant de fermer ?'}

          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>

            <Button onClick={handleConfirmCloseCancel}>Annuler</Button>

            <Button onClick={handleConfirmCloseDiscard} color="warning">Non</Button>

            <Button onClick={handleConfirmCloseSave} variant="contained" disabled={saving}>Oui</Button>

          </Box>

        </Box>

      </Dialog>

      {/* Modal de gestion des templates */}
      <Dialog 
        open={templateManagerOpen} 
        onClose={() => setTemplateManagerOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Gérer mes templates</Typography>
            <IconButton onClick={() => setTemplateManagerOpen(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {/* Bouton pour personnaliser un template */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              variant="contained"
              onClick={() => {
                // Réinitialiser le formulaire pour un nouveau template
                resetTemplateFormData();
                setEditingTemplate(null);
                setTemplateManagerOpen(false);
                setTemplateEditorOpen(true);
              }}
              sx={{
                bgcolor: '#007B8A',
                '&:hover': { bgcolor: '#006B75' },
                textTransform: 'none',
                px: 3,
                py: 1.5
              }}
            >
              ➕ Personnaliser un nouveau template
            </Button>
          </Box>

          {/* Liste des templates existants */}
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              📚 Mes templates ({userTemplates.length})
            </Typography>
            
            {loadingTemplates ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : userTemplates.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3, color: '#64748B' }}>
                <Typography>Aucun template sauvegardé pour le moment</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {userTemplates.map((template) => (
                  <Box
                    key={template.id}
                    sx={{
                      p: 2,
                      border: '1px solid #E2E8F0',
                      borderRadius: 2,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      '&:hover': { bgcolor: '#F8FAFC' }
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography sx={{ fontWeight: 500, color: '#334155' }}>
                          {template.name}
                        </Typography>
                        {template.is_default && (
                          <Box sx={{ bgcolor: '#007B8A', color: 'white', px: 1, py: 0.5, borderRadius: 1, fontSize: '10px' }}>
                            Par défaut
                          </Box>
                        )}
                        <Box sx={{ bgcolor: '#E2E8F0', color: '#64748B', px: 1, py: 0.5, borderRadius: 1, fontSize: '10px' }}>
                          {template.type.toUpperCase()}
                        </Box>
                      </Box>
                      {template.description && (
                        <Typography variant="caption" sx={{ color: '#64748B' }}>
                          {template.description}
                        </Typography>
                      )}
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        onClick={() => {
                          applyTemplate(template);
                          setTemplateManagerOpen(false);
                        }}
                        sx={{
                          textTransform: 'none',
                          color: '#007B8A',
                          '&:hover': { bgcolor: 'rgba(0, 123, 138, 0.05)' }
                        }}
                      >
                        Appliquer
                      </Button>
                      <IconButton
                        size="small"
                        onClick={() => editTemplate(template)}
                        sx={{ color: '#007B8A' }}
                        title="Modifier le template"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => deleteTemplate(template.id)}
                        sx={{ color: '#ef4444' }}
                        title="Supprimer le template"
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setTemplateManagerOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Modal d'édition/création de template */}
      <Dialog 
        open={templateEditorOpen} 
        onClose={() => {
          // Retour à la liste des templates
          setTemplateEditorOpen(false);
          setEditingTemplate(null);
          setTemplateManagerOpen(true);
        }}
        maxWidth="xl"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { height: { xs: 'auto', md: '90vh' } } }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {editingTemplate ? 'Modifier mon template personnalisé' : 'Personnaliser mon template'}
            </Typography>
            <IconButton 
              onClick={() => {
                // Retour à la liste des templates
                setTemplateEditorOpen(false);
                setEditingTemplate(null);
                setTemplateManagerOpen(true);
              }} 
              size="small"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ overflowY: 'auto', position: 'relative' }}>
          <Box sx={{ py: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, alignItems: 'start' }}>
            <Box>
            {/* Informations du template */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#334155' }}>
                📋 Informations du template
              </Typography>
              
              <TextField
                fullWidth
                label="Nom du template *"
                variant="outlined"
                size="medium"
                value={templateFormData.name}
                onChange={(e) => setTemplateFormData({ ...templateFormData, name: e.target.value })}
                sx={{ mb: 2 }}
                placeholder="Ex: Newsletter Mensuelle"
              />
              
              <TextField
                fullWidth
                label="Description (optionnel)"
                variant="outlined"
                size="medium"
                multiline
                rows={2}
                value={templateFormData.description}
                onChange={(e) => setTemplateFormData({ ...templateFormData, description: e.target.value })}
                sx={{ mb: 2 }}
                placeholder="Ex: Template pour les newsletters mensuelles"
              />
              
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  id="template-editor-default"
                  checked={templateFormData.isDefault}
                  onChange={(e) => setTemplateFormData({ ...templateFormData, isDefault: e.target.checked })}
                  style={{ marginRight: '8px' }}
                />
                <label htmlFor="template-editor-default" style={{ cursor: 'pointer', fontSize: '14px' }}>
                  Définir comme template par défaut
                </label>
              </Box>
            </Box>

            {/* Personnalisation du HEADER */}
            <Box sx={{ mb: 4, p: 3, bgcolor: '#F0F9FF', borderRadius: 2, border: '1px solid #BAE6FD' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0369A1' }}>
                  🎨 Éléments du Header
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const menu = document.getElementById('header-add-menu');
                    if (menu) menu.style.display = menu.style.display === 'none' ? 'block' : 'none';
                  }}
                  sx={{ textTransform: 'none', borderColor: '#0369A1', color: '#0369A1' }}
                >
                  ➕ Ajouter un élément
                </Button>
              </Box>

              {/* Menu d'ajout d'éléments */}
              <Box id="header-add-menu" sx={{ display: 'none', mb: 2, p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid #BAE6FD' }}>
                <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>Choisir un type d'élément :</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button size="small" onClick={() => { addElement('header', 'text'); document.getElementById('header-add-menu').style.display = 'none'; }}>📝 Texte</Button>
                  <Button size="small" onClick={() => { addElement('header', 'image'); document.getElementById('header-add-menu').style.display = 'none'; }}>🖼️ Image</Button>
                  <Button size="small" onClick={() => { addElement('header', 'button'); document.getElementById('header-add-menu').style.display = 'none'; }}>🔘 Bouton</Button>
                  <Button size="small" onClick={() => { addElement('header', 'spacer'); document.getElementById('header-add-menu').style.display = 'none'; }}>↕️ Espace</Button>
                  <Button size="small" onClick={() => { addElement('header', 'divider'); document.getElementById('header-add-menu').style.display = 'none'; }}>➖ Séparateur</Button>
                </Box>
              </Box>

              {/* Liste des éléments */}
              {templateFormData.headerElements.map((element, index) => (
                <Box key={element.id} sx={{ mb: 2, p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid #BAE6FD' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#0369A1' }}>
                      {element.type === 'text' && '📝 Texte'}
                      {element.type === 'image' && '🖼️ Image'}
                      {element.type === 'button' && '🔘 Bouton'}
                      {element.type === 'spacer' && '↕️ Espace'}
                      {element.type === 'divider' && '➖ Séparateur'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => moveElementUp('header', element.id)} disabled={index === 0}>
                        ⬆️
                      </IconButton>
                      <IconButton size="small" onClick={() => moveElementDown('header', element.id)} disabled={index === templateFormData.headerElements.length - 1}>
                        ⬇️
                      </IconButton>
                      <IconButton size="small" onClick={() => removeElement('header', element.id)} sx={{ color: 'error.main' }}>
                        🗑️
                      </IconButton>
                    </Box>
                  </Box>
                  
                  {/* Formulaire de configuration selon le type */}
                  {element.type === 'text' && (
                    <Box>
                      <TextField
                        fullWidth
                        size="small"
                        label="Contenu"
                        multiline
                        rows={2}
                        value={element.props.content}
                        onChange={(e) => updateElementProps('header', element.id, { content: e.target.value })}
                        sx={{ mb: 1 }}
                      />
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
                        <TextField
                          size="small"
                          label="Taille"
                          value={element.props.fontSize}
                          onChange={(e) => updateElementProps('header', element.id, { fontSize: e.target.value })}
                        />
                        <TextField
                          select
                          size="small"
                          label="Alignement"
                          value={element.props.align}
                          onChange={(e) => updateElementProps('header', element.id, { align: e.target.value })}
                          SelectProps={{ native: true }}
                        >
                          <option value="left">Gauche</option>
                          <option value="center">Centre</option>
                          <option value="right">Droite</option>
                        </TextField>
                        <TextField
                          size="small"
                          type="color"
                          label="Couleur"
                          value={element.props.color}
                          onChange={(e) => updateElementProps('header', element.id, { color: e.target.value })}
                        />
                      </Box>
                    </Box>
                  )}
                  
                  {element.type === 'image' && (
                    <Box>
                      <TextField
                        fullWidth
                        size="small"
                        label="URL de l'image"
                        value={element.props.src}
                        onChange={(e) => updateElementProps('header', element.id, { src: e.target.value })}
                        sx={{ mb: 1 }}
                      />
                      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          component="label"
                          sx={{ flex: 1, textTransform: 'none' }}
                        >
                          📎 Uploader depuis PC
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onload = (event) => {
                                  updateElementProps('header', element.id, { src: event.target.result });
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                          />
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ flex: 1, textTransform: 'none' }}
                          onClick={() => {
                            // Sauvegarder l'élément en cours pour savoir où insérer l'image
                            window.currentImageElement = { section: 'header', id: element.id };
                            handleOpenGallery();
                          }}
                        >
                          🖼️ Bibliothèque d'images
                        </Button>
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                        <TextField
                          size="small"
                          label="Largeur"
                          value={element.props.width}
                          onChange={(e) => updateElementProps('header', element.id, { width: e.target.value })}
                        />
                        <TextField
                          select
                          size="small"
                          label="Alignement"
                          value={element.props.align}
                          onChange={(e) => updateElementProps('header', element.id, { align: e.target.value })}
                          SelectProps={{ native: true }}
                        >
                          <option value="left">Gauche</option>
                          <option value="center">Centre</option>
                          <option value="right">Droite</option>
                        </TextField>
                      </Box>
                    </Box>
                  )}
                  
                  {element.type === 'button' && (
                    <Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1 }}>
                        <TextField
                          size="small"
                          label="Texte du bouton"
                          value={element.props.text}
                          onChange={(e) => updateElementProps('header', element.id, { text: e.target.value })}
                        />
                        <TextField
                          size="small"
                          label="Lien (URL)"
                          value={element.props.href}
                          onChange={(e) => updateElementProps('header', element.id, { href: e.target.value })}
                        />
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                        <TextField
                          size="small"
                          type="color"
                          label="Couleur de fond"
                          value={element.props.bgColor}
                          onChange={(e) => updateElementProps('header', element.id, { bgColor: e.target.value })}
                        />
                        <TextField
                          size="small"
                          type="color"
                          label="Couleur du texte"
                          value={element.props.textColor}
                          onChange={(e) => updateElementProps('header', element.id, { textColor: e.target.value })}
                        />
                      </Box>
                    </Box>
                  )}
                  
                  {element.type === 'spacer' && (
                    <TextField
                      size="small"
                      label="Hauteur"
                      value={element.props.height}
                      onChange={(e) => updateElementProps('header', element.id, { height: e.target.value })}
                      placeholder="10px"
                    />
                  )}
                  
                  {element.type === 'divider' && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                      <TextField
                        size="small"
                        label="Épaisseur"
                        value={element.props.borderWidth}
                        onChange={(e) => updateElementProps('header', element.id, { borderWidth: e.target.value })}
                      />
                      <TextField
                        size="small"
                        type="color"
                        label="Couleur"
                        value={element.props.borderColor}
                        onChange={(e) => updateElementProps('header', element.id, { borderColor: e.target.value })}
                      />
                    </Box>
                  )}
                </Box>
              ))}

              <TextField
                fullWidth
                label="Couleur de fond du header"
                variant="outlined"
                size="small"
                type="color"
                value={templateFormData.headerBgColor}
                onChange={(e) => setTemplateFormData({ ...templateFormData, headerBgColor: e.target.value })}
                sx={{ mt: 2 }}
              />
            </Box>

            {/* Personnalisation du FOOTER */}
            <Box sx={{ mb: 3, p: 3, bgcolor: '#FEF3C7', borderRadius: 2, border: '1px solid #FCD34D' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#92400E' }}>
                  🎨 Éléments du Footer
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const menu = document.getElementById('footer-add-menu');
                    if (menu) menu.style.display = menu.style.display === 'none' ? 'block' : 'none';
                  }}
                  sx={{ textTransform: 'none', borderColor: '#92400E', color: '#92400E' }}
                >
                  ➕ Ajouter un élément
                </Button>
              </Box>

              {/* Menu d'ajout d'éléments */}
              <Box id="footer-add-menu" sx={{ display: 'none', mb: 2, p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid #FCD34D' }}>
                <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>Choisir un type d'élément :</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button size="small" onClick={() => { addElement('footer', 'text'); document.getElementById('footer-add-menu').style.display = 'none'; }}>📝 Texte</Button>
                  <Button size="small" onClick={() => { addElement('footer', 'image'); document.getElementById('footer-add-menu').style.display = 'none'; }}>🖼️ Image</Button>
                  <Button size="small" onClick={() => { addElement('footer', 'button'); document.getElementById('footer-add-menu').style.display = 'none'; }}>🔘 Bouton</Button>
                  <Button size="small" onClick={() => { addElement('footer', 'spacer'); document.getElementById('footer-add-menu').style.display = 'none'; }}>↕️ Espace</Button>
                  <Button size="small" onClick={() => { addElement('footer', 'divider'); document.getElementById('footer-add-menu').style.display = 'none'; }}>➖ Séparateur</Button>
                  <Button size="small" onClick={() => { addElement('footer', 'social'); document.getElementById('footer-add-menu').style.display = 'none'; }}>👥 Réseaux sociaux</Button>
                </Box>
              </Box>

              {/* Liste des éléments */}
              {templateFormData.footerElements.map((element, index) => (
                <Box key={element.id} sx={{ mb: 2, p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid #FCD34D' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#92400E' }}>
                      {element.type === 'text' && '📝 Texte'}
                      {element.type === 'image' && '🖼️ Image'}
                      {element.type === 'button' && '🔘 Bouton'}
                      {element.type === 'spacer' && '↕️ Espace'}
                      {element.type === 'divider' && '➖ Séparateur'}
                      {element.type === 'social' && '👥 Réseaux sociaux'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => moveElementUp('footer', element.id)} disabled={index === 0}>
                        ⬆️
                      </IconButton>
                      <IconButton size="small" onClick={() => moveElementDown('footer', element.id)} disabled={index === templateFormData.footerElements.length - 1}>
                        ⬇️
                      </IconButton>
                      <IconButton size="small" onClick={() => removeElement('footer', element.id)} sx={{ color: 'error.main' }}>
                        🗑️
                      </IconButton>
                    </Box>
                  </Box>
                  
                  {/* Formulaire de configuration selon le type */}
                  {element.type === 'text' && (
                    <Box>
                      <TextField
                        fullWidth
                        size="small"
                        label="Contenu"
                        multiline
                        rows={2}
                        value={element.props.content}
                        onChange={(e) => updateElementProps('footer', element.id, { content: e.target.value })}
                        sx={{ mb: 1 }}
                      />
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
                        <TextField
                          size="small"
                          label="Taille"
                          value={element.props.fontSize}
                          onChange={(e) => updateElementProps('footer', element.id, { fontSize: e.target.value })}
                        />
                        <TextField
                          select
                          size="small"
                          label="Alignement"
                          value={element.props.align}
                          onChange={(e) => updateElementProps('footer', element.id, { align: e.target.value })}
                          SelectProps={{ native: true }}
                        >
                          <option value="left">Gauche</option>
                          <option value="center">Centre</option>
                          <option value="right">Droite</option>
                        </TextField>
                        <TextField
                          size="small"
                          type="color"
                          label="Couleur"
                          value={element.props.color}
                          onChange={(e) => updateElementProps('footer', element.id, { color: e.target.value })}
                        />
                      </Box>
                    </Box>
                  )}
                  
                  {element.type === 'image' && (
                    <Box>
                      <TextField
                        fullWidth
                        size="small"
                        label="URL de l'image"
                        value={element.props.src}
                        onChange={(e) => updateElementProps('footer', element.id, { src: e.target.value })}
                        sx={{ mb: 1 }}
                      />
                      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          component="label"
                          sx={{ flex: 1, textTransform: 'none' }}
                        >
                          📎 Uploader depuis PC
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onload = (event) => {
                                  updateElementProps('footer', element.id, { src: event.target.result });
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                          />
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ flex: 1, textTransform: 'none' }}
                          onClick={() => {
                            // Sauvegarder l'élément en cours pour savoir où insérer l'image
                            window.currentImageElement = { section: 'footer', id: element.id };
                            handleOpenGallery();
                          }}
                        >
                          🖼️ Bibliothèque d'images
                        </Button>
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                        <TextField
                          size="small"
                          label="Largeur"
                          value={element.props.width}
                          onChange={(e) => updateElementProps('footer', element.id, { width: e.target.value })}
                        />
                        <TextField
                          select
                          size="small"
                          label="Alignement"
                          value={element.props.align}
                          onChange={(e) => updateElementProps('footer', element.id, { align: e.target.value })}
                          SelectProps={{ native: true }}
                        >
                          <option value="left">Gauche</option>
                          <option value="center">Centre</option>
                          <option value="right">Droite</option>
                        </TextField>
                      </Box>
                    </Box>
                  )}
                  
                  {element.type === 'button' && (
                    <Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1 }}>
                        <TextField
                          size="small"
                          label="Texte du bouton"
                          value={element.props.text}
                          onChange={(e) => updateElementProps('footer', element.id, { text: e.target.value })}
                        />
                        <TextField
                          size="small"
                          label="Lien (URL)"
                          value={element.props.href}
                          onChange={(e) => updateElementProps('footer', element.id, { href: e.target.value })}
                        />
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                        <TextField
                          size="small"
                          type="color"
                          label="Couleur de fond"
                          value={element.props.bgColor}
                          onChange={(e) => updateElementProps('footer', element.id, { bgColor: e.target.value })}
                        />
                        <TextField
                          size="small"
                          type="color"
                          label="Couleur du texte"
                          value={element.props.textColor}
                          onChange={(e) => updateElementProps('footer', element.id, { textColor: e.target.value })}
                        />
                      </Box>
                    </Box>
                  )}
                  
                  {element.type === 'spacer' && (
                    <TextField
                      size="small"
                      label="Hauteur"
                      value={element.props.height}
                      onChange={(e) => updateElementProps('footer', element.id, { height: e.target.value })}
                      placeholder="10px"
                    />
                  )}
                  
                  {element.type === 'divider' && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                      <TextField
                        size="small"
                        label="Épaisseur"
                        value={element.props.borderWidth}
                        onChange={(e) => updateElementProps('footer', element.id, { borderWidth: e.target.value })}
                      />
                      <TextField
                        size="small"
                        type="color"
                        label="Couleur"
                        value={element.props.borderColor}
                        onChange={(e) => updateElementProps('footer', element.id, { borderColor: e.target.value })}
                      />
                    </Box>
                  )}
                  
                  {element.type === 'social' && (
                    <Box>
                      <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>Réseaux sociaux actifs :</Typography>
                      {element.props.networks.map((network, netIndex) => (
                        <Box key={network.name} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                          <input
                            type="checkbox"
                            checked={network.enabled}
                            onChange={(e) => {
                              const newNetworks = [...element.props.networks];
                              newNetworks[netIndex].enabled = e.target.checked;
                              updateElementProps('footer', element.id, { networks: newNetworks });
                            }}
                          />
                          <Typography variant="caption" sx={{ minWidth: '80px', textTransform: 'capitalize' }}>{network.name}</Typography>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder={`https://${network.name}.com/...`}
                            value={network.url}
                            onChange={(e) => {
                              const newNetworks = [...element.props.networks];
                              newNetworks[netIndex].url = e.target.value;
                              updateElementProps('footer', element.id, { networks: newNetworks });
                            }}
                            disabled={!network.enabled}
                          />
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              ))}

              <TextField
                fullWidth
                label="Couleur de fond du footer"
                variant="outlined"
                size="small"
                type="color"
                value={templateFormData.footerBgColor}
                onChange={(e) => setTemplateFormData({ ...templateFormData, footerBgColor: e.target.value })}
                sx={{ mt: 2 }}
              />
            </Box>

            {/* Note d'information */}
            <Box sx={{ p: 2, bgcolor: '#F0F9FF', borderRadius: 1, border: '1px solid #BAE6FD' }}>
              <Typography variant="caption" sx={{ color: '#0369A1' }}>
                💡 <strong>Astuce :</strong> Le contenu principal de l'email sera ajouté automatiquement entre le header et le footer lors de l'utilisation du template.
              </Typography>
            </Box>
            </Box>
            <Box sx={{ mt: { xs: 3, md: 0 }, position: 'sticky', top: 16, alignSelf: 'start', height: 'max-content', zIndex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Aperçu en direct
              </Typography>
              <Box
                sx={{
                  border: '1px solid #E2E8F0',
                  borderRadius: 1,
                  minHeight: '60vh',
                  bgcolor: '#f5f5f5',
                  overflow: 'auto'
                }}
              >
                <iframe
                  srcDoc={inlinePreviewHtml}
                  style={{
                    width: '100%',
                    minHeight: '60vh',
                    border: 'none',
                    backgroundColor: 'white'
                  }}
                  title="Inline Template Preview"
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Button 
            startIcon={<ArrowLeft />}
            onClick={() => {
              // Retour à la liste des templates sans sauvegarder
              setTemplateEditorOpen(false);
              setEditingTemplate(null);
              setTemplateManagerOpen(true);
            }}
            sx={{ 
              textTransform: 'none',
              color: '#64748B',
              '&:hover': { bgcolor: '#F8FAFC' }
            }}
          >
            Retour
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handlePreviewCustomTemplate}
              sx={{
                borderColor: '#007B8A',
                color: '#007B8A',
                '&:hover': { borderColor: '#006B75', bgcolor: 'rgba(0, 123, 138, 0.05)' },
                textTransform: 'none',
                px: 3
              }}
            >
              👁️ Prévisualiser
            </Button>
            <Button
              variant="contained"
              onClick={saveCustomTemplate}
              disabled={savingTemplate || !templateFormData.name.trim()}
              sx={{
                bgcolor: '#007B8A',
                '&:hover': { bgcolor: '#006B75' },
                textTransform: 'none',
                px: 3
              }}
            >
              {savingTemplate ? <CircularProgress size={20} sx={{ color: 'white' }} /> : '💾 Enregistrer mon template personnalisé'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Modal de prévisualisation template (étape 0) */}
      <Dialog
        open={templatePreviewOpen}
        onClose={() => setTemplatePreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Prévisualisation du template</Typography>
            <IconButton onClick={() => setTemplatePreviewOpen(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              border: '1px solid #E2E8F0',
              borderRadius: 1,
              minHeight: '500px',
              bgcolor: '#f5f5f5',
              overflow: 'auto'
            }}
          >
            <iframe
              srcDoc={templatePreviewHtml}
              style={{
                width: '100%',
                minHeight: '500px',
                border: 'none',
                backgroundColor: 'white'
              }}
              title="Template Preview"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplatePreviewOpen(false)} sx={{ textTransform: 'none' }}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de prévisualisation personnalisation */}
      <Dialog
        open={customPreviewOpen}
        onClose={() => setCustomPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Prévisualisation de votre template</Typography>
            <IconButton onClick={() => setCustomPreviewOpen(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              border: '1px solid #E2E8F0',
              borderRadius: 1,
              minHeight: '500px',
              bgcolor: '#f5f5f5',
              overflow: 'auto'
            }}
          >
            <iframe
              srcDoc={customPreviewHtml}
              style={{
                width: '100%',
                minHeight: '500px',
                border: 'none',
                backgroundColor: 'white'
              }}
              title="Custom Template Preview"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomPreviewOpen(false)} sx={{ textTransform: 'none' }}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

    </Dialog>

  );

};



export default EmailAssistant;

